/**
 * types.ts — local mirrors of the gpsclinics-contracts DTO shapes the portal
 * consumes across all surfaces (journey, identity, prices, orders).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY local mirrors: the portal carries no backend dependency — every call
 * goes through the gateway, which validates the real contracts. These types
 * only keep the SPA honest at compile time. Field names ARE the contract; keep
 * them in lock-step with the gateway. Shapes below were confirmed against the
 * live stage gateway where reachable (journey) and against the C1.7 brief where
 * an authed session was required (prices/orders/identity-me).
 *
 * ⚠️ THERE IS NO CONTRACTS DEPENDENCY TO BUMP, AND THAT IS THE DESIGN. This is a
 * browser bundle, not a Node service, so nothing here is generated and nothing
 * is verified by `npm install`: when a contracts tag moves, a human updates the
 * mirror in a reviewed diff and names the version in the docblock beside the
 * shapes that changed. The prescribing shapes below track **contracts v0.14.0**
 * (medicationVariant / formularyResponse / prescribeItemInput /
 * medicationAmendmentRequest / medicationAmendmentResult, and the nullable
 * `dosage` on a stored prescription line).
 *
 * The upside of a hand-written mirror is that it is READ; the risk is that it
 * drifts silently. So the rule is: mirror the field names EXACTLY (the CTO
 * spelling, `name`/`packQty`/`pip` — never a prettified local alias), and where
 * a shape's whole purpose is a safety property, say so in its docblock rather
 * than leaving a future reader to infer it from the field list.
 */
import type { DropOffReasonKey } from '../journey/dropOffReasons';

/* ── Journey (guest) ────────────────────────────────────────────────────── */

/** Question kinds the renderer supports (one question per screen). */
export type QuestionKind =
  | 'capture'
  | 'date'
  | 'single_select'
  | 'multi_select'
  | 'yes_no_text'
  | 'photo'
  | 'consent';

/** One selectable option; `exclusive` marks the "none of these" behaviour. */
export interface QuestionOption {
  key: string;
  label: string;
  exclusive?: boolean;
}

/**
 * A heading over a run of a consent question's options (clinic-service question
 * set v6, 2026-09-24 — the categorised agreement step). PRESENTATION ONLY: every
 * option is still held and submitted by its own key exactly as before (one
 * agreement tick covers them all; no option has a box of its own), and a group
 * never adds or removes a declaration. clinic-service guarantees the
 * groups cover every option key exactly once; the renderer still falls back to
 * the flat list if they do not, because a declaration that silently vanished
 * from the screen would be agreed to unseen.
 */
export interface QuestionOptionGroup {
  heading: string;
  optionKeys: string[];
}

/** Public/assessment question shape (server-redacted per R28). */
export interface QuestionPublic {
  key: string;
  idx: number;
  kind: QuestionKind;
  prompt: string;
  helpText?: string;
  /**
   * "Why we ask" — a short explanation of why this question is asked, opened
   * from a link under the prompt (question set v6, 2026-09-24). OPTIONAL and
   * ABSENT on every earlier set: an in-flight v5 assessment keeps its own
   * question set, so the renderer shows no link rather than inventing one.
   * Server-owned copy (clinical sign-off rides the question set); never written
   * in this repo. [CONTRACTS GAP] — clinic-service serves it through a local
   * extension of `questionPublic`; not yet on a contracts tag.
   */
  whyWeAsk?: string;
  /** Categorised consent options (v6, the q15 agreement step only). See {@link QuestionOptionGroup}. [CONTRACTS GAP]. */
  optionGroups?: QuestionOptionGroup[];
  options?: QuestionOption[];
  visibleIf?: { questionKey: string; equals: string };
  pendingClinicalSignoff: boolean;
}

export interface QuestionSetPublic {
  clinicSlug: string;
  version: number;
  pendingClinicalSignoff: boolean;
  questions: QuestionPublic[];
  /**
   * The `idx` of the LAST question in the whole set, including questions R28
   * is still withholding (clinic-service, 2026-09-11). It is the finish line the
   * patient's progress line is measured against, because the served list grows
   * mid-journey and a count off it moves backwards. OPTIONAL: an older
   * clinic-service omits it, and `journey/progressCopy.ts`'s caller falls back to
   * the highest served `idx` — never to a guess.
   */
  lastQuestionIdx?: number;
}

export type StopOutcome = 'pass' | 'soft_stop' | 'hard_stop';

/** The engine's verdict for an answered step. */
export interface EvaluationResult {
  outcome: StopOutcome;
  stopKey?: string;
  mhRisk: boolean;
  signposting?: { lines: string[] };
  offerCallback: boolean;
  flags: string[];
  bmi?: number;
  bmiThreshold?: number;
  nextQuestionKey?: string | null;
}

export type AnswerValue = string | string[] | { heightCm: number; weightKg: number };

export interface AnswerSubmitRequest {
  questionKey: string;
  value: AnswerValue;
  detail?: string;
}

/**
 * The clinician deal-drawer answer body (2026-08-06 hard-stop safety net) —
 * `AnswerSubmitRequest` plus the confirm-before-lost flag. Posted to the AUTHED
 * `/staff` answer route so a disqualifying answer can be previewed before the
 * lead is closed as Lost.
 */
export interface StaffAnswerSubmitRequest extends AnswerSubmitRequest {
  /**
   * When a hard-stop answer arrives without this, the server returns
   * `wouldCloseLost` and does NOT close the lead; `true` commits the Closed-Lost
   * transition.
   */
  confirmClose?: boolean;
}

/**
 * The clinician answer response — `EvaluationResult` plus the safety-net signal.
 * `wouldCloseLost` is true ONLY when this answer would hard-stop the lead into
 * Closed Lost but `confirmClose` was not set (the Lost transition is withheld;
 * the questionnaire stays editable). `questionKey` echoes the triggering step.
 */
export interface StaffAnswerResult extends EvaluationResult {
  wouldCloseLost: boolean;
  questionKey: string;
}

/** Marketing attribution tags carried from the landing URL (PHI-free). */
export interface Attribution {
  channel?: string;
  campaign?: string;
}

/** Public clinic config (R28-clean by contract: category-level only). */
export interface ClinicPublicConfig {
  slug: string;
  name: string;
  status: 'live' | 'coming_soon';
  lanes: Array<'form' | 'callback'>;
}

/**
 * The OPTIONAL Step-0 marketing-consent grant on the lead create (2026-09-23,
 * clinic #218). GRANT-ONLY BY SHAPE: `email` is the LITERAL `true` — there is
 * no `false` member, so this portal cannot even express "record a refusal
 * here". An unticked box sends NOTHING (the `marketingConsent` key is simply
 * absent from the body); `email: false` is a server 400 the portal must never
 * produce. Withdrawal has its own lanes (`/preferences`, the staff drawer) —
 * a lead create records at most a grant, never a no. `wordingVersion` names
 * the words the tick was shown (`marketing/step0ConsentWording.ts`), the same
 * registry rule as `mc-2026-09-11-d1`.
 */
export interface LeadMarketingConsentGrant {
  email: true;
  wordingVersion: string;
}

export interface LeadCaptureRequest {
  clinicSlug: string;
  email: string;
  heightCm: number;
  weightKg: number;
  displayUnits: 'metric' | 'imperial';
  attribution?: Attribution;
  /**
   * Step-0 consent tick (flag-gated, default off). Present ONLY when the
   * patient ticked the box — see `LeadMarketingConsentGrant` above for why an
   * untick is an ABSENT key, never `email: false`.
   */
  marketingConsent?: LeadMarketingConsentGrant;
  /**
   * First/last name — captured on Step 0 (2026-07-25) AND by the external Webflow
   * embed widget; the clinic accepts them on `leadCaptureRequestWidened`. Optional
   * on the wire (the admin fill-on-behalf + legacy paths may omit). `phone` stays
   * widget-only.
   */
  firstName?: string;
  lastName?: string;
  /**
   * S-2 declared readiness (2026-09-11) — one of clinic-service's
   * `LEAD_INTENTS`.
   *
   * ⚠️ THE PORTAL JOURNEY NO LONGER SENDS THIS (2026-09-12). The question moved
   * off Step 0 onto its own screen before the questionnaire, which is AFTER the
   * lead has already been created — so the journey records the answer with
   * `recordLeadReadiness()` instead, and this key stays on the type for the
   * WEBFLOW EMBED and any cached widget still posting it. Do not delete it to
   * tidy up: every embed in the wild would start sending a key the type says
   * does not exist.
   *
   * OPTIONAL on the wire, and omitted rather than sent empty: the server enum
   * has no empty member, so `''` would 400 an entire lead capture over a
   * segment field. It stays optional even though the portal's own readiness
   * screen became REQUIRED on 2026-09-25 (CTO): an embed capture may or may
   * not carry it, and a lead captured by any lane is still a lead.
   *
   * ⚠️ PURCHASE-STAGE ONLY, NEVER HEALTH — the excluded option and the reasons
   * (R28/MHRA before any eligibility gate, and Art 9) are documented in
   * `journey/leadReadiness.ts` and clinic-service's `lead-intent.ts`.
   */
  intent?: LeadIntentWire;
  phone?: string;
}

/**
 * The closed server vocabulary for "how ready is this lead to start" — the wire
 * spelling of clinic-service's `LEAD_INTENTS`.
 *
 * ⚠️ THE WIRE NAME STAYS `intent` even though the patient-facing concept was
 * renamed to READINESS (CTO, 2026-09-12). clinic-service owns the column, the
 * staff labels and the Insights cut under that name, and every embed already
 * deployed posts it. `journey/leadReadiness.ts` explains the rename and why it
 * is deliberately one-sided.
 */
export type LeadIntentWire = 'ready_to_start' | 'checking_eligibility' | 'comparing_prices' | 'researching';

/**
 * The gateway path the readiness answer is posted to — THE ONE PLACE IT IS
 * SPELT in this repo.
 *
 * ⚠️ IT IS A CONSTANT BECAUSE THE PATH IS NOT SETTLED YET (2026-09-12). Three
 * repos have to agree on this string: the portal posts it, the gateway has to
 * allowlist it as an explicit `@Public()` guest route (its guest clinic lanes
 * are a per-route allowlist, so until that lands this write 401s in every
 * environment), and clinic-service serves it. `api/journey.ts`, the API stub in
 * `testing/fixtures.ts` and the specs that assert the call was made all import
 * THIS, so settling on whatever the gateway lands is a one-line edit rather
 * than a grep across the tree that quietly misses a spec — and a stub still
 * answering the OLD path would leave every one of those specs green against a
 * route the portal had stopped calling.
 *
 * ⚠️ IT LIVES IN `types.ts`, NOT IN `api/journey.ts`, AND THAT MATTERS. This
 * file imports nothing; `api/journey.ts` pulls in `api/client` and with it the
 * gateway config and the auth session. `testing/fixtures.ts` is imported by
 * almost every spec in the repo, so giving it an edge into the API layer
 * changes module-init order estate-wide — it broke 40 unrelated pipeline tests
 * when this constant was first put there. A shared constant belongs on the leaf
 * both sides already depend on.
 *
 * The `intent` spelling is the WIRE name and is deliberately not "readiness":
 * clinic-service owns the column, the staff labels and the Insights cut under
 * `intent`, and every Webflow embed already in the wild posts it. The rename to
 * "readiness" is patient-facing and portal-internal only — `leadReadiness.ts`
 * argues why it is one-sided on purpose.
 */
export const LEAD_READINESS_PATH = '/v1/clinic/leads/intent';

/**
 * POST /v1/clinic/leads/intent — attach a readiness answer to an ALREADY
 * CAPTURED lead (2026-09-12).
 *
 * WHY A SECOND CALL AT ALL. The lead is created the moment Step 0 is submitted
 * ("an abandoned journey is still a lead"), and the readiness question is now
 * asked two screens later — so by the time there is an answer the row already
 * exists. Folding the question back into the capture POST would mean holding
 * the lead until the patient had answered an OPTIONAL question, which trades a
 * segment for actual leads.
 *
 * The server treats it as FILL-NULL-ONLY and answers every caller identically,
 * so a harvested leadId can neither re-label an existing card nor be used to
 * probe whether a lead exists. See clinic-service `leads.service.ts`.
 */
export interface LeadReadinessRequest {
  leadId: string;
  intent: LeadIntentWire;
}

/**
 * POST /v1/clinic/leads/resume response — the prefill the `?lead=<leadId>`
 * entry seeds the journey with (Webflow widget hand-off). Deliberately
 * minimal: no phone/lastName — the journey doesn't need them (data
 * minimisation), and PII never rides in a URL (only the opaque leadId does).
 */
export interface LeadResume {
  leadId: string;
  heightCm: number;
  weightKg: number;
  displayUnits: 'metric' | 'imperial';
  email: string;
  /**
   * NO `firstName` — it LEFT this response on 2026-08-28 (clinic-service
   * `fix/lead-resume-capability-ttl`, live in prod) and this type follows it out
   * rather than describing a field the server no longer sends. A stale optional
   * field is not harmless here: nothing reads it today, so the only way it gets
   * used is somebody writing `lead.firstName`, being promised a `string | null`
   * by the compiler, and getting `undefined` at runtime.
   *
   * It went because a trace found NO consumer in this repo — the `lead.firstName`
   * in `embed/main.ts` is the widget's OWN captured form state, not this payload
   * — so it was a named patient's given name handed to anyone holding a funnel
   * id, in exchange for nothing (UK GDPR Art. 5(1)(c) data minimisation). The
   * resume restores a JOURNEY; it does not hand back a patient profile.
   *
   * `email` deliberately STAYS: `setJourneyEmail` feeds three live consumers in
   * `checkout/CheckoutFlow.tsx` (the `hasJourneyIdentity` account-step gate,
   * `prefillEmail`, and the "we've emailed a code to …" line), and on the widget
   * lane this read is their only source. It should still go — the OTP-gated
   * Tier-2 `account-bootstrap` read is a strictly better source — but that is a
   * PORTAL change that has to land BEFORE clinic-service drops the field, or
   * that lane silently downgrades to the code-first path. Tracked separately;
   * do not remove `email` here to "match the server" until it does.
   */
  /**
   * Was THIS call the first time the hand-off was spent?
   *
   * clinic-service `fix/lead-resume-capability-ttl` makes the resume single-use
   * — it stamps `Lead.resumeConsumedAt` on the first spend and refuses the id
   * once a 15-minute TTL or a 2-minute replay grace has passed — so the server
   * already knows the answer. Surfacing it lets the portal count `generate_lead`
   * exactly once per lead WITHOUT keeping an analytics marker on the visitor's
   * device (UK PECR reg. 6); see `tracking/generateLead.ts`.
   *
   * STILL OPTIONAL even though the clinic-service half has now shipped: absent
   * means "the server did not say", which `firstTouchOfResume` maps to
   * `'server-silent'` — NOT to `false`, which would switch C1 off for the whole
   * widget lane. The portal deploys independently of clinic-service, so keep the
   * degradation rather than tightening this to required. Nothing else reads it.
   */
  firstResume?: boolean;
}

export interface AssessmentCreateRequest {
  clinicSlug: string;
  leadId?: string;
  attribution?: Attribution;
}

export type Eligibility = 'eligible' | 'clinician_review' | 'not_eligible';

/** contracts AssessmentState — the assessment's lifecycle stage. */
export type AssessmentState = 'draft' | 'submitted' | 'approved' | 'rejected';

export interface AssessmentSubmitResult {
  assessmentId: string;
  state: AssessmentState;
  eligibility: Eligibility;
  flags: string[];
}

/**
 * GET /v1/clinic/assessments/:assessmentId/progress?clinicSlug= — the
 * navigation-resilience read (feat/patient-journey-navigation-resilience).
 * Tier-1, minimised (#57): metadata ONLY — enough for the SPA to work out which
 * STAGE to resume on a hard refresh (`?assessment=<uuid>` in the URL), and
 * NOTHING clinical. The verdict (eligibility/hardStop/mhRisk), the answer-derived
 * next-question key, the soft flags, and the leadId capability are deliberately
 * NOT here: a bare assessment UUID rides in URLs/logs, so anything sensitive is
 * Tier-2 behind the emailed-code -> restore token. A `submitted` restore routes
 * to the code gate; the authoritative verdict comes back on the Tier-2 summary.
 */
export interface AssessmentProgress {
  assessmentId: string;
  clinicSlug: string;
  /**
   * Workflow state, collapsed to draft | submitted (approved/rejected read as
   * `submitted` server-side) — never the clinical verdict.
   */
  state: 'draft' | 'submitted';
  /**
   * Draft with zero persisted answers — Webflow/widget handoff. Mid-flow drafts
   * are false. (A cold `?assessment=` restore resumes the questionnaire either
   * way; the leading capture questions re-collect H/W.)
   */
  atStart: boolean;
  /**
   * Content-independent progress cursor: `currentStep` = answered count,
   * `totalSteps` = fixed per-lane question-bank size. Reveals depth, not answers.
   */
  currentStep: number;
  totalSteps: number;
}

/**
 * GET /v1/clinic/assessments/:assessmentId/result-summary?clinicSlug= — guest
 * ResultScreen hydrate after progress maps to `result`. Capture height/weight +
 * authoritative BMI only — never the answer blob. Kept separate from
 * {@link AssessmentProgress} so mid-draft restore stays PHI-minimal.
 */
export interface AssessmentResultSummary {
  // No assessmentId: clinic #57 deliberately NEVER echoes it back (the client
  // already holds it, and the server derives it from the token) — keep this shape
  // in lock-step with clinic's strict response schema.
  clinicSlug: string;
  state: AssessmentState;
  eligibility: Eligibility | null;
  flags: string[];
  heightCm: number;
  weightKg: number;
  bmi: number;
}

/**
 * GET /v1/clinic/assessments/:assessmentId/account-bootstrap?clinicSlug= — guest
 * Create-account hydrate. Lead email + date-kind DOB only — never other
 * answers. Null fields mean that value is genuinely missing (handle explicitly).
 */
export interface AssessmentAccountBootstrap {
  assessmentId: string;
  clinicSlug: string;
  email: string | null;
  dateOfBirth: string | null;
}

/**
 * GET /v1/clinic/assessments/:assessmentId/callback-status?clinicSlug= — guest
 * StopScreen restore. Boolean only — never name/phone/preferredTime/notes.
 */
export interface AssessmentCallbackStatus {
  assessmentId: string;
  clinicSlug: string;
  requested: boolean;
}

/**
 * A signed-upload grant: `{stub: true}` when object storage isn't configured on
 * this environment (the caller should upload nothing and show an info note), or
 * `{stub: false, uploadUrl, objectRef, expiresAt}` — PUT the file straight to
 * `uploadUrl`. This same DTO shape is reused for the clinician
 * signature-upload grant (POST /v1/identity/clinician/me/signature-upload) —
 * see api/identity.ts.
 */
export interface PhotoUploadGrant {
  stub: boolean;
  uploadUrl?: string;
  objectRef?: string;
  expiresAt?: string;
  /**
   * The exact byte ceiling clinic-service bound into the V4 signature's
   * `x-goog-content-length-range` extension. The PUT MUST echo `1,${maxBytes}`
   * or GCS rejects it — sending a mirrored constant instead re-breaks the moment
   * the server-side ceiling moves, so the value travels with the grant.
   */
  maxBytes?: number;
}

/**
 * `POST /v1/clinic/assessments/:id/checkout-token` — the guest checkout grant.
 *
 * ⚠️ `weightPhotoRequired` IS GONE FROM THIS TYPE (2026-09-04) AND MUST NOT COME
 * BACK. clinic-service still echoes the field; the portal declared it so the
 * plan step could mandate a weight photo on the clinician plan-link lane, the
 * one lane that reaches checkout with no submit verdict in hand. There is no
 * photo mandate anywhere in checkout any more — see the header of
 * `checkout/PlanSelection.tsx` — so declaring the field would advertise a
 * decision this client no longer takes, and the next reader would wire it back
 * up. An extra key on the wire is harmless; a type that invites a gate is not.
 */
export interface CheckoutTokenMint {
  checkoutToken: string;
}

export interface CallbackCreateRequest {
  clinicSlug: string;
  firstName: string;
  lastName: string;
  phone: string;
  preferredTime?: string;
  leadId?: string;
  /** StopScreen / journey — links ticket for assessment-scoped restore. */
  assessmentId?: string;
  attribution?: Attribution;
  /**
   * Standalone `/callback` lane only (no prior lead capture, so no leadId):
   * lets the CRM sync create the pipeline deal directly off this email
   * instead of resolving one through a Lead. Ignored server-side once a
   * leadId resolves.
   */
  email?: string;
}

/**
 * GET /v1/clinic/callbacks/status?clinicSlug=&leadId= — guest standalone
 * /callback restore. Boolean only.
 */
export interface CallbackLeadStatus {
  clinicSlug: string;
  leadId: string;
  requested: boolean;
}

/* ── Identity ───────────────────────────────────────────────────────────── */

export type UserRole = 'PATIENT' | 'CLINICIAN' | 'ADMIN';

export interface RegisterRequest {
  email: string;
  password: string;
  dateOfBirth: string;
  /**
   * Option-C assessment bind. A verified re-entry token (from the emailed code,
   * see verifyRestoreCode) links the completed guest assessment to the new
   * account AND marks the session email-verified server-side — identity derives
   * the authoritative email/DOB/name from it and ignores the browser fields.
   * Absent → a plain browser register (emailVerified:false + a verify link, no
   * assessment adopted). The bare `assessmentId` link was removed: it let anyone
   * with a leaked UUID claim another person's assessment.
   */
  reEntryToken?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

/** register/login response — the opaque session token + who it belongs to. */
export interface AuthSession {
  sessionToken: string;
  role: UserRole;
  userId: string;
  expiresAt: string;
  emailVerified: boolean;
}

/** GET /auth/me — the signed-in principal. */
export interface Me {
  userId: string;
  email: string;
  role: UserRole;
  emailVerified: boolean;
  /**
   * The account's display name (additive on identity's /me). OPTIONAL: the
   * deployed API may not return it yet — the header falls back to the email.
   */
  fullName?: string | null;
}

/** A postal address (registered or delivery). */
export interface Address {
  line1: string;
  line2?: string;
  city: string;
  postcode: string;
}

export interface PatientProfile {
  userId: string;
  email: string;
  /** Captured first/last name — nullable: a pre-capture patient may have none yet. */
  firstName?: string | null;
  lastName?: string | null;
  dateOfBirth?: string;
  /** The patient's registered address — the live identity field name. */
  registeredAddress?: Address;
}

/* ── Prices ─────────────────────────────────────────────────────────────── */

export interface PricePlan {
  months: number;
  monthlyPence: number;
}

/* ── Medication safety (post-eligibility, R28-restricted) ──────────────────
 *
 * Mirrors of the contracts v0.9.0 `medicationSafety` / `safetyAcknowledgement`
 * shapes, added after a clinician (Reneal Patel, 2026-08-18) raised that a
 * patient could commit money to a prescription-only weight-loss medicine having
 * been shown only what it costs and how much weight they might lose.
 *
 * These ride on the POST-ELIGIBILITY plan-option surface, so they inherit the
 * R28 gate the prices read already has — there is no new public route to
 * protect. The prose itself never appears in this repo: it arrives on the wire
 * and is rendered as given.
 */

/**
 * ONE risk topic the patient is shown before a prescription-only weight-loss
 * medicine is sold to them (contracts `medicationSafetyTopic`).
 *
 * `body` is one entry PER PARAGRAPH and the renderer never splits or joins
 * them. prices-service holds the clinician's sentences exactly as they were
 * written, with a pull request behind every edit; a portal that re-flowed them
 * would be quietly editing clinical copy with no review trail behind it.
 */
export interface MedicationSafetyTopic {
  /** Stable machine key — this, not the prose, is what an acknowledgement records. */
  key: string;
  title: string;
  body: string[];
}

/**
 * One independent source a patient can go and read for themselves (contracts
 * v0.10.0 `medicationSafety.externalLinks`).
 *
 * `label` is the accessible name the reader is given, so it must survive being
 * read out of context by a screen reader — the renderer appends the new-tab
 * warning to it rather than relying on an icon.
 */
export interface MedicationSafetyExternalLink {
  label: string;
  url: string;
}

/**
 * The risk information shown, and acknowledged, before payment (contracts
 * `medicationSafety`, carried on `PlanOption.safety`).
 *
 * NAMED `…Content` DELIBERATELY. Contracts calls this `medicationSafety`, but
 * `MedicationSafety` is already taken in this portal — `clinicians/KeyAlerts.tsx`
 * uses it for something entirely different, the double-prescribing check's
 * result union. Two identically-named types answering two different clinical
 * questions, both imported into the same review screen, is a mis-import waiting
 * to happen. The TYPE name carries the suffix; the FIELD name (`safety`) stays
 * exactly as contracts sends it, because the field name is the contract.
 *
 * NOTHING HERE IS EVER SUBSTITUTED, DEFAULTED OR SHARED BETWEEN MEDICATIONS.
 * A risk that belongs to one drug class attached to another is either an
 * invented hazard or a missed one, and both are clinical mis-statements. When
 * this is absent the portal blocks the sale and says so — see
 * `checkout/MedicationSafetyGate.tsx`.
 */
export interface MedicationSafetyContent {
  /**
   * Content version. The one thing that makes an acknowledgement mean something
   * a year later: it pins WHICH wording the patient agreed to, so re-issued
   * content can never retroactively rewrite what was consented to.
   */
  version: string;
  /**
   * Urgent-action wording — what to do, and how fast, if something goes wrong.
   * Always visible and never behind a disclosure: a patient who reads one thing
   * on this screen has to read these.
   */
  redFlags: string[];
  topics: MedicationSafetyTopic[];
  /** MHRA Yellow Card — patients may self-report suspected side effects. */
  yellowCardUrl: string;
  /**
   * Independent, non-commercial pages about THIS medicine — the NHS page for
   * it, the MHRA leaflet, and so on (contracts v0.10.0 `externalLinks`).
   *
   * WHY THEY ARRIVE WITH THE CLINICAL CONTENT rather than living in this repo:
   * which external page is the RIGHT one is a clinical judgement about a
   * specific medicine, made by whoever authored the rest of this block. A link
   * list maintained in a front end drifts from the medicine it describes the
   * first time the catalogue changes, and a patient following a stale link is
   * reading about a drug they were not prescribed.
   *
   * OPTIONAL, and absent simply renders nothing. Never substituted, never
   * defaulted, and never a place for our own pages: the standing of a public
   * page that names prescription-only medicines rests entirely on it being
   * information rather than promotion.
   */
  externalLinks?: MedicationSafetyExternalLink[];
  /**
   * FALSE until the clinical lead has ratified this exact wording.
   *
   * STAFF-FACING, and deliberately NOT rendered to patients. Withholding risk
   * information because it is not yet ratified is the exact failure this feature
   * exists to fix, and "this text is awaiting sign-off" is a fact about our
   * governance process, not a fact about the medicine — putting it in front of
   * someone deciding whether to start treatment tells them nothing they can act
   * on and undermines everything above it.
   *
   * No portal STAFF surface reads plan options with safety content yet, so
   * nothing renders it today. It is mirrored here so the first one that does has
   * the field waiting rather than inventing a second read for it.
   */
  clinicallyApproved: boolean;
}

/**
 * What the patient (or a staff member on their behalf) confirmed they were
 * shown (contracts `safetyAcknowledgement`).
 *
 * Not a bare boolean, on purpose: a lone `true` proves nothing when somebody
 * asks, a year later, what this person was actually told. `version` pins the
 * wording, `topicKeys` pins what was on the screen, `source` records who did the
 * explaining.
 */
export interface SafetyAcknowledgement {
  version: string;
  /**
   * The topic keys ACTUALLY RENDERED, in the order they were rendered. This is
   * evidence of a screen, not a restatement of config — never send the keys the
   * content COULD have had, only the ones the patient was shown.
   */
  topicKeys: string[];
  /**
   * `patient` — they ticked it themselves.
   * `clinician_explained` — staff covered the risks on a call and recorded it.
   */
  source: 'patient' | 'clinician_explained';
}

/**
 * The acknowledgement as STAFF read it back on the order (contracts
 * `staffOrder.safetyAck`) — the same record, plus when it was taken.
 */
export interface StaffSafetyAcknowledgement extends SafetyAcknowledgement {
  acknowledgedAt: string;
}

/**
 * ONE medication as the PUBLIC safety read returns it (prices-service
 * `feat/public-safety-read`, gateway `feat/public-safety-route`).
 *
 * A DELIBERATELY NARROW PROJECTION of `PlanOption`, and the narrowness is the
 * point. The plan-options read carries money, cold-chain handling and the
 * clinic's commercial configuration, and it is gated on eligibility for exactly
 * that reason. This shape carries the medication's NAME and its clinician-
 * authored safety content and nothing else — so the public surface cannot start
 * leaking commercial or operational data the day somebody adds a field to the
 * priced shape.
 *
 * NO PRICE FIELD EXISTS HERE ON PURPOSE. See `safety/ClinicalSafetyPage.tsx`:
 * a public page that names prescription-only medicines stands up as INFORMATION
 * and collapses the moment it carries an offer.
 */
export interface PublicMedicationSafety {
  productKey: string;
  medicationName: string;
  /**
   * REQUIRED here, unlike `PlanOption.safety`. The public read exists only to
   * serve this content, so a medication with none has nothing to publish and is
   * omitted upstream rather than returned empty — there is no half-answer for a
   * page whose whole job is to show the risks.
   */
  safety: MedicationSafetyContent;
}

/**
 * GET the public clinical-safety read → every medication this clinic offers,
 * with its safety content.
 *
 * `clinicSlug` is echoed back so a consumer can prove it rendered the clinic it
 * asked for; a page that silently renders another clinic's medicines would be
 * showing a patient risks for a treatment nobody offered them.
 */
export interface PublicClinicSafetyResponse {
  clinicSlug: string;
  medications: PublicMedicationSafety[];
}

/** GET /prices/prices/quote → a medication option with per-term plans. */
export interface PlanOption {
  productKey: string;
  medicationName: string;
  form: string;
  coldChain: boolean;
  plans: PricePlan[];
  /**
   * Flags THE recommended OPTION in a multi-option response (contracts sends
   * `recommended?: boolean`). An earlier portal build misread this as a
   * months value — it is not; the default term is a UI decision.
   */
  recommended?: boolean;
  /**
   * Anticipated average weight loss for this treatment, as a whole percentage of
   * body weight (e.g. `20` → "20% average weight loss").
   *
   * SERVER-SUPPLIED, NEVER HARDCODED IN THE PORTAL. Two reasons, both hard:
   *   1. It is a CLINICAL EFFICACY CLAIM about a prescription-only medicine. It
   *      needs substantiation, the trial context it came from, and clinical
   *      sign-off. A figure typed into a front-end file has none of that and
   *      cannot be audited or corrected without a portal release.
   *   2. Keying it by brand in portal source would collide with R28 — the
   *      `r28.spec.tsx` guard scans this tree for POM brand names by design.
   *
   * OPTIONAL, and its absence is a normal state: when prices-service does not
   * supply a figure the card simply omits the line. Never substitute a default,
   * an estimate, or a neighbouring medication's number.
   */
  expectedWeightLossPercent?: number;
  /**
   * Anticipated loss BROKEN DOWN BY TERM — one entry per offered plan length —
   * powering the projection band at the top of the plan builder.
   *
   * SEPARATE FROM `expectedWeightLossPercent` ON PURPOSE, and the distinction is
   * the whole point of this field. That one is a SINGLE figure for the treatment.
   * A projection needs to say what three months looks like versus twelve, and
   * there is no honest way to get from one number to that curve: spreading it
   * across the terms would be the PORTAL inventing clinical data about a
   * prescription-only medicine, which is exactly what the field above forbids.
   *
   * SERVER-SUPPLIED, per term, or absent. Absent is the CURRENT state —
   * prices-service does not send it yet — and absent means the band renders
   * NOTHING (`WeightProjection` returns null below two points). That is the
   * designed behaviour, not a gap to paper over: an absent chart is recoverable,
   * a plausible invented one shown to someone deciding whether to start a
   * prescription medicine is not.
   *
   * Lighting it up needs a prices-service change AND a named clinical owner for
   * the figures, with the trial basis recorded — not a portal release.
   */
  projection?: { months: number; percent: number }[];
  /**
   * The clinician-authored risk information for THIS medication, shown and
   * acknowledged before payment (contracts v0.9.0).
   *
   * OPTIONAL ON THE WIRE ONLY, so a prices-service mid-rollout cannot 500 the
   * plan screen — it is NOT an acceptable steady state. prices-service has a
   * guard spec asserting every sellable medication has content, so absent means
   * something is wrong upstream, and the portal's answer to that is to BLOCK the
   * sale and say so plainly. It never renders a substitute, a neighbouring
   * medication's wording, or nothing at all.
   */
  safety?: MedicationSafetyContent;
}

/** GET /prices/prices/options?clinicSlug= → every plan option for a clinic (C2.1). */
export interface PlanOptionsResponse {
  clinicSlug: string;
  options: PlanOption[];
}

/**
 * GET /prices/prices/options?clinicSlug= → EVERY priced medication option for
 * a clinic (the C2.1 plan-options read). Powers the patient plan chooser and
 * the clinician prescription builder's medication select — the builder only
 * ever renders productKey/medicationName/form from it, never a price
 * (MONEY-REDACTION §3.1 still holds on the clinician surface).
 */
export interface PlanOptionsResponse {
  options: PlanOption[];
}

/* ── Orders ─────────────────────────────────────────────────────────────── */

export interface CheckoutRequest {
  assessmentId: string;
  productKey: string;
  months: number;
  deliveryAddress: Address;
  /**
   * Proof the patient was shown this medication's risks before paying.
   *
   * OPTIONAL here because contracts makes it optional (the wire has to tolerate
   * a portal build that predates the tick box during a rollout) — but
   * orders-service REJECTS a checkout without it, so in this build every call
   * site sends it or does not call at all. Treat a checkout POST with no
   * `safetyAck` as a bug, not a degraded mode: the 400 it earns is a broken
   * checkout for a real patient.
   */
  safetyAck?: SafetyAcknowledgement;
}

/**
 * Phase-1 payment session grant from `POST /v1/orders/checkout/session`.
 * Real Stripe: `stub:false` + `clientSecret`. FakeStripe: `stub:true`, no secret.
 */
export interface PaymentSessionGrant {
  stub: boolean;
  setupIntentId: string;
  clientSecret?: string;
}

/** Phase-1 initialise response — OrderCase + Subscription + SetupIntent session. */
export interface CheckoutSessionResult {
  orderCaseId: string;
  subscriptionId: string;
  paymentSession: PaymentSessionGrant;
}

/**
 * GET /v1/orders/checkout/resume/:assessmentId — PATIENT hard-refresh restore.
 * Soft status:'none' when no owned session. paymentSession is re-issued for
 * awaiting_payment (clientSecret ephemeral — never stored by the SPA).
 * plan_selected = plan persisted, Address next (no SI yet).
 *
 * [CONTRACTS GAP — orders `checkoutResume` (orders-local, `dto.ts`), returning
 * patients S7.2a, 2026-09-25] `clinician_plan` = a clinician-built
 * (`preapproved`) case already BOUND to the caller by its session POST, with its
 * SetupIntent and delivery address, and no money held or captured for the
 * assessment. It carries the clinician's STORED plan and monthly (what Route 2
 * captures), the stored address and the case's OWN `paymentSession`, re-issued
 * under the session POST's idempotency key. Orders answers it only to the
 * principal the case is bound to, and never to a guest principal (S0.4). The 3D
 * Secure redirect return of a plan link lands here, and CheckoutFlow restores it
 * to the card step with `clinicianPlan` set (portal S7.2b). An orders build
 * without S7.2a answers `none` for the same case: CheckoutFlow then keeps its
 * older plan-preview path. Orders branch `fix/resume-clinician-plan`.
 */
export type CheckoutResumeStatus = 'none' | 'plan_selected' | 'awaiting_payment' | 'clinician_plan' | 'complete';

export interface CheckoutResume {
  status: CheckoutResumeStatus;
  orderCaseId: string | null;
  subscriptionId: string | null;
  productKey: string | null;
  months: number | null;
  monthlyPence: number | null;
  medicationName: string | null;
  deliveryAddress: Address | null;
  /** Present for awaiting_payment and clinician_plan — the case's own SetupIntent grant. */
  paymentSession: PaymentSessionGrant | null;
  paymentPhase: PaymentPhase | null;
}

/** POST /v1/orders/checkout/selection — persist plan before address/payment. */
export interface CheckoutSelectionRequest {
  assessmentId: string;
  productKey: string;
  months: number;
}

export interface CheckoutSelectionResult {
  orderCaseId: string;
  productKey: string;
  months: number;
  monthlyPence: number;
  medicationName: string;
}

/** Phase-2 body — SetupIntent id only (no amount, capture mode, or PaymentMethod id). */
export interface AttachPaymentMethodRequest {
  setupIntentId: string;
}

/**
 * [CONTRACTS GAP — orders `retryCardRequest`, 2026-09-25] Body of
 * `POST /v1/orders/checkout/:orderCaseId/retry-card`: the SetupIntent whose
 * month-1 hold the bank declined. The answer is a `CheckoutSessionResult`.
 */
export interface RetryCardRequest {
  setupIntentId: string;
}

/** Server-authoritative Route 1 (`held`) vs Route 2 (`captured`) after finalisation. */
export type PaymentPhase = 'held' | 'captured';

export interface AttachPaymentMethodResult {
  orderCaseId: string;
  subscriptionId: string;
  paymentPhase: PaymentPhase;
}

/**
 * GET /v1/orders/plan-preview/:assessmentId (2026-07-20, clinician-led plan) —
 * has a clinician already built + pre-approved a plan for this assessment? When
 * `hasPlan`, the checkout skips plan-selection and shows a read-only summary;
 * paying then captures immediately (no separate approval click). Post-
 * eligibility surface, so medication name/price are present (R28 is
 * pre-eligibility only).
 */
export interface PlanPreview {
  hasPlan: boolean;
  productKey: string | null;
  months: number | null;
  monthlyPence: number | null;
  medicationName: string | null;
}

export interface CurrentPlan {
  productKey: string;
  medicationName: string;
  months: number;
  monthlyPence: number;
  monthsRemaining: number;
  /** ISO UTC, or null when there is no scheduled next payment (contract: nullable). */
  nextPaymentDate: string | null;
  /** Canonical SubscriptionState (was a stale local `PlanState` that never matched
   * the backend — `dormant`/`past_due`/`completed` rendered as raw text). */
  state: SubscriptionState;
  /** NOTE: NOT in the canonical `patientDashboard.currentPlan` contract. The
   * delivery-address edit (ProfilePage) needs the orders projection to send these
   * (or a separate read); against a contract-conformant backend they are always
   * undefined. Tracked separately — do not rely on them. */
  orderCaseId?: string;
  deliveryAddress?: Address;
  /**
   * The subscription this plan IS — the key the patient's own re-subscribe sends
   * as `priorSubscriptionId` (v0.8.7). OPTIONAL on purpose: an orders revision
   * predating the re-subscribe lane does not carry it, and without it there is
   * nothing to send, so the entry point simply does not render.
   */
  subscriptionId?: string;
  /**
   * Whether the patient has ALREADY asked for this plan to stop at the end of
   * its term (2026-08-28). OPTIONAL for the same reason as `subscriptionId`: an
   * orders revision that predates the field does not send it.
   *
   * Its absence is treated as "not stopped", which is the safe default — the
   * control renders, and the server is the thing that decides. Its PRESENCE is
   * what stops the control lying: without it a patient who has already stopped
   * their plan meets the same button and cannot tell whether the first press
   * registered.
   */
  cancelAtTermEnd?: boolean;
  /**
   * The server's re-subscribe answer for THIS plan (v0.8.7). OPTIONAL on
   * purpose, and its absence means "this server does not do re-subscribe yet",
   * never "not eligible" — the patient surface renders nothing at all rather
   * than a disabled control it cannot explain.
   *
   * Eligibility is NEVER inferred from `state` here. `completed` is necessary
   * but nowhere near sufficient (a successor plan, a live offer or an unbound
   * patient all block it), and duplicating those rules client-side guarantees
   * the button and the write disagree the moment they move.
   */
  resubscribe?: ResubscribeEligibility;
}

/** One order-history row — matches the canonical `orderHistoryItem` contract. */
export interface OrderSummary {
  orderCaseId: string;
  createdAt: string;
  lane: 'form' | 'callback';
  state: string;
  productKey: string;
  medicationName: string;
  /**
   * The opaque assessment behind the order (orders `[CONTRACTS GAP]` widening,
   * 2026-07-30). OPTIONAL on purpose — the deployed dashboard may not carry it
   * yet. It lets a resumable row (`plan_selected` / `awaiting_payment` — a
   * drop-off at payment) link into the existing signed-in checkout resume
   * (`/weightloss#resume=<assessmentRef>`); absent → the row simply has no
   * Continue link. Never PII/health (R19).
   */
  assessmentRef?: string | null;
  /**
   * Courier + tracking once the pharmacy has dispatched (contracts
   * `patientOrderTracking`, v0.8.0). OPTIONAL on purpose: the deployed
   * `/me/dashboard` may not carry it yet, and an order that has not shipped
   * never will — the row simply renders no tracking line rather than an empty
   * one. Tracking ONLY: the pharmacy's rejection reason and identity are never
   * on a patient surface.
   */
  tracking?: PatientOrderTracking | null;
}

export type BillPhase = 'held' | 'captured' | 'released' | 'refunded' | 'failed';

/**
 * One billing-history row, MATCHING the canonical `billHistoryItem` contract
 * (gpsclinics-contracts) exactly: `paymentId`/`kind`/`phase`/`amountPence`/
 * `occurredAt`. (An earlier drift here invented `billId`/`createdAt`/
 * `description`, which the orders `/me/dashboard` response never sends — every
 * one came back `undefined`, hence the undefined React key + blank row.)
 */
export interface BillSummary {
  paymentId: string;
  kind: PatientPaymentKind;
  phase: BillPhase;
  amountPence: number;
  occurredAt: string;
  /** The order this payment belongs to — the dashboard groups bills by it (so a
   * payment for one order is never read against a different order's plan price).
   * NOT in the canonical `billHistoryItem` contract; the orders projection sends
   * it (local [CONTRACTS GAP]). Undefined against a contract-conformant backend
   * → bills fall back to a single ungrouped list. */
  orderCaseId?: string;
  /** Stripe-hosted invoice document for this payment (THE INVOICE IS A DOCUMENT,
   * NOT A CHARGE — orders-service CLAUDE.md). Null/undefined is a NORMAL,
   * PERMANENT state — not every payment (e.g. never captured, or a guest with no
   * Stripe customer vault) gets one; never treat its absence as an error. NOT in
   * the canonical contract (local [CONTRACTS GAP], same as `orderCaseId`). */
  invoiceHostedUrl?: string | null;
  invoicePdfUrl?: string | null;
}

export interface DashboardData {
  currentPlan: CurrentPlan | null;
  orders: OrderSummary[];
  bills: BillSummary[];
}

/* ── Staff surfaces: clinician queue + review, admin console (C1.5) ─────────
 *
 * These mirror the staff-facing gateway shapes. MONEY-REDACTION (Reshape §3.1):
 * the clinician projection of a staff order carries NO money key at all — the
 * orders-service builds it by construction (see queue.projection). `monthlyPence`
 * is therefore OPTIONAL and only ever present for an ADMIN caller; the clinician
 * surface must additionally never render or request it.
 */

/** A staff-queue order. `monthlyPence` is ADMIN-only (absent for a CLINICIAN). */
export interface StaffOrder {
  orderCaseId: string;
  /**
   * The HUMAN order reference (`ORD-…`) staff read out and search on — the
   * `orderCaseId` UUID stays the internal key. OPTIONAL/nullable: a row minted
   * before the reference column, or an older orders revision, carries none, so
   * every display falls back to the id (see `orderDisplayRef`).
   */
  reference?: string | null;
  /**
   * The clinic this case belongs to (contracts: required). Kept OPTIONAL here
   * defensively — older queue items minted before this field shipped may lack
   * it, and the UI must render either way.
   */
  clinicSlug?: string;
  /** contracts Lane: 'form' | 'callback' (the journey the case came in on). */
  lane: string;
  state: string;
  productKey: string | null;
  months: number | null;
  patientUserId: string | null;
  deliveryAddress: Address | null;
  createdAt: string;
  /**
   * Opaque ref to the clinic-service Assessment (Lane A) — the review read key.
   * OPTIONAL/nullable: older items may lack it even though the staff-queue
   * projection now includes it.
   */
  assessmentRef?: string | null;
  /**
   * What the patient confirmed they had been shown about this medicine's risks
   * before paying — the thing a clinician checks before approving (clinical
   * feedback, Reneal Patel, 2026-08-18).
   *
   * `null` IS MEANINGFUL AND MUST BE RENDERED, never hidden: it says the risks
   * were never acknowledged, so the clinician has to cover them before they
   * approve. `undefined` is the different, quieter case — an orders revision
   * that predates the field, where the portal knows nothing either way. The two
   * are kept apart deliberately; see `clinicians/SafetyAckBand.tsx`.
   */
  safetyAck?: StaffSafetyAcknowledgement | null;
  /* ── WHAT THIS ORDER STILL OWES A PRESCRIBER (2026-09-12) ────────────────
   *
   * Both are DERIVED SERVER-SIDE, on every read, from one call to orders-service's
   * `prescribingStatus` — so they are mutually exclusive by construction and the
   * portal never cross-checks them. Both are MONEY-FREE clinical facts and are
   * sent to CLINICIAN and ADMIN alike.
   *
   * ⚠️ OPTIONAL HERE, REQUIRED ON THE WIRE. orders-service sends both keys
   * always (an absent key reads to a UI as "still loading", and a paid patient
   * waiting for medicine must not look like a card whose data has not arrived).
   * They are optional in THIS type only so that a rolling deploy — an older
   * orders revision still answering the queue — type-checks. `undefined` means
   * "this build cannot tell", and `prescribingFlagsOf` renders nothing for it
   * rather than inventing work for a clinician. The fail-CLOSED half of this
   * lives on the server, where it stops a pharmacy send. */
  /**
   * Approved, PAID, and NO prescription exists yet — the normal resting place of
   * every newly approved order since `approve()` stopped auto-issuing one. The
   * clinician prescribes it through the ordinary formulary picker. NOT temporary.
   */
  awaitingPrescription?: boolean;
  /**
   * ⚠️ TEMPORARY. A prescription EXISTS and records no prescriber-chosen
   * strength — the finite v1 backlog minted by the deleted auto-issue lane. The
   * clinician re-prescribes it through `RePrescribeModal`. Deleted, along with
   * that modal and the chip, once it is false for every order in the book.
   */
  needsPrescriptionReapproval?: boolean;
  /** ADMIN-only — the clinician projection omits this key entirely. */
  monthlyPence?: number | null;
  /**
   * Is this case a REPEAT term (end-of-term re-subscribe) rather than new
   * business? (v0.8.7 — derived server-side from the successor subscription's
   * `priorSubscriptionId`, the ONE repeat discriminator.)
   *
   * MONEY-FREE, so it is safe on any staff surface. OPTIONAL: an older orders
   * revision omits it, and absent means "unknown", never "new" — nothing here
   * renders a claim on a missing key.
   */
  isRepeat?: boolean;
  /* ── Fulfilment tail (pharmacy dispensing lane, v0.8.0) ───────────────────
   * All OPTIONAL: a case that never reached the pharmacy carries none of them,
   * and an older orders revision carries none at all — so the admin drawer
   * renders what exists and shows nothing where there is nothing. These are
   * ADMIN visibility, not a clinician surface: the clinician console reads the
   * same StaffOrder but renders no fulfilment block. */
  /** When the pharmacist confirmed the dispense. */
  dispensedAt?: string | null;
  /** The coarse reason a pharmacy REFUSED to dispense — the admin "rejected"
   * flag. Distinct from a clinician rejection (state `rejected`); the patient
   * is never told either way. */
  dispenseRejectedReason?: string | null;
  dispenseRejectedAt?: string | null;
  courier?: string | null;
  trackingNumber?: string | null;
  dispatchedAt?: string | null;
}

/**
 * The SCR quick-copy block (Reshape §3.1) — first/last name, DOB, postcode,
 * sourced from the identity SCR read. Names are not captured on the patient
 * record yet (v3's MPI captures them at migration), so they may be null — the UI
 * renders what exists.
 */
export interface ScrBlock {
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  postcode: string | null;
}

/** One answered question in the structured review (never raw JSON on screen). */
export interface ReviewAnswer {
  questionKey: string;
  prompt: string;
  /** Human-readable rendered answer (option labels joined, yes/no, etc.). */
  answer: string;
  /** Free-text detail (e.g. the "if yes, which?" note on a yes/no question). */
  detail?: string;
}

/** A titled group of answers (eligibility, safety, weight, meds, preference, consent). */
export interface ReviewSection {
  key: string;
  title: string;
  items: ReviewAnswer[];
}

/** One section in the PMS summary — title + answer COUNT (never the items). */
export interface AssessmentSummarySection {
  key: string;
  title: string;
  itemCount: number;
}

/**
 * PMS data-minimised assessment summary (item 3, 2026-07-28) — `GET
 * /v1/clinic/assessments/:id/summary`. State / eligibility / BMI / section title +
 * answer count ONLY; NO names, answers, prior treatment or photo URL (that's the
 * full review). Backs the patient-record drawer's "Questionnaires" panel.
 *
 * [CONTRACTS GAP] — `assessmentSummary` is not yet in `@thegpservice/gpsclinics-contracts`;
 * this is a local mirror of the clinic-service shape. Fold into contracts if a second
 * consumer appears. NOT the same as `PatientAssessmentSummary` below — that is the
 * PATIENT self-read row (`/assessments/mine`); this is the STAFF PMS-panel projection.
 */
export interface AssessmentSummary {
  assessmentId: string;
  state: AssessmentState;
  eligibility: Eligibility;
  bmi: number | null;
  sections: AssessmentSummarySection[];
}

/**
 * Staff-only acquisition identity (contracts v0.6.8, additive) — the patient's
 * name resolved server-side from the assessment's step-0 Lead
 * (`Assessment.leadId → Lead`). NAME-ONLY; both fields nullable (the internal
 * Step-0 path captures neither). Lead is the single source of truth — never
 * copied onto the Assessment. This is the source of the patient NAME on the
 * clinician review; the identity SCR block still owns DOB/postcode.
 */
export interface AssessmentPatientIdentity {
  firstName: string | null;
  lastName: string | null;
}

/**
 * The clinician review of a submitted assessment. Structured — the raw versioned
 * answers Json is projected server-side into readable sections; the clinician
 * never sees raw JSON. Carries BMI, the soft-stop/clinician-review flags, any
 * recorded prior treatment, the weight photo ref, and the SCR-check consent
 * status. NO money field exists on this shape (clinicians never see money).
 */
export interface AssessmentReview {
  assessmentId: string;
  clinicSlug: string;
  state: AssessmentState;
  eligibility: Eligibility;
  patientUserId?: string | null;
  bmi?: number | null;
  bmiThreshold?: number | null;
  flags: string[];
  priorTreatment?: string | null;
  /** Storage ref for the Q14 weight photo (a signed read URL or object ref). */
  photoRef?: string | null;
  /**
   * A SIGNED, SHORT-LIVED read URL for the Q14 weight photo (v0.6.4,
   * additive) — present when object storage is configured; `null` in stub
   * mode even when `photoRef` exists (photo on file, no preview here).
   * Prefer this over `photoRef` for rendering; it expires — never cache it
   * beyond the current page view.
   */
  photoUrl?: string | null;
  /** Whether the patient consented to the Summary Care Record check (Q15). */
  scrCheckConsent?: boolean | null;
  sections: ReviewSection[];
  /**
   * Who filled the form (v0.6.5, additive): 'admin' when a staff member filled
   * it on the patient's behalf during a call, 'patient' otherwise. OPTIONAL —
   * older reviews predate the field; treat absent as 'patient'.
   */
  origin?: AssessmentOrigin;
  /** The staff user who filled an admin-initiated form (v0.6.5, additive). */
  originUserId?: string | null;
  /**
   * Staff-only acquisition name resolved from the step-0 Lead (contracts
   * v0.6.8, additive). OPTIONAL — older reviews predate the field; a leadless
   * or unnamed capture yields null fields. Prefer this for the patient name on
   * this screen (the identity SCR block keeps DOB/postcode).
   */
  patientIdentity?: AssessmentPatientIdentity | null;
  /**
   * R31 (contracts v0.7.1) — the submission tripped a mental-health-flagged hard
   * stop, so the review renders a crisis/safeguarding banner. STAFF read only;
   * OPTIONAL (older reviews / patient self-read omit it).
   */
  mhRisk?: boolean;
  /** Coarse hard-stop band (GDPR-3 — never the raw stopKey). */
  mhRiskCategory?: MhRiskCategory | null;
  /**
   * Earlier checks for this same journey that ended not-eligible, newest first
   * (clinic-service 2026-08-08). `[]` when nothing was superseded, and OPTIONAL
   * because a server that predates the feature omits the key entirely — treat
   * absent as empty, never as an error.
   *
   * STAFF read only: it is deliberately absent from the patient self-read.
   */
  priorStops?: PriorStop[];
}

/**
 * One earlier not-eligible check the current assessment resumes.
 *
 * GDPR-3 by construction: it carries the COARSE category band and NEVER the raw
 * stopKey, and no answer VALUE in either direction — only the boolean verdict of
 * a server-side comparison. Do not widen this to carry answers.
 */
export interface PriorStop {
  /** The superseded assessment. */
  assessmentId: string;
  /**
   * Which question stopped it, resolvable to a prompt from the rendered
   * sections. NULL on the submit-time lane, where the verdict came from the
   * whole questionnaire and is not attributable to one question — the UI must
   * then say the weaker, true thing rather than name a question.
   */
  questionKey: string | null;
  /** Coarse band only — never the raw stopKey. */
  category: MhRiskCategory;
  /**
   * R31 — that hop's mental-health flag. LOAD-BEARING: the current row's own
   * `mhRisk` is false whenever the patient changed the answer to clear the stop,
   * so this must be ORed into the crisis-banner decision or the banner goes dark
   * on exactly the cohort R31 protects.
   */
  mhRisk: boolean;
  /** Whether the answer actually differs now. NULL = not determinable. */
  answerChanged: boolean | null;
  /** When that journey ended. */
  stoppedAt: string;
  /** When the resuming journey began. */
  changedAt: string;
}

/** The coarse hard-stop band surfaced to staff (mirrors contracts `mhRiskCategory`). */
export type MhRiskCategory =
  | 'safety_condition'
  | 'bmi_floor'
  | 'bmi_gate'
  | 'age'
  | 'pregnancy'
  | 'clinical';

/**
 * ONE LICENSED VARIANT a prescriber may choose — contracts `medicationVariant`,
 * served by prices-service at `GET /v1/prices/formulary` (CLINICIAN/ADMIN).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THESE ARE ONE FACT IN FIVE FIELDS, NOT FIVE FACTS.
 *
 * `name` is the licensed description WHOLE — the exact string a dispenser
 * reads — and `drugName` + `strength` + `formulation` + `device` are that same
 * string decomposed AT SOURCE by a human, in a reviewed diff. The producer's
 * seed guard rebuilds `name` from the parts on every build and every boot and
 * throws if they disagree, and the contract carries the same refinement, so a
 * consumer may trust either and MUST PARSE NEITHER.
 *
 * That rule is the whole point. This portal used to manufacture a strength by
 * regex over a display string, and the pattern it used matched a COMMERCIAL
 * pricing anchor exactly — so a catalogue rename could have put a figure with
 * no clinical provenance into a dispensing console. Nothing in this repo splits
 * a clinical value out of another value ever again: if a field is needed, it
 * arrives as a field.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * `device` is nullable rather than optional on purpose: `null` is a positive
 * statement that the presentation has no injector, which a dispensing label has
 * to be able to tell apart from "the field was not sent". `pip`/`ean` are the
 * opposite — dispensing identifiers present only where the source sheet carried
 * them, so they are simply absent otherwise.
 *
 * ⚠️ `packQty` IS NOT A SUPPLY MULTIPLIER. It is the source sheet's pack figure
 * and for the device presentations it disagrees with the medication registry's
 * pack size. `quantity × packQty` renders a three-month supply as "3 packs
 * (3 units)" where twelve units are dispensed. NOTHING in this portal multiplies
 * by it, and nothing may start. [OPEN — CTO.]
 */
export interface MedicationVariant {
  variantKey: string;
  name: string;
  drugName: string;
  strength: string;
  formulation: string;
  device: string | null;
  packQty: number;
  /**
   * PIP code (seven digits) where the formulary carried one.
   *
   * `string | null | undefined`, all three: the source sheet omits the field on
   * some rows and sends an explicit `null` on others, and a consumer must not
   * be able to tell the two apart by accident. Nothing renders a dispensing
   * identifier today — they are carried so the pharmacy lane can adopt them
   * without a contract change.
   */
  pip?: string | null;
  /** Barcode (GTIN-8/13/14), digits only. Same three-state rule as `pip`. */
  ean?: string | null;
}

/**
 * One medication family and the variants a prescriber may choose within it
 * (contracts `medicationFormulary`).
 *
 * NO PRICE AND NO DEFAULT SELECTION travel on this shape, and neither may be
 * added: the patient is priced at the family's anchor, so a variant that moved
 * the price would make the prescribing screen a pricing screen — which is how a
 * commercial figure became a clinical one in the first place — and a
 * preselected variant would be a strength nobody chose, which is what the
 * anchor was.
 */
export interface MedicationFormulary {
  productKey: string;
  familyName: string;
  form: string;
  coldChain: boolean;
  variants: MedicationVariant[];
}

/**
 * `GET /v1/prices/formulary[?productKey=]` (contracts `formularyResponse`).
 *
 * `source` is provenance a prescriber can read. Clinical reference data with no
 * stated origin is an anonymous assertion, and this whole lane exists because an
 * unattributed strength-shaped value was believed once already.
 */
export interface FormularyResponse {
  source: string;
  medications: MedicationFormulary[];
}

/**
 * One prescribed line THE CLINICIAN IS POSTING — contracts `prescribeItemInput`.
 *
 * `variantKey` is the ONLY way a strength may reach a prescription from this
 * portal: the browser sends a key and the server resolves it against the
 * formulary and snapshots what IT says (R41 — the server is the control). The
 * contract REJECTS a client-posted `variant`, `strength`, `name` or `packQty`
 * outright, so there is no shape in which this portal could assert a clinical
 * fact even by accident.
 *
 * The key is OPTIONAL on the wire (a line prescribed outside the formulary has
 * none) but REQUIRED by the prescribe form — see `PrescribeForm`, which will not
 * submit a line whose variant has not been chosen and refuses to fall back to
 * free text when the formulary cannot be read.
 */
export interface PrescriptionItemInput {
  productKey: string;
  medicationName: string;
  /**
   * The prescriber's directions, VERBATIM — or absent.
   *
   * NULLABLE since contracts v0.14.0, and that is a correctness fix rather than
   * a loosening: as a required non-empty string the field FORCED its producers
   * to lie, and orders' auto-issue lane writes the literal string
   * "Not recorded" into it. Those rows are write-once. Read this field only
   * through `lib/prescribedLine.ts`, which treats both `null` and the persisted
   * sentinel as absent so the RENDER chooses the words.
   */
  dosage: string | null;
  /** Packs over the supply period — not units, and not months. */
  quantity: number;
  /** The chosen licensed variant. The server resolves it; the client never asserts it. */
  variantKey?: string;
}

/**
 * One prescribed line AS IT IS STORED AND READ BACK — contracts
 * `prescriptionItem`. Deliberately a DIFFERENT type from the one the form posts.
 *
 * Two differences, both load-bearing:
 *  - `dosage` is NULLABLE. It is the prescriber's own directions, and the
 *    auto-issue lane has none to record. It used to be a required non-empty
 *    string, which forced a literal "Not recorded" sentinel into a clinical
 *    field where no consumer could tell it apart from something a human typed.
 *  - `variant` is the SERVER'S resolved snapshot of the chosen variant, not a
 *    key. `null` means the line predates the formulary or was prescribed
 *    outside it — render that as an explicit absence, never as a blank.
 */
export interface PrescribedItem {
  productKey: string;
  medicationName: string;
  dosage: string | null;
  /** PACKS, as persisted. Render it through `lib/prescribedLine`, never raw. */
  quantity: number;
  /**
   * The SERVER'S rendering of `quantity` — the identical string the signed PDF
   * and the Pharmacy Express email carry (orders' `displayQuantity` seam).
   *
   * Optional because an older orders build does not send it. It exists so that
   * the portal never has to state the UNIT of a prescribed quantity itself: this
   * record used to print a bare `String(quantity)` while the document it
   * describes said "3 packs", so one prescription read three different ways.
   * See `lib/prescribedLine` for why the browser does not format its own.
   */
  displayQuantity?: string | null;
  variant?: MedicationVariant | null;
}

/**
 * contracts prescriptionIssueRequest — issued only from an approved order —
 * plus orders-service's local `supplies` extend (see {@link PrescriptionSupplyInput}).
 */
export interface PrescriptionIssueRequest {
  items: PrescriptionItemInput[];
  repeatFlag: boolean;
  notes?: string;
  /**
   * The ancillary supplies to dispense WITH this prescription (2026-09-24).
   *
   * ⚠️ PRESENT VS ABSENT IS A DIFFERENT INSTRUCTION, SERVER-SIDE. orders-service
   * reads an ABSENT key on a correction/supersede as "carry the previous
   * prescription's supplies forward", and a PRESENT one — `[]` included — as
   * "exactly these" — an explicit `[]` is STORED as `[]` (orders, review round
   * 2), distinct from NULL ("never decided"). BOTH prescribing surfaces —
   * `PrescribeForm` and `RePrescribeModal` — therefore ALWAYS send it: an
   * emptied list is the clinician's decision and must reach the server as one.
   *
   * ⚠️ THE MODAL USED TO OMIT IT, AND THAT WAS A DEFECT (review, 2026-09-24).
   * Every v1 backlog prescription predates the supplies column, so its stored
   * supplies are NULL; omitting the key carried forward `[]`, and the re-saved
   * injectable went to the pharmacy with nothing to use it with — with no control
   * on screen that could stop it. The modal now shows the same Supplies section,
   * seeded from the prior prescription or the suggestions, and posts it.
   */
  supplies?: PrescriptionSupplyInput[];
}

/* ── PRESCRIPTION SUPPLIES (2026-09-24, CTO + clinical lead) ────────────────
 *
 * HAND MIRRORS of orders-service's LOCAL strict schemas — a `[CONTRACTS GAP]`
 * extend, the estate's established pattern, so there is NO contracts version to
 * name here. The source is the shared supplies contract (orders
 * `fix/prescription-supplies`): `GET /v1/orders/:orderCaseId/prescribing-context`,
 * the `supplies` extend on `POST /v1/orders/:id/prescribe`, and the `supplies`
 * snapshot on the staff prescription reads and the pharmacy detail read.
 * When orders' schema and this mirror disagree, the SCHEMA is right.
 *
 * WHY SUPPLIES ARE NOT PRESCRIPTION LINES. A patient received her pens with
 * nothing to inject them with: the clinician wrote the requirement into the
 * free-text notes, and notes never reach the dispensing email. Supplies could not
 * ride on a medication line without bending invariants that line exists to hold
 * (a line must carry a prescribed strength, belong to the order's medication
 * family, and a priced catalogue entry reaches the patient's plan chooser), so
 * they are their own structured list, stored in their own column, and rendered in
 * their own clearly-labelled block on every surface. The portal never routes a
 * supply through `lib/prescribedLine` or `ui/PrescribedItems`.
 *
 * R28: no supply NAME is written in this repo. Names and units arrive from the
 * server at runtime (R41 — the browser posts a key and a quantity, nothing else).
 */

/** Why the server suggested a supply. The rule lives server-side, once (R41). */
export type SupplySuggestionReason = 'injectable' | 'first_prescription';

/** One entry of the supplies catalogue, as the prescribing context serves it. */
export interface SupplyCatalogueEntry {
  supplyKey: string;
  /** Display name — SERVER-authored. Never composed or edited in the browser. */
  name: string;
  unit: string;
  /** Whether the repeat engine carries this supply onto each monthly reissue. */
  onEachRepeat: boolean;
}

/** A supply the server suggests for THIS order, with the reason it applies. */
export interface SuggestedSupply {
  supplyKey: string;
  quantity: number;
  reason: SupplySuggestionReason;
}

/**
 * `GET /v1/orders/:orderCaseId/prescribing-context` (CLINICIAN/ADMIN).
 *
 * Money-free by construction. `suppliesAvailable: false` means orders could not
 * read the catalogue from prices-service — the prescribe form treats it exactly
 * like a failed read (an error, a retry, and Issue blocked), because this
 * incident IS a missing supply and "no suggestions" must never be how an outage
 * looks. `firstPrescription: null` means the server could not tell; the rule it
 * feeds already fails TOWARD suggesting the sharps bin, so the portal reads
 * nothing into it. (orders counts a prescription toward it only once it has
 * actually been SENT to the pharmacy.)
 */
export interface PrescribingContext {
  firstPrescription: boolean | null;
  suppliesAvailable: boolean;
  catalogue: SupplyCatalogueEntry[];
  suggestedSupplies: SuggestedSupply[];
  /**
   * The supplies stored on this case's LATEST prescription, key + quantity —
   * `null` when the case has none, or when that prescription stores none.
   *
   * A re-issue ("Issue another prescription", or the v1 backlog re-save) seeds
   * its rows from THIS first when it is non-empty, so the clinician starts from
   * what went out last time at their own quantities — and then adds every
   * `suggestedSupplies` key this list does not already carry, so a server that
   * says "first-time, include a bin" is never silently overridden by an older
   * document (see `clinicians/supplyRows.ts#seedSupplyRows`). Every key must be
   * in `catalogue`, exactly as a suggestion's must, or the read is treated as
   * failed.
   *
   * OPTIONAL because an orders build that predates the field omits it; ABSENT
   * reads as `null` (see `clinicians/supplyRows.ts`).
   */
  priorSupplies?: PrescriptionSupplyInput[] | null;
}

/**
 * One supply THE CLINICIAN IS POSTING. A key and a whole-number quantity
 * (1..50) and nothing else: the server resolves the key against the catalogue
 * and snapshots the name, unit and repeat rule IT holds (R41). An unknown key is
 * a 400; a catalogue outage with supplies on the request is a 503 — never a
 * silently dropped supply.
 */
export interface PrescriptionSupplyInput {
  supplyKey: string;
  quantity: number;
}

/**
 * A supply AS STORED ON AN ISSUED PRESCRIPTION — the server's snapshot, read
 * back on the staff prescription reads and on the pharmacy detail. Rendered by
 * `ui/PrescribedSupplies` and nothing else.
 */
export interface PrescribedSupply {
  name: string;
  quantity: number;
  unit: string;
}

/** contracts prescriptionResult — the issue → PDF → pharmacy-send outcome. */
export interface PrescriptionResult {
  prescriptionId: string;
  orderCaseId: string;
  repeatFlag: boolean;
  pdfRef: string | null;
  pharmacySent: boolean;
  issuedAt: string;
}

/* ── THE MEDICATION CORRECTION LANE — DELETED 2026-09-12 ───────────────────
 *
 * `MedicationAmendmentRequest` / `…Result` / `AmendmentCharge` /
 * `AmendmentNotifications` / `AmendmentNoChargeReason` described
 * `POST /v1/orders/order-cases/:id/medication-amendment`, a bespoke route for
 * filling in a missing strength on an already-issued prescription.
 *
 * ORDERS-SERVICE DELETED THAT ROUTE the same day it deleted the auto-issue that
 * created the need for it, and this portal kept the button mounted for every
 * clinician on every case with a prescription — a 404 on the exact screen, for
 * the exact defect, which a fixture stubbing the route 200 hid from every test.
 * The types are gone with it rather than left as a shape nothing can produce.
 *
 * WHAT REPLACED IT: `clinicians/RePrescribeModal.tsx`, which posts the ORDINARY
 * `POST /v1/orders/:id/prescribe` — one lane, one set of gates, and no second
 * route to drift out of step. See `StaffOrder.needsPrescriptionReapproval`.
 */

/**
 * One previously-issued prescription as staff see it (GET
 * /v1/orders/:orderCaseId/prescriptions, CLINICIAN/ADMIN). Mirrors contracts
 * `staffPrescription` field-for-field. Money-free by construction — no field
 * on this shape carries an amount.
 */
export interface StaffPrescription {
  prescriptionId: string;
  orderCaseId: string;
  repeatFlag: boolean;
  issuedAt: string;
  issuedBy?: string | null;
  items?: PrescribedItem[] | null;
  pdfRef?: string | null;
  /**
   * The supplies dispensed WITH this prescription — orders' local
   * `staffPrescriptionWithDisplayQuantity` extend (2026-09-24, see
   * {@link PrescribedSupply}). `[]` means the prescription lists none.
   *
   * OPTIONAL because an orders build that predates supplies omits the key, and
   * ABSENT IS NOT NONE: `ui/PrescribedSupplies` renders nothing for a missing
   * key (this build cannot tell) and says "none listed" only for an explicit
   * `[]`. Declared on this (first) declaration only — the second
   * `StaffPrescription` further down merges into this one.
   */
  supplies?: PrescribedSupply[] | null;
}

/** GET /v1/orders/:orderCaseId/prescriptions response — newest first. */
export interface StaffPrescriptionsResponse {
  items: StaffPrescription[];
}

/** Money-free activation summary returned to the clinician who approves. */
export interface ApproveResult {
  orderCaseId: string;
  state: string;
  subscriptionId?: string;
}

/** The reject outcome (hold released; case marked rejected). */
export interface RejectResult {
  orderCaseId: string;
  state: string;
}

/**
 * contracts callbackStatus (v0.6.7) — the ticketing lifecycle. 'parked' is
 * set ONLY by the server's auto-escalation state machine (3 missed clinician
 * attempts -> escalated -> 3 missed admin attempts -> parked, then the
 * parked-lead nurture lane picks it up) — never a manual transition, so the
 * UI never offers it as a status-select option.
 */
export type CallbackStatus = 'open' | 'in_progress' | 'escalated' | 'parked' | 'closed';

/** contracts callbackOutcome (v0.6.4) — how a closed callback was resolved. */
export type CallbackOutcome =
  | 'video_appointment'
  | 'online_booking'
  | 'not_proceeding'
  | 'escalated_to_clinician'
  | 'other';

/**
 * The persisted form of a callback-lane submission — the shape the ticket
 * writes echo (e.g. PATCH …/called; the standalone staff list read retired
 * with the CRM pipeline, 2026-07-21). Mirrors contracts `callbackSubmission`
 * field-for-field: the contact fields are `firstName` / `lastName` / `phone`
 * (NOT `contactName` / `contactPhone` — the portal composes a display name
 * from the two name fields). `status` / `calledAt` / `outcome` / `closedAt`
 * are the v0.6.4 ticketing fields — optional so an older projection without
 * them still type-checks.
 */
export interface CallbackSubmission {
  callbackId: string;
  clinicSlug: string;
  /** contracts Lane: 'form' | 'callback'. */
  lane: string;
  state: string;
  status?: CallbackStatus;
  calledAt?: string | null;
  outcome?: CallbackOutcome | null;
  closedAt?: string | null;
  createdAt: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  preferredTime?: string | null;
  /** Resolved from the linked step-0 Lead (v0.6.7); null with no leadId or no match. */
  email?: string | null;
}

/** contracts paymentFailureReason. */
export type PaymentFailureReason = 'card_declined' | 'hold_lapsed';

/**
 * One failed renewal capture or at-risk/lapsed hold (GET
 * /v1/orders/admin/failed-payments, ADMIN-only). The first eight fields mirror
 * contracts `paymentFailureAlert` field-for-field; `isHold` and `holdLapsed`
 * mirror orders' local `paymentFailureAlertExt` widening (a [CONTRACTS GAP]
 * there too — promoted on the next pin sweep).
 *
 * WHY THE SPLIT IS SERVER-SENT AND NOT DERIVED HERE. The bucket is also
 * computable as `failedAt < now`, and this board must NOT do that: an admin
 * laptop with a skewed clock would render a still-capturable hold as dead (or
 * the reverse), and which side of the line a row falls on is a policy decision
 * taken against the server's true-UTC clock.
 *
 * `reason` is the SOURCE, `holdLapsed` is the FACT — they are not redundant.
 * The `reason` enum is pinned to two values, so an at-risk hold still arrives
 * as `reason:'hold_lapsed'` even though it has NOT lapsed yet; read the pair
 * as (source, urgency).
 *
 * BOTH ARE OPTIONAL ON PURPOSE. They are a local orders widening, so the
 * portal can ship ahead of the orders deploy that emits them. Absent
 * `holdLapsed` means "an orders build from before the at-risk read" — and on
 * those builds the query started at the LAPSE line, so every hold row it
 * returned really was lapsed. Absent therefore reads as lapsed, which is the
 * honest fallback: guessing "still savable" would tell a desk to chase money
 * that is already gone.
 */
export interface PaymentFailureAlert {
  orderCaseId: string;
  subscriptionId?: string | null;
  clinicSlug: string;
  reason: PaymentFailureReason;
  amountPence: number;
  attemptCount: number;
  state: string;
  /** The failure time on a decline / lapsed hold — but the capture DEADLINE (a future timestamp) on an at-risk hold. */
  failedAt: string;
  /** Source discriminator: this row is a month-1 pre-auth, not a renewal capture. */
  isHold?: boolean;
  /** Urgency discriminator on a hold row: false = at risk but still capturable, true = past the lapse estimate. */
  holdLapsed?: boolean;
  /**
   * The order behind this alert is a staff-marked TEST record (orders
   * `paymentFailureAlertExt`, 2026-09-11). Sent on every alert in both modes —
   * `false` throughout the default read, the truth per row under
   * `?includeTest=true` — so the table badges from the row, never from the
   * request it sent.
   *
   * OPTIONAL because an orders build from before the list view's reveal omits
   * it. Absent is "not told", not "not a test", so the badge (and the withheld
   * payment-link control) key on `=== true` only.
   */
  isTest?: boolean;
}

/** GET /v1/orders/admin/failed-payments response. */
export interface PaymentFailureList {
  items: PaymentFailureAlert[];
}

/* ── Admin: the hosted payment link (failed-payment / card-update lane) ─────
 *
 * POST /v1/orders/:id/payment-link (ADMIN ONLY — @Roles('ADMIN') in
 * orders.controller.ts). This is a live Stripe payment page for the patient's
 * OWN money, which is why it is not on the clinician surface: "clinicians never
 * see money" holds, and the one carve-out in this estate is a clinician's own
 * compensation, which this is not. The CLINICIAN+ADMIN control is the PLAN link
 * (`lib/planResumeLink.ts`) — a different lane entirely.
 *
 * [CONTRACTS GAP] `emailOutcome` is a local orders widening (`paymentLinkResult`
 * in its dto.ts, `.extend()`ed from the strict contracts `paymentLink`). Not in
 * contracts yet — promoted on the next pin sweep.
 */

/**
 * What happened to the patient email that carries the minted link.
 *
 * The route answers 200 EVEN WHEN THE MAIL FAILED, and deliberately so: this
 * body carries the minted URL, which is the entire fallback. A 503 would
 * discard it and leave a live payment link at Stripe that no human can see. So
 * a 200 proves a LINK, never an email — the portal must read this field and
 * must never infer "emailed" from the status code.
 *
 *  - `sent`          the patient has been emailed the link.
 *  - `already_sent`  notifications suppressed a duplicate (a second click, or a
 *                    second mint of the same order). NOT a failure — the patient
 *                    already holds this exact email. Nothing was re-sent.
 *  - `no_recipient`  a guest order (no patientUserId) or no address on file.
 *                    RETRYING WILL NEVER WORK — this is the WhatsApp path.
 *  - `failed`        notifications rejected it / was unreachable / the 2s abort
 *                    budget hit. A retry genuinely can send (notifications
 *                    leaves a re-claimable claim row).
 *  - `stub`          NOTIFICATIONS_SERVICE_URL is not provisioned here
 *                    (dev/local). Nothing was transmitted.
 */
export type PaymentLinkEmailOutcome = 'sent' | 'already_sent' | 'no_recipient' | 'failed' | 'stub';

/**
 * POST /v1/orders/:id/payment-link result. `:id` is the OrderCase UUID (the PK,
 * not the `ORD-` display reference).
 *
 * `emailOutcome` is OPTIONAL because it is the local widening above: an orders
 * build from before it simply omits the field, and absent must read as
 * not-emailed (the copy path), never as sent.
 */
export interface PaymentLinkResult {
  orderCaseId: string;
  /** The Stripe-hosted payment page — the thing the admin copies. */
  url: string;
  expiresAt: string;
  emailOutcome?: PaymentLinkEmailOutcome;
}

/**
 * One per-case clinician note (orders-service, G3 — pinned wire shape). Mirrors
 * the clinic-service CallbackNote pattern onto the OrderCase: authored,
 * timestamped, editable by its own author only. `body` is SANITISED rich text —
 * the server strips it to a safe tag allowlist, and the client applies a second,
 * defence-in-depth strip (see lib/sanitizeHtml) before ever rendering it as
 * HTML. `authorEmail` is null server-side (PHI-free events, R19); the portal
 * resolves the display name via the identity clinician-record read, falling
 * back to the raw id.
 */
export interface CaseNote {
  noteId: string;
  authorUserId: string;
  authorEmail?: string | null;
  body: string;
  createdAt: string;
  editedAt?: string | null;
}

/** GET /v1/orders/order-cases/:id/notes response — chronological (oldest→newest). */
export interface CaseNoteList {
  items: CaseNote[];
}

/* ── Admin: plan management (prices-service) + user invites (identity) ────── */

/** Commitment terms (contracts PlanMonths). */
export type PlanMonths = 1 | 3 | 6 | 12;

/**
 * A medication registry entry. `form` is the contracts delivery-form enum (three
 * values: the injectable form, pill, capsule) — held as a plain string here so
 * this repo's source stays clean under the R28 banned-terms scan; the API
 * validates the enum. The admin form assembles its option values from fragments.
 */
export interface Medication {
  productKey: string;
  name: string;
  form: string;
  packSize: number;
  unitsPerMonth: number;
  coldChain: boolean;
}

/** Editable fields on a medication (productKey is the row identity — not editable). */
export type MedicationUpdate = Partial<Omit<Medication, 'productKey'>>;

/** One clinic × medication × term → monthly integer pence. */
export interface PlanPrice {
  clinicSlug: string;
  productKey: string;
  months: PlanMonths;
  monthlyPence: number;
}

/* ── Price-change governance (contracts v0.8.3, 2026-07-30) ─────────────────
 *
 * A plan-price never moves without a recorded REASON, and the change is written
 * to an append-only audit the admin can read back. [CONTRACTS GAP]: the portal
 * carries no backend dependency, so these mirror the v0.8.3 contract shapes
 * locally (the gateway validates the real contract). Money stays integer PENCE;
 * `note` is COMMERCIAL text (never PHI) and safe to display.
 */

/**
 * Why a plan price changed — recorded on every write (contracts
 * `priceChangeReason`). Two commercial reasons only; the admin surface maps
 * each to a friendly label.
 */
export type PriceChangeReason = 'pharmacy_express_update' | 'internal_gps_decision';

/**
 * POST /v1/prices/plan-prices body (contracts `planPriceUpsert`): the
 * {@link PlanPrice} shape PLUS a REQUIRED price-change `reason` and an OPTIONAL
 * `note` (≤280 chars). The admin drawer sends this whenever a term actually
 * changed; the same reason/note applies to every changed term in one save.
 */
export interface PlanPriceUpsert extends PlanPrice {
  reason: PriceChangeReason;
  note?: string;
}

/**
 * One row of the plan-price change history (contracts `priceChangeAuditEntry`)
 * — GET /v1/prices/plan-prices/audit, newest first, ADMIN-only.
 * `oldMonthlyPence` is null when the term is being priced for the FIRST time
 * (rendered as "—"). `actorUserId` is the verified ADMIN who made the change.
 */
export interface PriceChangeAuditEntry {
  id: string;
  actorUserId: string;
  clinicSlug: string;
  productKey: string;
  months: PlanMonths;
  oldMonthlyPence: number | null;
  newMonthlyPence: number;
  reason: PriceChangeReason;
  note: string | null;
  changedAt: string;
}

/** The editable margin model (all fees are integer pence). */
export interface PricingSettings {
  clinicSlug: string;
  costMarkupPct: number;
  gpsFeePence: number;
  pxFeePence: number;
  deliveryFeePence: number;
  repeatRxFeePence: number;
  coldChainFeePence: number | null;
}

/**
 * contracts prescriberType (v0.6.4) — drives whether the registration number
 * is labelled GMC or GPhC on the prescription PDF/email, and (independent
 * prescribers) a generic "Registration number" label.
 */
export type PrescriberType = 'doctor' | 'independent_prescriber' | 'pharmacist';

/**
 * Invite a clinician/admin (email + role, plus clinician-record fields).
 * `prescriberType` is now REQUIRED by the gateway for a CLINICIAN invite
 * (v0.6.4) — kept optional here so a non-clinician (ADMIN) invite, which omits
 * it, still type-checks.
 */
export interface InviteCreateRequest {
  email: string;
  role: 'CLINICIAN' | 'ADMIN';
  fullName?: string;
  registrationNumber?: string;
  prescriberType?: PrescriberType | null;
}

/** The created invite — the raw token is returned once (to build the link). */
export interface InviteCreated {
  token: string;
  email: string;
  role: 'CLINICIAN' | 'ADMIN';
  expiresAt: string;
}

/** POST /v1/identity/admin/invites/resend body. */
export interface InviteResendRequest {
  email: string;
}

/**
 * One active staff account (GET /v1/identity/admin/users, ADMIN only). Powers
 * the Users tab table. `fullName`/`registrationNumber`/`prescriberType` are
 * only ever set for a CLINICIAN (captured at invite time, for the
 * prescription PDF).
 */
export interface StaffUserRow {
  userId: string;
  email: string;
  role: 'CLINICIAN' | 'ADMIN';
  fullName?: string | null;
  registrationNumber?: string | null;
  prescriberType?: PrescriberType | null;
  emailVerified: boolean;
  createdAt: string;
}

/** One invite issued but not yet accepted — listed alongside active staff users. */
export interface StaffPendingInvite {
  email: string;
  role: 'CLINICIAN' | 'ADMIN';
  fullName?: string | null;
  expiresAt: string;
}

/** GET /v1/identity/admin/users response — the whole staff directory. */
export interface StaffUsersList {
  users: StaffUserRow[];
  pendingInvites: StaffPendingInvite[];
}

/** POST /v1/identity/admin/users/lookup body — bulk id→email resolution (ADMIN). */
export interface StaffUserLookupRequest {
  userIds: string[];
}

/**
 * POST /v1/identity/admin/users/lookup response.
 *
 * ✅ `fullName` IS SENT (shipped 2026-09-06). Kept as `?:` here deliberately —
 * see the last paragraph.
 *
 * ⚠ IT WAS NOT, FOR A YEAR, AND THE HISTORY IS THE USEFUL PART. An earlier
 * version of this comment claimed identity had returned the name since
 * "2026-07-21, Wave 3b". That was false and had been false all along:
 * `staffUserLookupResponse` was `.strict()` over `{ userId, email }`, and
 * `.strict()` means an extra key throws on parse — so the field could not
 * arrive even if the row had one. Every `fullName?.trim() || email` in the
 * portal resolved to the EMAIL, on every row, and anywhere the product copy
 * promised "a name, not an email" it was showing an address. A comment
 * asserting a wire nobody had checked is what let that stand for a year.
 *
 * What actually shipped: contracts v0.11.0 carries `fullName` on the row as
 * REQUIRED-but-nullable (a producer that forgets it now fails its own parse,
 * rather than quietly reverting every staff surface to emails), and
 * identity-service adds it to the `lookupUsers` select and response. No RBAC
 * change; no portal change was needed, because the fallback was already right.
 *
 * STILL `?:` AND NOT REQUIRED HERE, on purpose. This is the portal's view of a
 * response it does not control, and it must stay readable against a
 * clinic/identity deploy that has not yet caught up — during a promote the
 * portal can be newer than the service for minutes. `=== undefined` and a
 * stored `null` (the seeded master admin, which genuinely has no name in any
 * environment) both fall back to the email, which is the correct render for
 * both.
 */
export interface StaffUserLookupResponse {
  users: Array<{ userId: string; email: string; fullName?: string | null }>;
}

/**
 * GET /v1/identity/clinician/me/profile — the signed-in clinician's own
 * prescription-relevant details (full name, registration number, and whether a
 * signature is on file for the prescription PDF). A caller with no clinician
 * record (e.g. the master admin) gets a 404 rather than this shape.
 */
export interface ClinicianProfile {
  userId: string;
  fullName: string;
  registrationNumber: string;
  signatureSet: boolean;
}

/* ── Admin: subscriptions/payments read (v0.6.2) ───────────────────────────
 *
 * Local mirrors of contracts `staffSubscription` / `staffPaymentRecord` /
 * `staffSubscriptionItem` / `staffSubscriptionsResponse` (GET
 * /v1/orders/admin/subscriptions, ADMIN-only). This is a money read, so it
 * stays ids/enums/amounts/dates ONLY — never patient email/name.
 */

/** contracts SubscriptionState. */
export type SubscriptionState = 'dormant' | 'active' | 'past_due' | 'cancelled' | 'completed';

/**
 * One subscription row for the admin billing console. Mirrors contracts
 * `staffSubscription` field-for-field.
 */
export interface StaffSubscription {
  subscriptionId: string;
  orderCaseId: string;
  /** The patient who owns this subscription (v0.6.3, additive) — resolve to an email via the batched staff-user lookup; fall back to the id when missing. */
  patientUserId?: string | null;
  clinicSlug: string;
  productKey: string;
  months: PlanMonths;
  monthlyPence: number;
  /** Whole months left on the commitment (0 once served in full). */
  monthsRemaining: number;
  /** Next monthly bill; null/absent when dormant/completed/cancelled. */
  nextPaymentDate?: string | null;
  state: SubscriptionState;
  createdAt: string;
}

/** contracts paymentRecord phase (the admin billing ledger). */
export type StaffPaymentPhase = 'held' | 'captured' | 'released' | 'refunded';

/**
 * One payment-ledger row for the admin billing console. Mirrors contracts
 * `staffPaymentRecord` field-for-field.
 */
export interface StaffPaymentRecord {
  paymentRecordId: string;
  orderCaseId: string;
  amountPence: number;
  phase: StaffPaymentPhase;
  occurredAt: string;
}

/** One subscription with its payment ledger, most-recent-payment-first. */
export interface StaffSubscriptionItem {
  subscription: StaffSubscription;
  payments: StaffPaymentRecord[];
  /**
   * The order this subscription bills for is a staff-marked TEST record (orders
   * `staffSubscriptionItemExt`, 2026-09-11).
   *
   * ON THE ITEM, beside `subscription` and `payments` — not inside
   * `subscription`, which mirrors the strict contracts shape field-for-field.
   * The flag is a fact about the ORDER, and the payment ledger beside it hangs
   * off the same order, so this one field marks both.
   *
   * OPTIONAL because an older orders deploy omits it; absent is "not told", so
   * the Test badge renders on `=== true` only.
   */
  isTest?: boolean;
}

/**
 * GET /v1/orders/admin/subscriptions response — every subscription, newest
 * first. Test records are in it only under `?includeTest=true`.
 */
export interface StaffSubscriptionsResponse {
  items: StaffSubscriptionItem[];
}

/* ── Admin: the payments PIPELINE board (CRM PR 5/5, 2026-07-21) ───────────
 *
 * Mirror of orders-service `paymentsPipelineResponse` ([CONTRACTS GAP] —
 * local zod in its dto.ts): every subscription as a card in exactly one of
 * four money columns. ADMIN-only — this shape carries money and must never
 * be fetched from a clinician surface.
 */
export type PaymentsPipelineColumnKey = 'ongoing' | 'failed' | 'cancelled' | 'completed';

export interface PaymentsPipelineCard {
  subscriptionId: string;
  orderCaseId: string;
  patientUserId: string;
  productKey: string;
  months: number;
  monthlyPence: number;
  state: SubscriptionState;
  /** dormant — the month-1 hold exists but nothing is captured yet. */
  awaitingFirstPayment: boolean;
  monthsElapsed: number;
  monthsRemaining: number;
  retryCount: number;
  startedAt: string | null;
  nextPaymentDate: string | null;
  /** months × monthlyPence — the commitment's lifetime value. */
  ltvPence: number;
  /** Σ captured payments on the case (holds/releases/refunds never count). */
  securedPence: number;
  /** monthsRemaining × monthlyPence while billing can still happen; 0 on terminals. */
  estimatedRemainingPence: number;
  /** Visible-but-unbanked: the dormant month-1 hold or the failing renewal amount. */
  stuckPence: number;
  /**
   * This case is a staff-marked TEST record — a QA or marketing dry run whose
   * pence are not real money (orders `isTest`, 2026-09-05).
   *
   * orders-service sends it on EVERY card in BOTH modes: `false` throughout the
   * default read (which excludes the flagged cases anyway), the truth per row in
   * the `?includeTest=true` audit view. So the portal renders the badge on the
   * field alone and never has to infer the view from the request it sent.
   *
   * OPTIONAL HERE for the estate's usual reason — orders-service and the portal
   * deploy independently, and a required field would make the board crash rather
   * than degrade during the window where the portal is ahead. Absent means "this
   * server does not report it", NEVER "not a test": the badge renders on
   * `=== true` only, so an old server shows the board it always showed.
   */
  isTest?: boolean;
}

export interface PaymentsPipelineColumn {
  key: PaymentsPipelineColumnKey;
  cards: PaymentsPipelineCard[];
  totals: { securedPence: number; estimatedRemainingPence: number; stuckPence: number };
}

/** GET /v1/orders/admin/payments-pipeline response. */
export interface PaymentsPipelineResponse {
  columns: PaymentsPipelineColumn[];
  totals: { securedPence: number; estimatedRemainingPence: number; stuckPence: number };
}

/* ── Admin: staff-admin operations (v0.6.3) ────────────────────────────────
 *
 * Local mirrors of contracts `staffPrescription` / `staffPharmacySend` /
 * `staffSubscriptionDetail` (GET /v1/orders/admin/subscriptions/:id,
 * ADMIN-only) — the subscription drill-down the "Orders & subscriptions" tab
 * opens in a Drawer on row click.
 */

/**
 * An issued prescription as staff see it (items are clinical, PDF-bound
 * content — permitted on the authed staff read, NEVER in an event/log).
 */
export interface StaffPrescription {
  prescriptionId: string;
  orderCaseId: string;
  repeatFlag: boolean;
  issuedAt: string;
  issuedBy?: string | null;
  items?: PrescribedItem[] | null;
  pdfRef?: string | null;
}

/** A Pharmacy Express send receipt row (ids + timestamp — the PDF stays in storage). */
export interface StaffPharmacySend {
  pharmacySendId: string;
  prescriptionId: string;
  channel: string;
  pdfRef?: string | null;
  sentAt: string | null;
}

/**
 * GET /v1/orders/admin/subscriptions/:id — the subscription drill-down: the
 * sub + its order case (ADMIN money view), payments, prescriptions and
 * pharmacy sends — "what happened on this subscription".
 */
export interface StaffSubscriptionDetail {
  subscription: StaffSubscription;
  order: StaffOrder | null;
  payments: StaffPaymentRecord[];
  prescriptions: StaffPrescription[];
  pharmacySends: StaffPharmacySend[];
  /**
   * Whether this finished plan can be re-subscribed, and if not, why (v0.8.7).
   * Drives the drawer's Re-subscribe control. OPTIONAL on purpose: an orders
   * revision predating the lane omits the key, and the drawer then renders no
   * re-subscribe block at all — a missing key is "this server cannot answer",
   * not "no".
   */
  resubscribe?: ResubscribeEligibility;
}

/* ── Staff: assessment origin + clinician plan-link handover (v0.6.5,
 * moved ADMIN→CLINICIAN 2026-07-20) ───────────────────────────────────────
 *
 * Local mirrors for the fill-on-behalf-of-patient slice: assessment rows and
 * the review carry `origin` (who filled the form), and POST
 * /v1/clinic/assessments/:id/handover (CLINICIAN) emails the patient a
 * "choose your plan" link for a submitted, eligible/clinician_review
 * assessment (409 otherwise, and 409 when no lead email is on file).
 */

/** contracts assessmentOrigin (v0.6.5) — who filled the form. */
export type AssessmentOrigin = 'patient' | 'clinician';

/**
 * POST /v1/clinic/assessments/:id/handover result — the plan-link email send.
 *
 * PINNED TO THE REAL WIRE SHAPE (2026-09-04). This used to read
 * `{assessmentId, emailed}`, which clinic-service has never sent: its
 * `handoverResult` schema is a strict `{sent, stub?}`. Nothing read the body
 * before — the clinician's Authorise→Confirm just assumed a 201 meant
 * "emailed" — so the drift was invisible. The re-send control DOES read it,
 * and it has to tell an admin the truth: `sent:false, stub:true` is a
 * `NOTIFICATIONS_SERVICE_URL`-less environment where nothing left the process,
 * and rendering that as "emailed" would send a back-office desk away from a
 * patient who is still stuck.
 */
export interface HandoverResult {
  sent: boolean;
  /** Present (and true) only in stub mode — notifications is not provisioned here. */
  stub?: boolean;
}

/**
 * POST /v1/clinic/assessments/:id/approve-plan result — THE CLINICIAN'S
 * APPROVAL, and what the server actually did with it (2026-09-07).
 *
 * ⚠️ `outcome` IS NOT DECORATION AND A CALLER MUST BRANCH ON IT. `charged` means
 * the patient's card was taken; `plan_link_sent` means an email went out asking
 * them to pay. Telling a clinician "recommendations emailed to the patient" when
 * we have in fact just charged them is a claim about a patient's money that is
 * not true, and the kind of thing that goes unnoticed until somebody phones
 * about it.
 *
 * ⚠️ AND `dispatch` IS THE SECOND FIELD TO BRANCH ON — a charge is NOT a
 * dispatch. See the field below; "gone to the pharmacy" is only true for
 * `dispatch:'sent'`.
 *
 * ⚠️ THE UI IS NOT THE CONTROL (R41). This screen does not choose the lane and
 * cannot: it posts the approval, clinic-service asks orders-service — the owner
 * of the money — and orders-service decides. All this type carries is the
 * ANSWER. Nothing here may be used to pick an endpoint or to ask for a capture.
 *
 * MONEY-FREE: there is no pence field, because a clinician needs the outcome and
 * never the price (Reshape §3.1).
 */
export interface ApprovePlanResult {
  outcome: 'charged' | 'plan_link_sent';
  /** Whether a plan-link email left the process. Always `false` on `charged` — nothing was sent. */
  sent: boolean;
  /**
   * ⚠️ WHETHER THE PHARMACY ACTUALLY HAS IT, AND IT IS A SECOND THING TO BRANCH
   * ON (2026-09-07). `outcome:'charged'` guarantees the MONEY and only the money:
   * orders-service captures month 1 and then issues the prescription and posts it
   * to the pharmacy post-commit and best-effort, precisely so that a failure in
   * that tail cannot unwind a banked charge. The capture can therefore succeed
   * with the dispatch failing behind it.
   *
   *  · `sent`    — a confirmed pharmacy send exists. This is the ONLY value that
   *                licenses a screen to say the prescription has gone.
   *  · `pending` — the money is safe and the pharmacy does NOT have it. A PAID
   *                order that still needs finishing, and the clinician is the
   *                only person in a position to notice.
   *  · `null`    — on `plan_link_sent`: nothing was captured, so there is no
   *                pharmacy step to describe.
   */
  dispatch: 'sent' | 'pending' | null;
  /** Present (and true) only in stub mode — notifications is not provisioned here. */
  stub?: boolean;
  /** The `ORD-…` reference staff quote to a patient, when there was an order to name. */
  orderReference: string | null;
  /**
   * WHY the link lane was taken (`no_order`, `not_authorised`, `changed_product`,
   * `changed_term`, `gate_refused`, `no_recommendation`, `orders_stub`), or null
   * on `charged`. A plain string, not a union: it is the servers' vocabulary and
   * a value this build has not heard of must not break a screen.
   */
  reason: string | null;
}

/* ── Admin: callbacks overhaul (v0.6.5, trimmed 2026-07-21) ────────────────
 *
 * Local mirrors of the clinic-service callback ACTIVITY write (the deal
 * drawer's composer — the standalone counts/detail/patient-context reads
 * retired with the CRM pipeline), plus the identity admin email-search and
 * clinician-record reads. [CONTRACTS GAP]: these wire shapes are not in
 * @thegpservice/gpsclinics-contracts yet — promote them once the batch lands
 * (established pattern: identity `clinicianRecordRead`, orders
 * `prescription_px`).
 */

/** The activity kinds a callback timeline entry can carry (v0.6.5). */
export type CallbackActivityKind = 'note' | 'call_attempt' | 'email';

/**
 * How a logged call attempt went. 'disconnected' added v0.6.7 — connected then
 * dropped. **'call_back_later' added 2026-09-06** — the patient ANSWERED and
 * asked us to try again at a better time.
 *
 * ⚠️ IT WAS IN clinic-service's ENUM AND MISSING FROM THIS ONE, WHICH BROKE THE
 * TIMELINE. The server has accepted `call_back_later` since 2026-09-05 and
 * treats it specially — it is the one outcome that does NOT advance a patient
 * towards being parked into `unreachable`, because a patient who picks up and
 * says "call me Tuesday" is the most engaged person in the queue. This union
 * omitted it, so the composer could not offer it (staff had to log "answered",
 * which DOES spend a strike) and `CALL_OUTCOME_TEXT[outcome]` returned
 * `undefined` for a row that carried it — rendering the literal string
 * "Called — undefined" on the drawer timeline.
 */
export type CallAttemptOutcome =
  | 'answered'
  | 'no_answer'
  | 'voicemail'
  | 'busy'
  | 'disconnected'
  | 'call_back_later';

/**
 * One timeline entry on a callback ticket (v0.6.5) — a note, a call attempt
 * or a logged email, authored and timestamped. `body` is SANITISED rich text
 * for a `note` (server-stripped, then stripped AGAIN client-side via
 * lib/sanitizeHtml before it is ever rendered as HTML) and an optional PLAIN
 * short note for `call_attempt`/`email` (rendered as text, never HTML).
 */
export interface CallbackActivity {
  activityId: string;
  kind: CallbackActivityKind;
  authorUserId: string;
  authorEmail?: string | null;
  body?: string | null;
  /** call_attempt only. */
  outcome?: CallAttemptOutcome | null;
  createdAt: string;
  editedAt?: string | null;
}

/** POST /v1/clinic/callbacks/:id/activities body. */
export interface CallbackActivityCreateRequest {
  kind: CallbackActivityKind;
  body?: string;
  outcome?: CallAttemptOutcome;
}

/**
 * POST /v1/identity/admin/users/search response — an EXACT account-email
 * match (ADMIN only), or `{user: null}` when no account carries that email.
 */
export interface StaffUserSearchResult {
  user: { userId: string; email: string; role: UserRole; fullName?: string | null } | null;
}

/**
 * GET /v1/identity/clinician/records/:userId — a clinician's display record,
 * used to resolve a prescription's `issuedBy` ref into a prescriber name.
 */
export interface ClinicianRecord {
  userId: string;
  fullName: string;
  registrationNumber?: string | null;
  prescriberType?: PrescriberType | null;
}

/* ── Staff login/logout activity + password reset-link (users-menu slice) ────
 *
 * Appended as an interface-merge augmentation of StaffUserRow (identity now
 * returns each staff account's last login/logout) to keep this file
 * append-only — mirrors the existing dual StaffPrescription / PlanOptionsResponse
 * declarations already in this module. [CONTRACTS GAP]: these wire fields/shape
 * are not in @thegpservice/gpsclinics-contracts yet — promote once the batch lands.
 */
export interface StaffUserRow {
  /** Last successful login (true-UTC ISO); null until the account has ever logged in. */
  lastLoginAt?: string | null;
  /** Last logout (true-UTC ISO); null until the account has ever logged out. */
  lastLogoutAt?: string | null;
}

/**
 * POST /v1/identity/admin/users/:userId/reset-link (ADMIN) → the minted
 * one-time password-reset link + its expiry. Staff accounts only — the gateway
 * 400s for a patient userId. Identity ALSO emails the link; the admin surface
 * shows it for a copy fallback.
 */
export interface StaffResetLink {
  resetUrl: string;
  expiresAt: string;
}

/* ── G2 clinical governance: audit read + clinician record management ───────
 *
 * Local mirrors of the shared G2 audit contract (identical read shape in the
 * orders / identity / clinic services: GET /v1/{service}/admin/audit) and the
 * two clinician-record PATCH bodies. [CONTRACTS GAP]: not in
 * @thegpservice/gpsclinics-contracts yet — promote once the batch lands
 * (established pattern, see the v0.6.5 block above). Appended to keep this
 * module append-only.
 */

/**
 * One audit-log row (G2). `reason` lives ONLY on this row (R19: it never
 * appears in an event/log line); `actorUserId` is the verified principal —
 * the portal resolves it to an email via the batched staff-user lookup.
 */
export interface AuditEntry {
  auditId: string;
  actorUserId: string;
  actorRole: string;
  action: string;
  targetType: string;
  targetId: string;
  reason: string | null;
  createdAt: string;
}

/** GET /v1/{orders|identity|clinic}/admin/audit response — newest first. */
export interface AuditListResponse {
  items: AuditEntry[];
}

/**
 * Audit read params: `limit` 1..100 (default 50); `before` = a strict
 * createdAt cursor. The 2026-07-21 server-side filters (UX Wave 3c tracer)
 * are all combinable — they AND together in the service's query, composed
 * with the cursor: `actorUserId` (uuid), `action`/`targetType` (serialised as
 * comma-lists; every value MUST belong to the target service's own vocabulary
 * — see admin/auditVocab.ts — or the strict boundary 400s), and `from`/`to`
 * (an INCLUSIVE isoUtc createdAt range; `before` stays the strict lt cursor).
 */
export interface AuditQuery {
  limit?: number;
  before?: string;
  actorUserId?: string;
  action?: string[];
  targetType?: string[];
  from?: string;
  to?: string;
}

/**
 * PATCH /v1/identity/admin/users/:userId/clinician-record body (ADMIN, G2) —
 * every field optional; `reason` is recorded in the identity audit log.
 */
export interface ClinicianRecordUpdate {
  fullName?: string;
  registrationNumber?: string;
  prescriberType?: PrescriberType;
  reason?: string;
}

/**
 * Interface-merge augmentation (same pattern as StaffUserRow above): the
 * clinician's own profile read now carries the prescriber type (G2) so the
 * "Your profile" panel can show it read-only. OPTIONAL — the deployed API may
 * not return it yet.
 */
export interface ClinicianProfile {
  prescriberType?: PrescriberType | null;
}

/**
 * UX Wave 8 augmentation (2026-07-21) — mirrors identity's strict local
 * `clinicianSelfProfile` ([CONTRACTS GAP]): the LinkedIn-style profile fields
 * plus SHORT-LIVED (~15 min) signed READ URLs for the photo and signature
 * images (null when absent or when object storage is in stub mode — the
 * `photoSet`/`signatureSet` booleans stay the storage-independent truth).
 * All OPTIONAL: the deployed identity may not return them yet.
 */
export interface ClinicianProfile {
  designation?: string | null;
  bio?: string | null;
  photoSet?: boolean;
  photoUrl?: string | null;
  signatureUrl?: string | null;
}

/**
 * PATCH /v1/identity/clinician/me/profile body (UX Wave 8) — the self-serve
 * DISPLAY identity: names travel together (identity re-derives fullName as
 * "First Last"); `designation`/`bio` are sanitised server-side (bio ≤600 after
 * sanitisation; null clears); `photoRef` is the photo CONFIRM — sent only
 * after a 2xx signed PUT, own-prefix enforced server-side. Registration
 * number / prescriber type are NOT here — admin-gated (strict 400).
 */
export interface ClinicianProfileUpdate {
  firstName?: string;
  lastName?: string;
  designation?: string | null;
  bio?: string | null;
  photoRef?: string;
}

// ---------------------------------------------------------------------------
// Analytics (2026-07-21) — MONEY-FREE COUNT reads for the clinician dashboard
// (#21), the per-clinic call-volume heatmap (#22) and busiest-times (#24).
// Mirrors the services' local [CONTRACTS GAP] shapes. No pence field anywhere —
// that is what makes them safe to render on the CLINICIAN surface.
// ---------------------------------------------------------------------------

/** orders-service GET /v1/orders/analytics/me — the caller's own order decisions. */
export interface ClinicianOrderStats {
  windowDays: number;
  from: string;
  to: string;
  totals: { approved: number; preapproved: number; rejected: number; prescriptionsIssued: number };
  /** Zero-filled Europe/London daily trend; `approvals` folds approved + preapproved. */
  perDay: Array<{ date: string; approvals: number; rejections: number }>;
}

/** clinic-service GET /v1/clinic/analytics/me — the caller's own callback activity. */
export interface ClinicianCallbackStats {
  windowDays: number;
  from: string;
  to: string;
  totals: { callbacksHandled: number; converted: number; handovers: number };
  outcomes: { answered: number; no_answer: number; voicemail: number; busy: number; disconnected: number };
  /** Live open+in_progress queue depth (tenant-wide — the clinician queue). */
  pendingNow: number;
  perDay: Array<{ date: string; callbacks: number }>;
}

/** One (day-of-week × hour) cell of a call-volume / busiest-times heatmap. */
export interface ActivityBucket {
  /** 0 = Monday … 6 = Sunday (Europe/London). */
  dayOfWeek: number;
  /** 0..23 (Europe/London). */
  hour: number;
  count: number;
}

/** GET /v1/{clinic,orders}/analytics/activity — sparse weekday×hour buckets. */
export interface ActivityHeatmap {
  windowDays: number;
  timezone: string;
  /** Present (clinic heatmap) when narrowed to a clinic; absent on the orders heatmap. */
  clinicSlug?: string | null;
  buckets: ActivityBucket[];
}

/** clinic-service GET /v1/clinic/analytics/clinicians — per-clinician rollup (ADMIN). */
export interface ClinicianMetric {
  clinicianRef: string;
  callbacksHandled: number;
  convertedCallbacks: number;
  handovers: number;
}
export interface ClinicianMetrics {
  windowDays: number;
  from: string;
  to: string;
  clinicians: ClinicianMetric[];
}

/**
 * One row of the drop-off ranking (clinic-service `dropOffReasonCount`).
 * `reason` is typed WIDE on purpose: a reason a newer server adds must still
 * render (as its key) rather than drop out of the ranking.
 */
export interface DropOffReasonCount {
  reason: string;
  count: number;
  /** As of TODAY: of `count`, how many are still waiting to pay. */
  unpaidNow: number;
}

/**
 * GET /v1/clinic/analytics/drop-off-reasons (ADMIN, T-2 2026-09-11) — why
 * approved patients have not paid. Counts only (R19), zero-filled across the
 * vocabulary and RANKED by the server; `responses` is the sum. The window is
 * `?days=` over when a reason was first given — except `unpaidNow`, which is
 * as of today.
 */
export interface DropOffReasonCounts {
  windowDays: number;
  from: string;
  to: string;
  responses: number;
  reasons: DropOffReasonCount[];
}

/**
 * The six configurable per-clinician pence rates (2026-08-26 added dispatch +
 * repeat-Rx to the original three; 2026-08-27 added the new-patient one-off).
 */
export interface CompensationRates {
  perCallbackPence: number;
  perApprovedOrderPence: number;
  perConvertedCallbackPence: number;
  perDispatchPence: number;
  perRepeatRxPence: number;
  /**
   * Ashley's new-patient rate (2026-08-27): a ONE-OFF paid the FIRST time a
   * new patient gets a repeat prescription from their initial form — once per
   * patient ever, on top of the per-month repeat rate. Server-side the PUT
   * still accepts a three- or five-key body for one deploy window (absent =
   * leave unchanged), but this portal always sends all six.
   */
  perNewPatientRepeatPence: number;
}

/** orders-service GET /v1/orders/admin/compensation — stored per-clinician rates (ADMIN money). */
export interface ClinicianCompensationRow extends CompensationRates {
  clinicianRef: string;
  updatedAt: string;
  updatedByRef: string;
}
export interface CompensationList {
  items: ClinicianCompensationRow[];
}

/** orders-service GET /v1/orders/admin/earnings — per-clinician earnings (ADMIN money). */
export interface ClinicianEarnings {
  clinicianRef: string;
  callbacksHandled: number;
  convertedCallbacks: number;
  approvedOrders: number;
  /** Orders handed to the pharmacy in the window (× perDispatchPence). */
  dispatchedOrders: number;
  /** Repeat Rx months issued in the window — each case's FIRST issue excluded (× perRepeatRxPence). */
  repeatPrescriptions: number;
  /**
   * Patients whose FIRST-EVER repeat fell in this window (× perNewPatientRepeatPence).
   * Counted at most once per patient across all time — a patient whose first
   * repeat landed in an earlier window contributes nothing here even though
   * their later repeat months still count above.
   */
  newPatientRepeats: number;
  /**
   * THE ESTIMATE (CTO 2026-09-09): everything this clinician has recommended
   * that the patient has been sent and has NOT paid for. The approval fee is
   * earned by whoever gives the final approval before the card is charged, so
   * these earn nothing yet — but the work is done and the fee is expected
   * "until the patient requests something else".
   *
   * ⚠️ **AS OF TODAY — the one field on this response that is NOT scoped to the
   * requested period.** It does not move when the picker changes, so never
   * render it under a period heading: the tables label it "(now)".
   *
   * ⚠️ **OPTIONAL: absent means "we could not tell", never zero.** It comes from
   * clinic-service through a fail-soft port, and on `partial: true` orders-service
   * OMITS it rather than sending 0 — a zero that is only a failed HTTP call reads
   * as "nothing is outstanding" on a payroll screen. Render it through
   * `estimateText` so an absence shows "—".
   */
  plansAwaitingPayment?: number;
  rates: CompensationRates;
  earnedPence: number;
  /**
   * `plansAwaitingPayment × perApprovedOrderPence`, computed server-side.
   * BESIDE `earnedPence`, never inside it: render it as its own figure and
   * never add it to an earned total. It becomes earned by leaving this figure
   * and entering `approvedOrders` when the patient pays and is approved.
   * Carries the as-of-today caveat of the count it prices (above).
   *
   * ⚠️ Absent in TWO situations, and they must render identically: an
   * orders-service that predates the estimate, and a live clinic-service outage
   * (`partial: true`). Render through `estimateText`, never `formatPence`
   * directly, or a money screen shows £NaN.
   */
  estimatedPence?: number;
}
export interface EarningsResponse {
  windowDays: number;
  from: string;
  to: string;
  partial: boolean;
  totalPence: number;
  /**
   * Σ estimatedPence — the server's own figure, beside totalPence and never
   * added to it. Omitted when the estimate could not be read at all: a total
   * summed over unknowns is not a total.
   */
  estimatedTotalPence?: number;
  clinicians: ClinicianEarnings[];
}

/**
 * orders-service GET /v1/orders/clinician/earnings/me — the signed-in
 * clinician's OWN earnings (CTO 2026-08-26: a clinician MAY see their own
 * compensation; patient money and every OTHER clinician's figures stay
 * ADMIN-only). Same row shape as the admin table, wrapped with the window
 * metadata + the fail-soft `partial` flag. The server derives the clinician
 * from the session — the request never carries a ref (R41). Mirrors the
 * orders-service `clinicianSelfEarnings` shape (deliberately NO totalPence
 * and NO clinicians array — those stay ADMIN-only).
 */
export interface MyEarningsResponse {
  windowDays: number;
  from: string;
  to: string;
  partial: boolean;
  me: ClinicianEarnings;
}

/* ── CRM pipeline (2026-07-21) — [CONTRACTS GAP] local mirrors of clinic-service
 * `deal.schemas.ts`. The server's deal-stages map is the control (R41);
 * src/pipeline/stages.ts mirrors the vocabulary/labels/manual moves for
 * affordance only. Contact PHI rides these AUTHED staff responses (as on the
 * callback queue) — never in a client analytics event (R19). NO money fields
 * anywhere in these shapes: deal-level money is a separate ADMIN-only read. */

/** The 11-stage pipeline vocabulary, in board display order. */
export type DealStage =
  | 'mql'
  | 'callback_requested'
  /**
   * THE LEAD LANE (2026-09-07, CTO) — "unless the card is pre-authed we should
   * not bring patients to pending clinician review lane. They can be a lead."
   *
   * A completed questionnaire no longer opens the clinician's queue. The person
   * finished the form, saw their plan and may well have typed a delivery
   * address, but has NOT authorised a card — so they sit here, where an admin
   * can chase them, until orders-service reports a HELD month-1 pre-auth.
   *
   * ⚠️ IT IS NOT `mql`, AND THE PORTAL MUST NEVER RENDER IT AS IF IT WERE. "New
   * lead" is the person who typed an email and bounced; this is somebody with a
   * finished questionnaire behind them. Collapsing the two columns would hand
   * an admin a chase list they cannot tell apart from cold email captures,
   * which is the exact state this feature exists to leave behind.
   */
  | 'warm_lead'
  /**
   * THE MERGED CLINICAL LANE (2026-09-05, CTO) — "a clinician owns this
   * questionnaire, whether or not the patient has paid yet". It replaces BOTH
   * `eligibility_in_progress` (the self-serve check in flight, pre-payment) and
   * `awaiting_clinical_review` (a PAID checkout waiting on approve/reject),
   * which split one clinician's queue across two board columns.
   *
   * ⚠️ THE TWO OLD STAGES HAD OPPOSITE MANUAL RULES, and the difference did NOT
   * die with the merge — it is keyed on a row COLUMN now instead of on a stage
   * name. That column is `holdAuthorisedAt` since 2026-09-07 (it was
   * `orderCaseRef`, which the authorisation gate turned into the marker of an
   * UNPAID card). See `isAwaitingClinicalDecision` in src/pipeline/stages.ts: a
   * card in this stage with an AUTHORISED card behind it is system-only exactly
   * as `awaiting_clinical_review` was, and one without keeps the manual exits
   * `eligibility_in_progress` had.
   */
  | 'clinical_review'
  /** A clinician approved the questionnaire and sent the plan invite; the deal
   *  now waits on the PATIENT to pay (2026-08-26, CTO). Entered automatically
   *  by the server's handover hook — never a manual move in. */
  | 'plan_sent'
  | 'awaiting_clinician_followup'
  | 'awaiting_admin_followup'
  /** The clinician→admin handoff park (2026-08-07): a clinician spotted
   *  something only an ADMIN may action and handed the deal over with a note. */
  | 'pending_admin_review'
  | 'closed_won'
  /**
   * COULD NOT REACH THEM (2026-09-05, CTO — renamed from `marketing_funnel`).
   * THREE WAYS IN. 3 clinician call attempts and 3 admin call attempts all
   * went unanswered (the callbacks escalation machine parks the ticket and the
   * deal with it); the rot cron found the card stuck past its funnel window;
   * or — since 2026-09-22 — an ADMIN moved the card here by hand from
   * `plan_sent`, `awaiting_admin_followup` or `pending_admin_review`, because
   * the patient behind it does not answer the phone. The manual route exists
   * because neither automatic one reaches a `plan_sent` card in practice: the
   * rot cron has no scheduler job in any environment, and the park needs call
   * attempts logged against a ticket.
   *
   * ⚠️ IT IS NOT A MARKETING LIST, AND THE OLD NAME NEARLY COST US THE LANE.
   * `marketing_funnel` read like an opt-in mailing list and the CTO came close
   * to deleting the stage on the strength of it — which would have stranded
   * every patient we could not get hold of. The single nurture email the rot
   * cron sends on the way in is a side-effect of the lane, not its meaning.
   * Anything that labels, groups or counts this stage must say "unreachable",
   * never "marketing".
   */
  | 'unreachable'
  | 'closed_lost'
  /**
   * MERGED AWAY (2026-09-06, CTO — the Asana model: a task marked as a
   * duplicate is CLOSED, not left open and not completed). The patient on this
   * card converted, or did not, on the OTHER card; this journey is a second
   * copy of theirs.
   *
   * ⚠️ IT IS NOT `closed_lost`, AND THE DIFFERENCE IS A REPORTING ONE. Filing a
   * merged card under Lost books a customer as a loss, and "where are we losing
   * patients" is exactly the question the Lost lane exists to answer — so the
   * one board number that would be wrong is the one somebody acts on. The
   * server made it a distinct terminal value for that reason; this mirror
   * follows, and the funnel's Closed group names it explicitly.
   */
  | 'closed_duplicate';

/** How the person entered the pipeline — the card's provenance tag. */
export type DealEntryPath = 'widget_form' | 'callback_button' | 'portal_journey' | 'staff_sql';

/** The contact block riding on cards + the deal aggregate. */
export interface DealContact {
  contactId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
}

/** One pipeline card. daysInStage/rotting are computed server-side (env-tunable). */
export interface DealCard {
  dealId: string;
  clinicSlug: string;
  contact: DealContact;
  stage: DealStage;
  entryPath: DealEntryPath;
  /**
   * What they said they came to do, from the qualification screen (the wire name
   * stays `intent` — see the note on `LeadCaptureRequest.intent`).
   *
   * THREE-VALUED, and the distinction matters on the rail: `undefined` means
   * this build of clinic-service does not send it, so we DID NOT LOOK; `null`
   * means we asked and they skipped, or the card predates the question. Only a
   * key is an answer. Same discipline as `StaffOrder.awaitingPrescription`.
   */
  intent?: LeadIntentWire | null;
  stageEnteredAt: string;
  daysInStage: number;
  rotting: boolean;
  scheduledCallbackAt: string | null;
  leadId: string | null;
  assessmentId: string | null;
  callbackId: string | null;
  orderCaseRef: string | null;
  /**
   * ⚠️ THE PADLOCK KEY (2026-09-07) — when this card was last proved to have an
   * AUTHORISED card behind it (ISO), or null.
   *
   * NOT `orderCaseRef`, and the difference is the whole of the authorisation
   * gate: that field says the patient reached the card FORM, this one says a
   * `month1_preauth` was HELD. The mirror in stages.ts keys on THIS, because a
   * mirror asking a different question from the server's 409 greys out actions
   * the server would happily accept — which is exactly what it did to a warm
   * lead pulled into review by hand.
   */
  holdAuthorisedAt: string | null;
  assessmentState: string | null;
  eligibility: string | null;
  createdAt: string;
}

export interface DealList {
  items: DealCard[];
}

/** GET /v1/clinic/deals/counts — zero-filled per-stage badges. */
export type DealCounts = Record<DealStage, number>;

/**
 * One lane's money, from `GET /v1/clinic/deals/lane-money` (ADMIN).
 *
 * ⚠️ EVERY PENCE FIGURE IS NULLABLE AND NULL IS NOT ZERO. Null means "this lane
 * does not answer that question" — a realised figure on a lane with no order
 * behind it is a fact nobody has, and a forfeited-revenue figure on the one
 * lane that is a SALE would book a loss against a win. Zero means "nothing".
 * Render the two differently or the panel lies.
 */
export interface DealLaneMoney {
  /** Approved cards counted into this lane — the denominator that makes a £0 lane legible. */
  approvals: number;
  /** `closed_won` only: ONE MONTH of the current fee, over plans money is still taken on. */
  realisedMonthlyPence: number | null;
  /** Every lane but `closed_won`: the first month expected from the patient. */
  firstMonthPence: number | null;
  /** The same expectation carried to `assumedTermMonths`. */
  threeMonthPence: number | null;
  /**
   * `closed_lost` only: the clinician fees we owe for these approvals anyway.
   * ⚠️ NEVER ADD THIS TO THE FORFEITED FIGURES — cash that LEFT against revenue
   * that never ARRIVED. One "total loss" made of both double-counts the deal.
   */
  clinicianFeesPence: number | null;
  /** Approvals no plan price covers; they contributed nothing to the figures above. */
  unpricedApprovals: number;
}

/**
 * GET /v1/clinic/deals/lane-money — per-lane money behind the APPROVED cards.
 * **ADMIN only**: the route is `@Roles('ADMIN')` server-side, so a CLINICIAN
 * gets a 403 rather than a redacted payload.
 */
export interface DealLaneMoneyResponse {
  /** Zero-filled for every stage, like `DealCounts` — a lane is never `undefined`. */
  lanes: Record<DealStage, DealLaneMoney>;
  /**
   * The term behind `threeMonthPence`, ON THE WIRE so no label hardcodes a 3.
   * Outside `closed_won` there is no order and therefore no term: this is an
   * assumption the CTO accepted, and the tile has to say so.
   */
  assumedTermMonths: number;
  /**
   * Which downstream money sources answered. `unavailable` is NOT a silent
   * zero — render the admission instead of the figure, because an empty price
   * matrix and a dead prices-service produce exactly the same total.
   */
  sources: {
    prices: 'ok' | 'unavailable';
    orders: 'ok' | 'unavailable';
    rates: 'ok' | 'unavailable';
  };
}

/** One entry of the deal's MERGED activity feed (chronological). */
export interface DealActivityEntry {
  at: string;
  /**
   * `clinical_review` / `system_email` / `journey` joined 2026-08-26 with the
   * clinic sibling's journey-feed batch (feat/journey-feed): clinical decision
   * events, system-sent emails, and SYNTHETIC journey milestones the server
   * derives from the linked lead/assessment/callback timestamps. Widened IN
   * PLACE (a union member cannot ride an interface merge); an unknown kind
   * still degrades to its raw token via the KIND_LABEL Record idiom.
   */
  kind:
    | 'stage_change'
    | 'handover'
    | 'call_attempt'
    | 'note'
    | 'email'
    | 'audit'
    | 'clinical_review'
    | 'system_email'
    | 'journey';
  actorUserId: string | null;
  summary: string;
  outcome: string | null;
  /** Rich-text note body — sanitise AGAIN client-side before rendering (lib/sanitizeHtml). */
  body: string | null;
}

/** A sibling deal of the same contact ("previously submitted forms"). */
export interface DealSibling {
  dealId: string;
  stage: DealStage;
  entryPath: DealEntryPath;
  assessmentId: string | null;
  createdAt: string;
}

/**
 * How an earlier attempt ENDED — a coarse enum the server derives; the portal
 * only ever labels it. Widen this union server-first: an outcome we do not know
 * must fall back to neutral wording rather than a guess (see ATTEMPT_LABEL).
 */
export type DealAttemptOutcome =
  | 'in_progress'
  | 'superseded'
  | 'not_proceeding'
  | 'closed'
  | 'parked'
  | 'not_eligible'
  | 'eligible'
  | 'submitted'
  | 'abandoned';

/**
 * One EARLIER attempt by the same contact — a callback ticket or an eligibility
 * check this card no longer points at. A repeat journey re-points the ONE deal
 * instead of minting a second card, so this is where the board remembers that
 * the person tried before, and how it went.
 */
export interface DealPriorAttempt {
  kind: 'callback' | 'assessment';
  ref: string;
  startedAt: string;
  endedAt: string | null;
  outcome: DealAttemptOutcome;
}

/** GET /v1/clinic/deals/:id — the drawer aggregate. */
export interface DealDetail {
  deal: DealCard;
  callback: {
    callbackId: string;
    status: string;
    phone: string;
    preferredTime: string | null;
    attempts: number;
    attemptsThisRound: number;
    /**
     * When the ticket was raised (clinic journey-feed batch, 2026-08-26 —
     * feeds the synthetic journey entries). OPTIONAL, edited in place: a
     * nested object type cannot ride the module's interface-merge idiom, and
     * an older server simply omits it.
     */
    createdAt?: string;
  } | null;
  assessment: {
    assessmentId: string;
    state: string;
    eligibility: string | null;
    origin: string;
    submittedAt: string | null;
    createdAt: string;
    /**
     * When the patient ticked the consent declarations, and which question-set
     * version they agreed to (clinic journey-feed batch, 2026-08-26). BOTH
     * OPTIONAL and nullable, edited in place (nested object — no interface
     * merge possible): an older clinic-service omits them, and a pre-consent
     * draft has nothing to report. The Review tab renders its "Agreed by the
     * patient" line ONLY when `consentAgreedAt` is present — never a guess.
     */
    consentAgreedAt?: string | null;
    consentQuestionSetVersion?: number | null;
    /**
     * An earlier check for this journey ended not-eligible and this one resumes
     * it. Feeds the Review-tab marker, so a clinician who never opens the tab
     * still knows; the detail lives on the review's `priorStops`. OPTIONAL — a
     * server predating the feature omits it, and absent means "no".
     */
    supersedes?: boolean;
    /**
     * Every stored weight photo as a signed READ url (clinician photo review,
     * 2026-08-27). OPTIONAL and edited IN PLACE — a nested object type cannot
     * ride this module's interface-merge idiom, and a clinic-service that
     * predates the gallery omits the key. Treat absent as "unknown", NEVER as
     * "the patient uploaded nothing": the chase warning below keys on it, and
     * an old server must not manufacture a follow-up prompt.
     */
    photos?: AssessmentPhotoEntry[];
    /**
     * How many REAL photo-request chases this journey has received. Surfaced
     * to ADMIN on the drawer Overview so a human decides what to do about a
     * patient who never uploads — CTO 2026-08-27: surface it, NEVER auto-close.
     * OPTIONAL for the same deploy-window reason; absent means "not reported".
     */
    photoChaseCount?: number;
    /**
     * WHEN THE PLAN LINK WAS LAST EMAILED to this patient — the newest of the
     * assessment's send rows (`assessment_handover_sent` for the first send,
     * `assessment_plan_link_resent` for every chase after it). A COPY does not
     * move it: nothing was sent, and the patient may never see a copied link.
     *
     * OPTIONAL AND NULLABLE, and the two mean DIFFERENT things — the drawer
     * must not collapse them:
     *   ABSENT  a clinic-service that predates the field (the two services
     *           deploy independently).
     *   NULL    the server looked and found no send row. That is "we have NO
     *           RECORD of sending it", NOT "it was never sent": every re-send
     *           made before 2026-09-05 exists only as the older action, and a
     *           journey older than the audit trail has nothing at all.
     * Both render the honest placeholder rather than a date. A confident wrong
     * timestamp on a clinical record is worse than a blank, which is why the
     * placeholder was written before this field existed (PlanLinkActions).
     *
     * Edited IN PLACE, not merged: a nested object type cannot ride this
     * module's interface-merge idiom (same reason as `consentAgreedAt`).
     */
    planLinkLastSentAt?: string | null;
  } | null;
  activity: DealActivityEntry[];
  /**
   * OPTIONAL here on purpose, even though clinic-service always sends it: the
   * two services deploy independently, so the drawer has to survive the window
   * where the portal is ahead of the API rather than crash the History tab.
   */
  priorAttempts?: DealPriorAttempt[];
  otherDeals: DealSibling[];
}

/** POST /v1/clinic/deals — the clinician "Create New" (SQL lane). */
export interface DealCreateRequest {
  clinicSlug: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
}

/** POST /v1/clinic/deals response — contact + deal only (the form creates lead/assessment). */
export interface DealCreated {
  dealId: string;
  contactId: string;
}

/* ── Deal-scoped activity logging (2026-07-21 UX pass) — [CONTRACTS GAP] local
 * mirror of clinic-service POST /v1/clinic/deals/:id/activities (contract
 * PINNED with the parallel backend batch): unlike the ticket composer's
 * optional note, `body` is REQUIRED (1..2000 chars) for EVERY kind, and
 * `outcome` is REQUIRED iff kind='call_attempt' (refused elsewhere). Appended
 * to keep this module append-only. */

/** POST /v1/clinic/deals/:id/activities body (strict). */
export interface DealActivityCreateRequest {
  kind: CallbackActivityKind;
  /** REQUIRED, 1..2000 — the server rejects an empty body. */
  body: string;
  /** call_attempt only — required there, refused on note/email. */
  outcome?: CallAttemptOutcome;
}

/** POST /v1/clinic/deals/:id/activities response (201). */
export interface DealActivityCreated {
  activityId: string;
}

/** GET /v1/clinic/assessments/:id/answers — latest raw values keyed by questionKey. */
export interface AssessmentAnswers {
  assessmentId: string;
  answers: Record<string, unknown>;
  /**
   * [CONTRACTS GAP] The yes_no_text free-text detail per questionKey —
   * optional/additive so an older read without it still type-checks. Without
   * it a resumed "yes" renders an empty Details box and a later save would
   * overwrite the stored clinical text.
   */
  details?: Record<string, string>;
}

/* ── UX Wave 4 (2026-07-21): authorise→recommend, editor attachments,
 * notes-per-answer ────────────────────────────────────────────────────────
 *
 * [CONTRACTS GAP] local mirrors of the clinic-service Wave-4 batch:
 * assessment recommendation sets (upsert-latest, CLINICIAN write), the
 * deal-activity attachment upload grant + feed attachments, and the
 * per-question `questionKey` anchor on assessment notes. Appended (with the
 * established interface-merge pattern — see StaffUserRow above) to keep this
 * module append-only; promote once the batch lands in
 * @thegpservice/gpsclinics-contracts.
 */

/**
 * The clinician's persisted medication recommendation set for an assessment
 * (1..4 productKeys, upsert-latest — a re-issue REPLACES the stored set).
 * Money-free by construction: keys only, never a price — safe to render on
 * the clinician surface.
 */
export interface AssessmentRecommendation {
  productKeys: string[];
  /**
   * The stored row's id. Optional so an older read still parses — but when the
   * SAVE returns one, the approval that follows carries it back as an
   * optimistic-concurrency token, so a set another clinician replaced in the gap
   * cannot be the one that gets charged for and dispatched (2026-09-07).
   */
  recommendationId?: string;
  /** Additive server stamps — optional so an older read still parses. */
  recommendedBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** GET /v1/clinic/assessments/:id/recommendations response — null until one is issued. */
export interface AssessmentRecommendationRead {
  recommendation: AssessmentRecommendation | null;
}

/**
 * GET /v1/clinic/assessments/:id/patient-recommendations — PATIENT-safe Route 2
 * filter. Opaque productKeys only (never recommendedBy / recommendationId).
 */
export interface PatientAssessmentRecommendation {
  productKeys: string[];
}

export interface PatientAssessmentRecommendationRead {
  recommendation: PatientAssessmentRecommendation | null;
}

/** The four content types the deal-activity attachment grant allows. */
export type DealAttachmentContentType = 'image/jpeg' | 'image/png' | 'image/webp' | 'application/pdf';

/**
 * One attachment riding a deal-activity feed entry: the stored object ref and
 * a SIGNED, short-lived read URL — `null` in stub-mode environments (file on
 * record, no preview link; same rule as the review's photoUrl).
 */
export interface DealActivityAttachment {
  ref: string;
  url: string | null;
}

/** Interface merge: the activity POST optionally carries completed upload refs (max 5). */
export interface DealActivityCreateRequest {
  attachmentRefs?: string[];
}

/** Interface merge: feed entries logged with attachments carry them (additive). */
export interface DealActivityEntry {
  attachments?: DealActivityAttachment[];
}

/**
 * Interface merge: an assessment note may be ANCHORED to one questionnaire
 * answer via its validated questionKey (Wave 4 notes-per-answer); null/absent
 * = a general clinical note.
 */
export interface CaseNote {
  questionKey?: string | null;
}

/* ── UX Wave 6 §5: the PMS patient directory + patient context ──────────────
 *
 * Local mirrors of the two Wave-6 §5 backend reads ([CONTRACTS GAP] — promote
 * once the batch lands, established pattern above):
 *  - identity GET /v1/admin/patients (`adminPatientRow`/`adminPatientListResponse`
 *    in identity-service src/admin/admin.service.ts) — the paginated,
 *    searchable, newest-first PATIENT directory. ADMIN **and** CLINICIAN get
 *    the IDENTICAL money-free shape (identity fields only — no money lives in
 *    that service).
 *  - orders GET /v1/orders/admin/patients/:patientUserId/context
 *    (`patientContext` in orders-service src/orders/dto.ts) — the per-patient
 *    money aggregate. ADMIN-ONLY: this shape carries pence and must never be
 *    fetched from a clinician surface (the money-redaction sweep asserts the
 *    request is never even made).
 * Appended to keep this module append-only (sibling agents edit it in
 * parallel worktrees).
 */

/** One PMS directory row — identity fields ONLY (money-free by construction). */
export interface AdminPatientRow {
  userId: string;
  email: string;
  /** Null for self-registered patients who never set a name. */
  fullName: string | null;
  /**
   * Whole years, computed SERVER-side (identity #82). An INTEGER and never the
   * date of birth: DOB appears nowhere else on a staff surface, so putting a
   * full date in a directory list would be new exposure of a stronger
   * identifier for no added purpose. Null when no DOB is on record.
   */
  age: number | null;
  emailVerified: boolean;
  createdAt: string;
  /** Null until the account's first login since the column landed. */
  lastLoginAt: string | null;
  /**
   * ADMIN-flagged QA/rehearsal account (identity #86, 2026-09-04).
   *
   * REQUIRED, not optional, and that is the honest shape: identity's row schema
   * is `.strict()` with `isTest: z.boolean()`, so every row on this wire carries
   * it. Marking it optional here would invite `patient.isTest ? …` to read
   * "absent means real" — which is true, but it would also hide the day the
   * field genuinely went missing behind a silent "real record" render.
   *
   * On the DEFAULT directory read this is uniformly `false`: the exclusion is a
   * WHERE term on the server, applied BEFORE `take`, so a flagged row cannot
   * reach the page at all. It is only ever `true` inside the opt-in
   * `?includeTest=true` view — which is exactly where the chip has to appear.
   *
   * BOTH staff roles receive it; only the WRITE is ADMIN-only.
   */
  isTest: boolean;
}

/**
 * One row of the money-free treatment-status batch read (orders #186).
 *
 * ⚠️ `planState` IS ABSENT — NOT NULL — FOR A CLINICIAN, and that is enforced by
 * the SERVER, not here. orders-service builds a `.strict()` clinician shape that
 * has no such key, decided from the gateway-verified role, so a clinician
 * response cannot carry it even if this type or the rendering changed. The
 * optionality below MIRRORS that; it is not the control.
 */
export interface PatientTreatmentStatus {
  userId: string;
  /** Holds medication now — a supply question, not a billing one. */
  inTreatment: boolean;
  /** ADMIN only. Null = never had a plan (or an unrecognised stored state). */
  planState?: 'dormant' | 'active' | 'past_due' | 'cancelled' | 'completed' | null;
}

export interface PatientTreatmentStatusResponse {
  items: PatientTreatmentStatus[];
}

/** GET /v1/identity/admin/patients response — newest first by createdAt. */
export interface AdminPatientListResponse {
  items: AdminPatientRow[];
}

/**
 * GET /v1/identity/admin/patients query: `limit` 1..100 (default 25),
 * `before` = a strictly-older createdAt cursor (same convention as the audit
 * reads), `q` = case-insensitive email/name contains-search (1..320 — OMIT
 * when the search box is blank; an empty `q` is a 400 at the strict boundary).
 */
export interface AdminPatientListQuery {
  limit?: number;
  before?: string;
  q?: string;
  /**
   * The test-record audit view (identity #86, 2026-09-04).
   *
   * ⚠️ A STRING LITERAL UNION, NOT A BOOLEAN, AND THAT IS THE WHOLE POINT.
   * identity's query schema is `z.enum(['true','false'])` rather than
   * `z.coerce.boolean()` because query params arrive as STRINGS and JS
   * `Boolean("false")` is `true` — so a coerced `?includeTest=false` would have
   * silently OPENED the audit view. Typing the portal side as the same two
   * literals means a `boolean` here fails to compile instead of stringifying
   * into whatever `String(false)` happens to give us; anything the server does
   * not recognise is a boundary 400, which would take the whole directory read
   * down rather than degrade.
   *
   * OMITTED for the working directory. `'false'` behaves exactly like omitting
   * it, so the portal never sends it — one wire shape for "the normal read", so
   * a diff of the requests can only show the deliberate opt-in.
   */
  includeTest?: 'true' | 'false';
}

/**
 * POST /v1/identity/admin/patients/:userId/mark-test — the ADMIN-only patient
 * test-record flag (identity #86, 2026-09-04). `[CONTRACTS GAP]` — a local
 * mirror of identity's strict `adminPatientMarkTestRequest`.
 *
 * `revert: true` on the SAME endpoint is the undo. `reason` is free text that
 * lives ONLY on the server-side audit row and the authed ADMIN audit read (R19
 * — never logged, never evented), which is why it is optional here: the mark is
 * accepted without one.
 */
export interface AdminPatientMarkTestRequest {
  reason?: string;
  revert?: boolean;
}

/** The mark's answer: the subject and its RESULTING flag state (both directions). */
export interface AdminPatientMarkTestResult {
  userId: string;
  isTest: boolean;
}

/** contracts PaymentKind — what a ledger row was FOR (mirrored for display labels). */
export type PatientPaymentKind = 'month1_preauth' | 'callback_49' | 'renewal';

/** One order case inside the patient context (plan fields null on a Lane-B callback order). */
export interface PatientContextOrder {
  orderCaseId: string;
  lane: string;
  state: string;
  productKey: string | null;
  months: PlanMonths | null;
  monthlyPence: number | null;
  createdAt: string;
  assessmentRef: string | null;
  /** G3 — how many clinician case-notes the order carries (never the bodies). */
  noteCount: number;
}

/** One subscription inside the patient context. */
export interface PatientContextSubscription {
  subscriptionId: string;
  orderCaseId: string;
  productKey: string;
  months: PlanMonths;
  monthlyPence: number;
  state: SubscriptionState;
  monthsRemaining: number;
  nextPaymentDate: string | null;
  createdAt: string;
}

/** One payment-ledger row inside the patient context (carries `kind`, unlike StaffPaymentRecord). */
export interface PatientContextPayment {
  paymentRecordId: string;
  orderCaseId: string;
  kind: PatientPaymentKind;
  phase: StaffPaymentPhase;
  amountPence: number;
  occurredAt: string;
}

/** One issued prescription inside the patient context. */
export interface PatientContextPrescription {
  prescriptionId: string;
  orderCaseId: string;
  repeatFlag: boolean;
  issuedAt: string;
  issuedBy: string | null;
  items: PrescribedItem[] | null;
}

/**
 * GET /v1/orders/admin/patients/:patientUserId/context — everything the
 * orders service holds for one patient: cases, subscriptions, the full
 * payment ledger, prescriptions and the money totals. Empty arrays (never a
 * 404) for a patient with no cases. ADMIN money posture throughout.
 */
export interface PatientOrdersContext {
  patientUserId: string;
  orders: PatientContextOrder[];
  subscriptions: PatientContextSubscription[];
  payments: PatientContextPayment[];
  prescriptions: PatientContextPrescription[];
  /**
   * The ADMIN deep link into the Stripe dashboard for this patient (orders #214,
   * 2026-09-08) — `https://dashboard.stripe.com/customers/cus_…`, or the
   * `/test/customers/…` variant in test mode.
   *
   * ⚠️ A FULLY-FORMED URL, NOT A CUSTOMER ID, AND THAT IS THE POINT. This estate
   * runs BOTH Stripe modes and the dashboard puts test mode behind a `/test/`
   * path segment, so "which dashboard does this customer live in?" is a
   * server-side decision taken from deployment configuration (R41). Render it
   * VERBATIM. A console that rebuilt the path would be a second, drifting copy of
   * that rule — and a wrong-mode link does not fail visibly: Stripe signs the
   * admin in and shows "no such customer" for a patient sitting in front of them.
   *
   * ⚠️ `null` IS THE COMMON ANSWER AND IT MEANS RENDER NOTHING. It is null when
   * the patient never reached checkout — most of the pipeline, because an
   * assessment goes to clinical review BEFORE the card form — and also when no
   * dashboard mode is configured, when Stripe is stubbed, or for a non-ADMIN
   * principal. Do NOT render a disabled control or an em-dash beside a "Stripe"
   * label for null: both read as "we lost it", when the truth is that there is
   * nothing to point at.
   *
   * REQUIRED, not optional: orders' response schema is `.strict()` and always
   * carries the key. Marking it optional here would invite `context
   * .stripeCustomerUrl ? …` to read "absent means no customer" — true today, and
   * exactly what would hide the day the field genuinely went missing.
   *
   * R19: the `cus_…` inside is opaque — no card data, no PAN, no last4, no
   * cardholder name, no `pm_…`.
   */
  stripeCustomerUrl: string | null;
  totals: {
    orderCount: number;
    transactionCount: number;
    /** Sum of phase='captured' amounts ONLY — holds/releases/refunds never count. */
    chargedPence: number;
  };
}

/* ── §9 three-attempt call workflow (UX Wave 7B, 2026-07-21) ────────────────
 *
 * [CONTRACTS GAP] local mirrors of the clinic-service additions (deal.schemas.ts
 * on feat/call-attempts): DERIVED per-ownership-round call-attempt stats on
 * every deal card, and the server-counted attempt number echoed by the
 * activity create. OPTIONAL on the read side (the established additive-field
 * posture — an older server without them still parses); the UI hides the
 * attempt chrome when the fields are absent. Appended as interface merges to
 * keep this module append-only (sibling agents edit it in parallel worktrees).
 */

/** Interface merge: §9 derived round stats on every card (see clinic-service attemptStats). */
export interface DealCard {
  /** Call attempts logged in the ACTIVE ownership round (both ledgers; resets on handover). */
  attemptsThisRound?: number;
  /** Every call attempt ever logged on the deal (never resets). */
  attemptsLifetime?: number;
  /** Whose round it is — null when no call round is active (mql/terminal stages). */
  roundOwner?: 'clinician' | 'admin' | null;
}

/** Interface merge: the activity create echoes the SERVER-counted attempt number (null for note/email). */
export interface DealActivityCreated {
  attempt?: number | null;
}

/* ── UX Wave 10 (2026-07-22): the PATIENT self-serve "My forms" reads ─────────
 *
 * [CONTRACTS GAP] The patient portal's own view of the questionnaires THEY
 * submitted — a PATIENT self-scoped mirror of the clinician assessment reads
 * (R41: the gateway binds the caller to Assessment.patientUserId; the portal
 * only ever asks for `/mine`). No money rides any of these shapes. Appended as
 * their own interfaces (not merges) to keep this module append-only for the
 * sibling agents editing it in parallel worktrees.
 */

/**
 * One row in the patient's list of submitted forms — GET
 * /v1/clinic/assessments/mine. Summary only (no answers): enough to list and
 * to open the detail. `submittedAt` is the true-UTC submit timestamp.
 */
export interface PatientAssessmentSummary {
  assessmentId: string;
  clinicSlug: string;
  state: AssessmentState;
  eligibility: Eligibility;
  submittedAt: string;
  questionSetVersion: number;
}

/**
 * The patient's read of ONE of their own submitted forms — GET
 * /v1/clinic/assessments/mine/detail?assessmentId=. Reuses the clinician projection's
 * {@link ReviewSection} answer shape (the same server projection, self-scoped),
 * so the portal renders a read-only summary and never raw versioned JSON. NO
 * money field exists on this shape.
 */
export interface PatientAssessmentDetail {
  assessmentId: string;
  clinicSlug: string;
  state: AssessmentState;
  eligibility: Eligibility;
  submittedAt: string;
  questionSetVersion: number;
  sections: ReviewSection[];
}

/**
 * A clinician note the PATIENT is allowed to see against their own form — GET
 * /v1/clinic/assessments/mine/notes?assessmentId=. A strict, minimal subset of the
 * clinician {@link CaseNote} (never the author/edit metadata): the visible
 * body, its optional `questionKey` anchor (renders the note beside the matching
 * answer; null = a general note), and when it was written. `body` is
 * server-sanitised rich text; the portal applies the same defence-in-depth
 * strip (lib/sanitizeHtml) before rendering it as HTML.
 */
export interface PatientVisibleNote {
  noteId: string;
  questionKey: string | null;
  body: string;
  createdAt: string;
}

/* ── Pharmacy dispensing lane (contracts v0.8.0, 2026-07-29) ────────────────
 *
 * Local mirrors of the `pharmacy*` contract shapes, same rule as every other
 * block in this file: field names ARE the wire contract.
 *
 * TWO invariants shape every type below, and both are structural rather than
 * conventional — the guarantee is the ABSENCE of a field, not a redaction some
 * future component has to remember:
 *
 *  1. MONEY-FREE. Not one shape here carries a price, an amount or a pence
 *     value. A pharmacist must never see what a patient paid; margin reporting
 *     is an ADMIN surface. Because there is no field, there is nothing to leak.
 *  2. ATTRIBUTABLE. Every write identifies the INDIVIDUAL pharmacist, never
 *     just the organisation — GPhC expects a dispensing record naming a
 *     registered professional, and "Pharmacy Express" is not a person.
 */

/**
 * The order-case lifecycle INCLUDING the fulfilment tail (contracts
 * `orderCaseState`). Used by the pharmacy shapes only — the wider staff shapes
 * keep `state: string` deliberately, because narrowing them would be
 * wire-breaking for consumers pinned to an older contracts tag.
 *
 * `dispense_rejected` is deliberately NOT a reuse of `rejected`: `rejected`
 * means a CLINICIAN refused the case before a prescription existed. Collapsing
 * the two would leave the admin console unable to tell a clinical refusal from
 * a pharmacy one.
 */
export type OrderCaseState =
  | 'open'
  | 'awaiting_payment'
  | 'plan_selected'
  | 'held'
  | 'awaiting_approval'
  | 'preapproved'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'dormant'
  | 'past_due'
  | 'awaiting_dispense'
  | 'dispensed'
  | 'dispatched'
  | 'dispense_rejected';

/**
 * Pharmacy sign-in — BOTH factors in ONE request (contracts
 * `pharmacyAuthRequest`).
 *
 * The dispensing notification lands in a SHARED mailbox, so the emailed link
 * proves possession of that mailbox (the organisation) and nothing more; the
 * per-person code is what identifies the human. Carrying both in a single body
 * means there is no half-completed shape a caller can use to establish a
 * session on the link alone.
 */
export interface PharmacyAuthRequest {
  /** Single-use, short-lived. Read from `location.hash` — NEVER a query string. */
  linkToken: string;
  /** The pharmacist's own pre-issued 12-character code. Server-minted, never chosen. */
  code: string;
}

/**
 * The established pharmacy session (contracts `pharmacyAuthResult`).
 * Deliberately NOT {@link AuthSession}: that shape carries `emailVerified`
 * (meaningless for an account with no password lane) and a wide `role`, which
 * would type-permit treating a pharmacy sign-in as a patient one. Neither the
 * link token nor the code is ever echoed back.
 */
export interface PharmacyAuthResult {
  sessionToken: string;
  role: 'PHARMACY';
  /** The INDIVIDUAL pharmacist — what every dispense is attributed to. */
  pharmacistUserId: string;
  /** Scope: this session may only ever reach this organisation's orders. */
  pharmacyOrgId: string;
  expiresAt: string;
  /**
   * The named professional, for the console's account menu.
   *
   * [CONTRACTS GAP] `pharmacyAuthResult` is `.strict()` and does NOT carry this,
   * so identity cannot send it until contracts adds it. Optional here so the
   * menu falls back to a neutral role label rather than an empty pill, and so
   * the console lights up with no portal change the day it lands.
   * TODO(contracts): add `fullName` to `pharmacyAuthResult` — it already exists
   * on `pharmacistProfile`, which has no endpoint of its own.
   */
  fullName?: string | null;
}

/** The dispensing queue's tabs, mirroring the flow the pharmacy already works to. */
export type PharmacyQueueTab = 'pending' | 'dispensed' | 'history';


/**
 * The TEST-RECORD marker on a pharmacy row or detail (orders-service, 2026-09-08).
 *
 * PRESENCE IS THE FLAG — `null` means an ordinary order. There is deliberately
 * no `isTest: false` variant: a marker that says "not a test" is a thing a
 * renderer can forget to check, whereas a nullable object has to be unwrapped
 * before anything can be drawn from it.
 *
 * `reason` is the admin's own words for why the case is a rehearsal, shown to
 * the pharmacist VERBATIM — they are deciding whether an order in their queue
 * is real, and a paraphrase is a different claim. `null` means no reason was
 * ever recorded (the mark's note has always been optional, and the orders
 * flagged before the column existed have none).
 *
 * [CONTRACTS GAP] `pharmacyOrderSummary`/`pharmacyOrderDetail` are strict in
 * contracts v0.9.0, so orders-service serves this as a local extension until it
 * is promoted. Mirrored here, and OPTIONAL on both shapes, so the console keeps
 * rendering against a server that has not shipped it yet.
 */
export interface PharmacyTestRecord {
  reason: string | null;
}

/** One queue row. `clinicName` is carried explicitly so a second clinic needs
 * no new screen — the queue is ONE list, tagged by origin. No money field. */
export interface PharmacyOrderSummary {
  orderCaseId: string;
  /** Human reference shown to the pharmacist and in the email subject. */
  orderRef: string;
  clinicName: string;
  patientName: string;
  /** Display label only — the dispensable item, not a price. */
  itemLabel: string;
  coldChain: boolean;
  state: OrderCaseState;
  receivedAt: string;
  /** Non-null only when the row is a staff-marked test record — see PharmacyTestRecord. */
  testRecord?: PharmacyTestRecord | null;
}

export interface PharmacyQueueResponse {
  tab: PharmacyQueueTab;
  items: PharmacyOrderSummary[];
  /** History is bounded and paged — an unbounded list is a bulk PHI export. */
  nextCursor?: string | null;
}

/**
 * One dispensable line (contracts `pharmacyPrescriptionItem`). Quantity,
 * strength and directions — never money.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE SAFETY INVARIANT THIS SHAPE EXISTS TO CARRY:
 *   NO SURFACE MAY EVER RENDER A MEDICATION STRENGTH THAT A PRESCRIBER DID NOT
 *   SUPPLY.
 *
 * So the fields split in two, and the split is the whole point of the shape:
 *  - PRODUCT FACTS — `name`, `form`, `formulation`. These describe the product
 *    and may legitimately be resolved from the medication catalogue.
 *  - PRESCRIBER-ONLY — `strength`, `directions`. These are a clinical decision.
 *    They carry what the prescriber chose or they carry `null`. They are never
 *    catalogue-derived, never inferred, never parsed out of a product label.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * WHAT CHANGED, AND WHY. Two breaking changes, both deliberate:
 *
 *  1. `strength` is NEW, and required-but-nullable. It was absent before, which
 *     is precisely why this portal used to MANUFACTURE one: `OrderDetailPanel`
 *     regex-scraped a trailing quantity off `name` and printed the capture in a
 *     column headed "Strength", with nothing to say it had been inferred from a
 *     display string. That regex matched the orders-side PRICING-ANCHOR format
 *     exactly — the dearest variant each family is priced against — so one
 *     catalogue rename would have promoted a commercial figure into a dispensing
 *     console as if it were a prescribed dose, several-fold above the dose these
 *     treatments start at. The field now exists so the wire can say "nobody
 *     recorded one" instead of the client guessing.
 *
 *  2. `directions` is now NULLABLE. It used to be a required non-empty string,
 *     which forced the producer to fake the literal text "Not recorded" into a
 *     data field — a sentinel no consumer could tell apart from directions a
 *     clinician actually typed. Absence is now expressible, and honest.
 *
 * THE STALE COMMENT THAT USED TO SIT ON `name` — "the exact strength the
 * CLINICIAN selected, not the patient's family choice" — was wrong on both
 * counts and has been deleted. No clinician surface offered a strength control
 * when it was written, so no such selection had ever happened; and `name` is a
 * name, not a strength. That sentence was the stated justification for the
 * client-side scrape, which is how a false comment became a live patient-safety
 * defect. (The prescribe form now DOES offer the choice — see
 * `clinicians/VariantPicker.tsx` — which is what makes this field real.)
 *
 * RENDERING RULE FOR THE TWO NULLABLE CLINICAL FIELDS: `null` renders
 * "Not recorded" AND is visibly flagged — never a blank cell, never a grey em
 * dash, never a placeholder dressed as fact. A pharmacist must be able to tell
 * "no strength was recorded" from a recorded one at a glance.
 */
export interface PharmacyPrescriptionItem {
  /**
   * The product name THE WIRE STATES. ⚠️ NOT, on its own, a name anybody chose —
   * see `lib/prescribedLine#resolvePharmacyLine` for the rule that decides.
   *
   * ⚠️ NON-NULL, AND THE `| null` THAT WAS HERE UNTIL 2026-09-13 WAS A FICTION.
   * `pharmacyPrescriptionItem.name` is `z.string().min(1)` inside a `.strict()`
   * object in BOTH contracts versions in production — v0.9.0, which orders `dev`
   * pins, and v0.14.0, which the prescribing branch pins. No orders build can
   * send `null` here and neither could get such a payload past its own
   * `.parse()`. The nullable mirror was not merely untidy: it let a spec assert
   * the console's behaviour against a payload that cannot exist, which is
   * exactly the hole this release closed (see `lib/resolverAgreement.spec.tsx`).
   *
   * What CAN arrive is the raw product key — `dev` sends `item.medicationName`
   * verbatim with no catalogue lookup at all, and the branch falls back to the
   * key when the lookup fails. That is the real absence, and it is detected by
   * comparing the name with the queue row's `itemLabel`, not by a `null`.
   */
  name: string;
  /** Dose form, catalogue-sourced. */
  form?: string | null;
  /** Formulation, catalogue-sourced. Absent on an orders build before v0.14.0. */
  formulation?: string | null;
  /**
   * The PRESCRIBED strength, as the prescriber chose it. NEVER catalogue-derived.
   * `null` means no strength was recorded — render "Not recorded", flagged,
   * never a guess.
   *
   * OPTIONAL because v0.9.0's `pharmacyPrescriptionItem` has no such key and is
   * `.strict()`, so orders `dev` cannot send one: the field arrives `undefined`
   * there and `null` on the prescribing branch. Both mean "nobody recorded one",
   * and the resolver answers them identically — but typing it as always-present
   * would have made a `dev` payload unrepresentable in a fixture, which is how
   * the console's behaviour against the deployed build went untested.
   */
  strength?: string | null;
  /** PACKS, as persisted. Render it through `lib/prescribedLine`, never raw. */
  quantity: number;
  /**
   * The SERVER'S rendering of `quantity` — the identical string the signed PDF
   * and the Pharmacy Express email carry (orders' `displayQuantity` seam).
   *
   * Optional because an older orders build does not send it. THIS IS THE FIELD
   * THAT STOPS THE DISPENSING CONSOLE DISAGREEING WITH THE DOCUMENT: the console
   * printed a bare `3` under a "Qty" header beside a pre-filled-device
   * description — which reads as three devices — while the PDF and the PX email
   * for the same line said "3 packs". The portal renders this verbatim and
   * formats no unit of its own; see `lib/prescribedLine`.
   */
  displayQuantity?: string | null;
  /** The prescriber's own directions. `null` means none were recorded. */
  directions: string | null;
  coldChain: boolean;
}

/**
 * The dispensing view. A DISPENSING-SPECIFIC clinical extract — allergies,
 * current medication, contraindication flags — NOT the clinician's full
 * questionnaire projection. A pharmacist needs what makes the dispense safe;
 * the rest of the consultation stays with the clinician (data minimisation,
 * UK GDPR Art 9).
 */
export interface PharmacyOrderDetail {
  orderCaseId: string;
  orderRef: string;
  clinicName: string;
  state: OrderCaseState;
  patient: {
    fullName: string;
    dateOfBirth: string;
    phone?: string | null;
    deliveryAddress: string[];
  };
  items: PharmacyPrescriptionItem[];
  /**
   * The supplies to include with this order (2026-09-24) — orders'
   * `pharmacy/dto.ts` local extend, ALWAYS sent (`[]` when none) by an orders
   * build that carries supplies. See {@link PrescribedSupply}.
   *
   * THE INCIDENT THIS FIELD EXISTS FOR: a pharmacy dispensed pens with nothing to
   * inject them with, because the requirement was written into prescription notes
   * the dispensing channel never carries. The console renders a non-empty list as
   * its own "Supplies to include" block ABOVE the prescribed items, so it cannot
   * be read past on the way to the table.
   *
   * OPTIONAL only so an older orders build (which cannot send it) still renders
   * — and renders exactly what it did before, which is nothing.
   */
  supplies?: PrescribedSupply[];
  clinical: {
    allergies?: string | null;
    currentMedication?: string | null;
    contraindications: string[];
    clinicianNotes?: string | null;
  };
  prescriber: { fullName: string; registrationNumber?: string | null };
  pharmacyNotes?: string | null;
  issuedAt: string;
  dispensedAt?: string | null;
  dispensedByName?: string | null;
  /**
   * Courier + tracking, once captured (the case is then `dispatched`).
   * Optional so a just-dispensed order (courier not yet entered) still
   * validates — the pharmacy console shows the capture form until these land.
   * Mirrored from contracts `pharmacyOrderDetail` (v0.8.5+).
   */
  courier?: string | null;
  trackingNumber?: string | null;
  dispatchedAt?: string | null;
  /**
   * Non-null only when the order is a staff-marked test record. The detail read
   * only ever returns one of these when the pharmacist has asked to see test
   * orders, so its presence here means "you opted in and this is one of them".
   */
  testRecord?: PharmacyTestRecord | null;
}

/**
 * Confirm a dispense (contracts `pharmacyDispenseRequest`).
 *
 * `confirmed` is a literal `true` so the "are you sure?" step cannot be skipped
 * by a client that simply omits it. `stepUpCode` re-proves the INDIVIDUAL on
 * the dispensing action itself — a shift-length session on a shared dispensary
 * workstation would otherwise let someone else dispense under a colleague's
 * session, which defeats the attributability the code exists to provide.
 * `idempotencyKey` is required so a double-click, retry or replay can never
 * dispense twice.
 */
export interface PharmacyDispenseRequest {
  confirmed: true;
  stepUpCode: string;
  idempotencyKey: string;
}

/** Closed vocabulary — free text is for the audit row, never a wire enum. */
export type PharmacyRejectionReason =
  | 'out_of_stock'
  | 'address_unclear'
  | 'clinical_query'
  | 'cold_chain_not_assured'
  | 'duplicate_order'
  | 'other';

/**
 * Refuse to dispense. The reason and detail reach the ADMIN team and the audit
 * record; the patient is NEVER told a pharmacy refused their order.
 */
export interface PharmacyRejectRequest {
  reason: PharmacyRejectionReason;
  /** Audit-row only. Third-party free text never rides into an email body. */
  detail?: string;
  idempotencyKey: string;
}

/** Courier + tracking. Setting this moves the case to `dispatched`. */
export interface PharmacyTrackingRequest {
  courier: string;
  trackingNumber: string;
  idempotencyKey: string;
}

/** What a dispense/reject/tracking write reports back — the order's new state. */
export interface PharmacyWriteResult {
  state: OrderCaseState;
  dispensedAt?: string | null;
  trackingNumber?: string | null;
}

/**
 * A short-lived signed link to the prescription PDF (contracts
 * `prescriptionDownloadGrant`).
 *
 * RELEASED ONLY ONCE THE ORDER IS DISPENSED. Handing the document over first
 * allows a print-and-never-dispense path, where a prescription leaves the
 * building against an order the system still believes is pending. The rendered
 * PDF carries a DISPENSED watermark plus the dispensing pharmacist and
 * timestamp, so every copy in existence is self-evidencing.
 */
export interface PrescriptionDownloadGrant {
  url: string;
  expiresAt: string;
  watermarked: true;
}

/**
 * One row of the pharmacist's OWN activity — the self-scoped projection of the
 * orders audit trail (dispense / reject / dispatch / prescription download).
 * Ids, enums and timestamps only: PHI-free by construction (R19), and money-free
 * because the pharmacy vocabulary contains no money action.
 *
 * [BACKEND GAP] There is no contract shape and no endpoint for this yet. It is
 * specified here because the CTO's rail is "Dispensing queue, My activity" and
 * a dispensing record is only useful to the professional it names if they can
 * see it. `MyActivity` renders an honest "not available yet" panel meanwhile.
 * TODO(orders): `GET /v1/pharmacy/activity`, actor derived from the verified
 * principal so the read cannot be widened from the browser.
 */
export interface PharmacyActivityEntry {
  auditId: string;
  action: string;
  orderCaseId: string;
  orderRef: string;
  occurredAt: string;
  /** The recorded reason on a rejection (audit-row prose, ADMIN + audit only). */
  reason?: string | null;
}

export interface PharmacyActivityResponse {
  items: PharmacyActivityEntry[];
}

/**
 * The patient-facing dispatch read (contracts `patientOrderTracking`). Carries
 * tracking ONLY — never the pharmacy's rejection reason, and never a pharmacy
 * identity. See {@link OrderSummary.tracking}.
 */
export interface PatientOrderTracking {
  courier: string;
  trackingNumber: string;
  dispatchedAt: string;
  trackingUrl?: string | null;
}

/* ── Key alerts: the double-prescribing safety check (2026-08-07) ──────────
 *
 * A patient can reach a prescriber down three roads (self-serve, clinician
 * callback, back-office), so the same person can arrive holding a plan nobody
 * on this screen knows about. The orders-service answers "what medication is
 * this patient already on?" for the CLINICIAN/ADMIN review. MONEY-FREE for
 * both roles by construction — there is no pence field anywhere in the shape,
 * so the clinician money-redaction sweep passes over it unchanged.
 */

/** How the patient's plan behind one medication is billing right now. */
export type MedicationSafetySubscriptionState = 'active' | 'past_due' | 'dormant';

/**
 * WHICH identity seam the server matched this item on.
 *
 * `'self'` is the only CONFIRMED basis: the same verified patient account
 * (`OrderCase.patientUserId`), so the person in front of the clinician
 * definitely holds this plan. Every other member is a PROBABLE match found
 * across accounts — the same human under a different sign-in — and the review
 * must say so rather than assert it as fact.
 *
 * The union is deliberately WIDER than what orders serves today (it still only
 * emits `'self'`): the cross-account tiers are DPO-gated and land behind this
 * exact wire value, and the portal's rendering rule is "anything that is not
 * `'self'` is unconfirmed", so a tier that ships before the portal knows its
 * name still renders the safe, weaker wording instead of a confident one.
 */
export type MedicationMatchBasis = 'self' | 'email' | 'name_dob_address' | 'name_dob';

/**
 * How sure the server is about a cross-account match, when it says so.
 *
 * These are IDENTITY's tier names, passed through orders unchanged — the service
 * that does the matching owns what the bands are called. The portal briefly had
 * its own vocabulary ('confirmed'/'medium'/'low'), which overlapped on exactly
 * one value, so `probable` and `possible` both fell through to the weakest
 * wording and a "server-sent band wins" claim was untrue for three of the four.
 *
 * Optional on the wire: a Phase-1 orders does not send it, so the review derives
 * a band from `matchedOn` when it is absent — and a server-sent band always wins.
 */
export type MedicationMatchConfidence = 'certain' | 'high' | 'probable' | 'possible';

/** One medication the patient already holds. */
export interface MedicationSafetyItem {
  orderCaseId: string;
  /** The human `ORD-…` reference, or null on a case minted before references. */
  reference: string | null;
  productKey: string;
  /** Null until the plan carries a resolved medication (falls back to productKey). */
  medicationName: string | null;
  /**
   * ⚠️ NOT A PRESCRIBED STRENGTH — DO NOT RENDER IT AS ONE.
   *
   * orders resolves this through `PricesPort.describeMedication`, which reads a
   * launch catalogue mirroring the prices-service PLAN ANCHOR — "the dearest
   * variant each family is priced against". It is a COMMERCIAL figure with no
   * clinical provenance whatsoever, and for the device families it sits
   * several-fold above the dose treatment starts at.
   *
   * This banner is the highest-consequence read in the estate: it tells a
   * prescriber what the patient is ALREADY on at the moment they choose the next
   * dose. So `KeyAlerts` deliberately does not display this value, and states
   * that no dose is recorded instead. The field stays on the mirror only because
   * it is on the wire — removing it here would not stop orders sending it, and a
   * reader needs to find this warning when they go looking for why the dose is
   * not on screen.
   *
   * TODO(orders): the prescribe form now captures a real prescriber-chosen
   * variant. When this read is repointed at the PRESCRIPTION rather than the
   * catalogue, it becomes a clinical field and the banner can show it again.
   */
  strength: string | null;
  /** Catalogue-sourced product fact. Safe to show. */
  formulation: string | null;
  subscriptionState: MedicationSafetySubscriptionState;
  /** contracts orderCaseState — the case the medication hangs off. */
  orderState: string;
  monthsElapsed: number;
  /** The plan's total months, or null for an open-ended/unset plan. */
  months: number | null;
  startedAt: string;
  /**
   * When the PAID-FOR supply ran out — `startedAt` plus one calendar month per
   * month billed, computed server-side with the drift-free `addUtcMonths` the
   * billing schedule uses. Null when nothing was ever captured (a dormant
   * in-flight plan has no supply window at all).
   *
   * The portal RENDERS this and never recomputes it: month arithmetic in JS
   * disagrees with the server for every month-end activation (31 Jan + 1 month),
   * and a safety banner must not state a date the service would dispute.
   */
  supplyCoveredUntil: string | null;
  nextBillingAt: string | null;
  matchedOn: MedicationMatchBasis;
  /** Server-stated confidence band; absent today (see the type's note). */
  matchConfidence?: MedicationMatchConfidence | null;
}

/**
 * GET /v1/orders/patients/:patientUserId/medication-safety (CLINICIAN/ADMIN).
 *
 * TWO GRADED ALERT TIERS, both measured from the end of the paid-for supply:
 *  - `ongoing` — the patient still HOLDS this medication (not the same as "the
 *    plan is still billing": a completed 1-month plan still holds its supply).
 *    THE alert;
 *  - `recentlyIssued` — the supply ran out recently. STILL an alert, but a
 *    softer one whose copy names the medication and the date rather than
 *    claiming the patient is on it right now.
 * `inFlight` (dormant) is a lower-severity "another journey is in progress" and
 * raises no alert; `history` is informational and never alerts.
 *
 * `status` is 'alerts' when `ongoing` OR `recentlyIssued` is non-empty.
 *
 * `recentlyIssued` is ADDITIVE (orders Phase 2): a response without the key is a
 * Phase-1 server, not a broken answer — see `medicationSafetyFrom`.
 */
export interface MedicationSafetyResponse {
  patientUserId: string;
  status: 'clear' | 'alerts';
  ongoing: MedicationSafetyItem[];
  recentlyIssued?: MedicationSafetyItem[];
  inFlight: MedicationSafetyItem[];
  history: MedicationSafetyItem[];
  /**
   * Whether the cross-account half finished. `partial` means that half did not
   * run (identity timed out, or the list was cut). Absent on an older orders.
   * A clear screen must not be drawn from `partial` — see `medicationSafetyFrom`.
   */
  crossAccountCoverage?: 'complete' | 'partial';
}

/* ── End-of-term re-subscribe (v0.8.7) ──────────────────────────────────────
 *
 * A subscription that runs its full term lands `completed` with no next bill —
 * ONE-MONTH PLANS DELIBERATELY DO NOT AUTO-RENEW, so a patient who finishes a
 * plan has to actively choose again. These are the local mirrors of the
 * contracts v0.8.7 `resubscribe*` shapes behind that choice.
 *
 * TWO LANES, and the default matters. LANE B (`patient_confirmed`) is the norm:
 * an admin (or the patient) starts it and THE PATIENT confirms the new term
 * before anything is charged, because a new term is a NEW CONTRACT and the
 * original mandate covered the original term only. LANE A (`admin_authorised`)
 * is the override for a patient who cannot self-serve: the admin takes
 * authorisation on the call and records how and when.
 *
 * MONEY IS INTEGER PENCE. The offer preview carries money on purpose — it is
 * the patient's OWN price and they must see what they are authorising — but no
 * re-subscribe shape reaches a clinician surface (constraint 6).
 */

/** contracts `resubscribeLane`. */
export type ResubscribeLane = 'patient_confirmed' | 'admin_authorised';

/** contracts `resubscribeConfirmationMethod` — how the confirmation was given. */
export type ResubscribeConfirmationMethod = 'token' | 'session' | 'admin_recorded';

/** contracts `resubscribeOfferState`. `payment_action_required` is the SCA / declined-card / no-card stall. */
export type ResubscribeOfferState =
  | 'pending_confirmation'
  | 'confirming'
  | 'payment_action_required'
  | 'confirmed'
  | 'expired'
  | 'declined'
  | 'withdrawn';

/**
 * contracts `resubscribeIneligibleReason` — why a plan cannot be re-subscribed.
 * COMPUTED SERVER-SIDE AND NEVER INFERRED HERE: the read that enables the button
 * and the write that creates the offer run the same function on the server, so
 * a client-side guess would disagree with it the moment the rules move.
 */
export type ResubscribeIneligibleReason =
  | 'plan_still_live'
  | 'never_started'
  | 'cancelled_mid_term'
  | 'already_resubscribed'
  | 'offer_in_flight'
  | 'no_patient_bound'
  | 'not_supplied';

/** contracts `resubscribeAuthorisationMethod` — how a LANE A admin took the patient's authorisation. */
export type ResubscribeAuthorisationMethod = 'phone_call' | 'video_call' | 'in_person' | 'secure_message';

/** contracts `resubscribeFailureReason` — the renewal-failure vocabulary, reused rather than forked. */
export type ResubscribeFailureReason = 'card_declined' | 'no_payment_method' | 'requires_action';

/**
 * contracts `resubscribeEligibility` — the server's button-enable answer.
 *
 * `months` / `monthlyPence` are TODAY'S price for the new term, resolved by the
 * same `PricesPort` read the write path uses. They are OPTIONAL only because the
 * orders revision that first shipped this lane did not send them yet — and any
 * surface that asks the patient to COMMIT to a figure must render nothing until
 * they arrive. See `patient-portal/ResubscribeCard`: the prior term's price is
 * not the new term's price, and quoting one as the other is a misquote, not a
 * rounding error.
 */
export interface ResubscribeEligibility {
  eligible: boolean;
  reason: ResubscribeIneligibleReason | null;
  /** Today's term for the new plan (defaults to the prior term server-side). */
  months?: PlanMonths;
  /** Today's monthly price in integer pence for that term. */
  monthlyPence?: number;
}

/**
 * The LANE A authorisation block. `attested` is `z.literal(true)` in contracts,
 * so an unattested request is unrepresentable rather than merely rejected.
 */
export interface ResubscribeAuthorisationInput {
  method: ResubscribeAuthorisationMethod;
  /** ISO UTC — when the patient actually said yes, not when the form was filled. */
  takenAt: string;
  /** Optional call/ticket reference (≤64 chars). */
  reference?: string;
  attested: true;
}

/** POST /v1/orders/resubscribe/offers body (ADMIN). Lane A REQUIRES `authorisation`; lane B rejects it. */
export interface ResubscribeInitiateRequest {
  priorSubscriptionId: string;
  lane: ResubscribeLane;
  /** Defaults to the prior term when omitted. */
  months?: PlanMonths;
  /** REQUIRED prose — lands on the append-only audit row's `reason` ONLY (R19). */
  reason: string;
  authorisation?: ResubscribeAuthorisationInput;
}

/** POST /v1/orders/resubscribe/self body (PATIENT). Ownership is the verified principal, never a body field. */
export interface ResubscribeSelfRequest {
  priorSubscriptionId: string;
  months?: PlanMonths;
}

/** The PUBLIC token lanes (preview / confirm / decline) — the token travels in the BODY, never a URL. */
export interface ResubscribeTokenRequest {
  token: string;
}

/**
 * What the patient sees BEFORE confirming. Money is present by design: this is
 * the patient's own price and the number they are authorising.
 *
 * `medicationName` is SERVER-RESOLVED and rendered verbatim — never derived,
 * mapped or defaulted in this tree (R28: a POM name must not exist in portal
 * source, comments included).
 */
export interface ResubscribeOfferPreview {
  medicationName: string;
  months: PlanMonths;
  monthlyPence: number;
  totalPence: number;
  expiresAt: string;
  deliveryAddress: Address | null;
  orderRef: string | null;
}

/** One offer as ADMIN sees it. Ids / enums / integer pence + the lane-A evidence. */
export interface ResubscribeOffer {
  offerId: string;
  priorSubscriptionId: string;
  orderCaseId: string;
  subscriptionId: string;
  patientUserId: string;
  lane: ResubscribeLane;
  state: ResubscribeOfferState;
  confirmationMethod: ResubscribeConfirmationMethod | null;
  months: PlanMonths;
  monthlyPence: number;
  expiresAt: string;
  confirmedAt: string | null;
  failureReason: ResubscribeFailureReason | null;
  authorisationMethod: ResubscribeAuthorisationMethod | null;
  authorisationTakenAt: string | null;
  authorisedBy: string | null;
  createdAt: string;
}

/** GET /v1/orders/resubscribe/offers response — bounded page, newest first. */
export interface ResubscribeOfferList {
  items: ResubscribeOffer[];
}

/**
 * The result of a confirm (token lane, self lane, or a lane-A initiate).
 *
 * `paymentLinkUrl` is a FLAT STRING and is present ONLY on
 * `payment_action_required` — the SCA / declined-card / no-card fallback. It is
 * the URL itself, not an object: the orders `resubscribeConfirmResult` schema is
 * `.strict()` and declares `paymentLinkUrl: z.string().url().optional()`, so a
 * nested `paymentLink` shape here would read as permanently absent and the
 * patient would silently never be offered the way out of an SCA stall.
 *
 * There is no half-state behind any of the three failures: no ledger row exists
 * unless the authorisation actually succeeded.
 */
export interface ResubscribeConfirmResult {
  offerId: string;
  orderCaseId: string;
  subscriptionId: string;
  state: ResubscribeOfferState;
  paymentLinkUrl?: string;
  failureReason: ResubscribeFailureReason | null;
}

/**
 * What `POST /v1/orders/resubscribe/offers` actually answers with, and the
 * reason it is a UNION rather than a single shape.
 *
 * LANE B returns the created offer — nothing has been attempted, the patient
 * still has to confirm. LANE A runs the confirm inline (the admin already took
 * the authorisation on the call) and returns the CONFIRM RESULT, which carries
 * ids, the state and a failure reason but NOT `lane`, `months`, `monthlyPence`
 * or the authorisation read-back.
 *
 * Typing this as `ResubscribeOffer` alone is what let the admin drawer render
 * `undefined months at £NaN` after the highest-risk action in the feature.
 * Narrow with `isResubscribeOffer` (api/orders.ts — this module stays types
 * only) and never read an offer-only field off the union without it.
 */
export type ResubscribeInitiateResult = ResubscribeOffer | ResubscribeConfirmResult;

/** POST /v1/orders/resubscribe/decline response. */
export interface ResubscribeDeclineResult {
  state: 'declined';
}

/* ── Approval visibility (2026-08-26) ─────────────────────────────────────────
 *
 * [CONTRACTS GAP] local mirrors of the clinic-service additions. The main
 * surface is the `plan_sent` STAGE above; these two fields are its backstops.
 * OPTIONAL on the read side (the established additive-field posture — an older
 * server without them still parses); the UI hides the chrome when absent.
 * Appended as interface merges to keep this module append-only.
 */

/** Interface merge: a clinician issued a medication recommendation for the
 *  linked assessment.
 *
 *  THE HOOK NO LONGER MOVES ONLY ONE STAGE, so this flag covers less than it
 *  used to and the difference matters. It was written when clinic-service's
 *  plan-sent hook advanced `eligibility_in_progress` alone and left every
 *  call-round card where it was; since the 2026-08-27 walkthrough the rule is a
 *  DENY-list (`PLAN_SENT_HELD_STAGES`), so the hook now moves a card into
 *  `plan_sent` from every open lane EXCEPT `pending_admin_review` and a PAID
 *  card in `clinical_review`. Those two are where the approval would otherwise
 *  go invisible, and they are what this flag is now the backstop for.
 *  Absent (older server) → hide the chip, never claim "not sent". */
export interface DealCard {
  planSent?: boolean;
}

/** Interface merge: WHEN THE PATIENT FIRST OPENED THAT PLAN LINK (T-1,
 *  2026-09-10) — the other half of `planSent`. Together they answer the
 *  question the conversion review could not: `planSent` says we told them,
 *  this says whether it landed. An approved card that never paid means
 *  something entirely different with a stamp here and without one.
 *
 *  ⚠️ THREE STATES, AND THE UI MUST KEEP THEM APART.
 *    · a string  → opened, at that instant;
 *    · `null`    → the server looked and found no open RECORDED. Not "not
 *                  opened": every deal predating the column is null because
 *                  nothing was watching, and even now a stamp exists only if
 *                  the browser reached us (the beacon is fire-and-forget and
 *                  the count is a floor). Only sayable where
 *                  `planLinkLastSentAt` is also on hand to put it against —
 *                  which is the deal DETAIL, never the board;
 *    · absent    → an older server. Say nothing at all.
 *
 *  So: a board may chip the positive; nothing may render the negative. The
 *  drawer says "no open recorded", which is true in every one of these cases.
 *  Stored server-side, unlike `planSent` and `planLinkLastSentAt`, which are
 *  read off the audit trail — an open happens in a browser and leaves no trail
 *  to derive. */
export interface DealCard {
  planViewedAt?: string | null;
}

/** Interface merge: WHY AN APPROVED PATIENT HAS NOT PAID (T-2, 2026-09-11) —
 *  clinic-service `dealCard.dropOffReason` / `dropOffReasonAt` /
 *  `dropOffReasonSource`. [CONTRACTS GAP] — the three fields have been on the
 *  wire since clinic #202 and were absent here, so no staff surface could read
 *  what a patient told us.
 *
 *  · `dropOffReason` — the patient's own choice, LATEST WINS. Null is "no
 *    reason RECORDED", never "no reason": most people who leave never say. The
 *    server nulls a value outside its vocabulary on the way out.
 *  · `dropOffReasonAt` — when a reason was FIRST given; a later change of mind
 *    does not move it.
 *  · `dropOffReasonSource` — where it was given; `patient_plan_page` only.
 *
 *  Absent = an older clinic-service: say nothing. What a surface may assert off
 *  these, and when, lives in pipeline/dropOffReasonsStaff.tsx. */
export interface DealCard {
  dropOffReason?: DropOffReasonKey | null;
  dropOffReasonAt?: string | null;
  dropOffReasonSource?: 'patient_plan_page' | null;
}

/** Interface merge: WHEN THE CLINICIAN FIRST APPROVED, and WHO (2026-09-11).
 *
 *  The two fields `journeyModel.ts` named as "THE UNBLOCK": the decision rung
 *  could previously neither date itself nor say who decided, and substituted
 *  `stageEnteredAt` only while the card sat in `plan_sent` — so a decision's
 *  date vanished the moment the card moved on.
 *
 *  ⚠️ AND THE OTHER HALF OF THE ORDERING FACT. Against `holdAuthorisedAt`,
 *  these answer "did the money land before the clinician decided?" — a question
 *  the panel used to infer from CURRENT state, which meant a completed
 *  card-first journey re-rendered as decision-first the moment it was approved,
 *  showing a clinician as having decided before a card they were holding. Both
 *  instants are durable, so the question can be asked of the past tense.
 *
 *  SET-ONCE (the FIRST approval), deliberately unlike last-wins
 *  `holdAuthorisedAt`: a decision happens once, an authorisation can happen
 *  repeatedly. Null means NOT APPROVED — the server backfilled every approval
 *  that left a `recommendation_issued` row, so it is not "approved at an
 *  unknown time". Absent means an older clinic-service: say nothing.
 *
 *  `approvedByUserId` is a STAFF id — no money, no PHI. Resolve it to a name
 *  only where staff names are already resolved, and render nothing rather than
 *  guess: naming the wrong clinician on a prescribing decision is not a
 *  cosmetic error. */
export interface DealCard {
  approvedAt?: string | null;
  approvedByUserId?: string | null;
}

/** Interface merge: ADMIN-only display label on an audit row — who an
 *  ASSESSMENT-targeted entry is about (the Lead's name, else email), resolved
 *  by clinic-service at READ time (never stored on the row — R19). Only the
 *  clinic source sends it today; null for clinician readers and unresolvable
 *  chains. Absent/null → fall back to the short target id. */
export interface AuditEntry {
  targetLabel?: string | null;
}

/* ── Test-record flagging (2026-08-26) ───────────────────────────────────────
 *
 * [CONTRACTS GAP] local mirrors of the two mark-test endpoints (clinic-service
 * `POST /v1/deals/:id/mark-test`, orders-service `POST /v1/order-cases/:id/
 * mark-test` — landing in their sibling PRs). Staff walk-throughs and demo
 * runs are indistinguishable from real patients on the board and in every
 * report, so ADMIN/CLINICIAN can mark a record as test data: the clinic
 * sibling CLOSES an open deal (closed_lost, reason "test record") and the
 * DEFAULT deals list excludes it — the board's "Show test records" view opts
 * back in with `includeTest=true` — while reporting reads exclude both
 * halves. Nothing is deleted and no email is sent. Appended as an interface
 * merge to keep this module append-only (sibling agents edit it in parallel
 * worktrees).
 */

/** Interface merge: the test-record flag on every card. OPTIONAL on the read
 * side (the established additive-field posture) — an older server simply
 * omits it, and absent means "a real record". */
export interface DealCard {
  isTest?: boolean;
}

/** POST /v1/orders/:orderCaseId/mark-test response — ids + the flag only,
 * never money (the flag is bookkeeping, not a money action). */
export interface OrderMarkTestResult {
  orderCaseId: string;
  isTest: boolean;
}

/* ── Journey feed (2026-08-26, CTO stage-walkthrough) ────────────────────────
 *
 * [CONTRACTS GAP] local mirrors of the clinic sibling's feat/journey-feed
 * additions. Appended as an interface merge to keep this module append-only
 * (sibling agents edit it in parallel worktrees); the NESTED DealDetail
 * additions (callback.createdAt, assessment.consentAgreedAt/
 * consentQuestionSetVersion) and the DealActivityEntry.kind widening live IN
 * PLACE above, because neither a nested object type nor a union member can
 * ride a merge.
 */

/**
 * Who performed a feed entry, as a coarse ROLE the server resolves at read
 * time (never a name on the wire — R19: the portal resolves display names
 * through the existing ADMIN-only batched lookup). Agreed clinic seam
 * (2026-08-27): audit-derived entries always carry a role — `SYSTEM` is a
 * VERIFIED non-staff audit principal (guest/scheduler/service), rendered as
 * "System" — while `null` is reserved for sources with NO role recorded (the
 * note ledgers, the ambiguous journey milestones). The portal renders a chip
 * ONLY for these four values; null/absent renders no chip at all.
 */
export type DealActivityActorRole = 'CLINICIAN' | 'ADMIN' | 'PATIENT' | 'SYSTEM';

/** Interface merge: the actor's role on every feed entry. OPTIONAL on the
 * read side (the established additive-field posture) — an older clinic-service
 * omits the key entirely; absent and null alike render NO role chip (the role
 * was not recorded, and guessing one would mislabel a human action). */
export interface DealActivityEntry {
  actorRole?: DealActivityActorRole | null;
}

/* ── Stage clarity (2026-08-27) ──────────────────────────────────────────────
 *
 * [CONTRACTS GAP] local mirrors of the clinic sibling's `feat/stage-clarity`
 * additions. Two facts the board could not state before, both already recorded
 * server-side and neither derivable client-side:
 *
 *   1. WHY a deal ended. Today a closed deal wears the derived "Clinician
 *      rejected" chip (statusChips.tsx) for BOTH closure kinds, because both
 *      land on `eligibility === 'not_eligible'` — so a HARD STOP, which no
 *      clinician ever looked at, is currently mislabelled as a clinician's
 *      decision. The Lost column cannot be read honestly without the reason
 *      and the real author.
 *   2. WHAT an admin has been asked to do. The clinician→admin handoff note
 *      only exists inside the activity feed, so the ask is invisible until
 *      someone reads note history.
 *
 * SPLIT ACROSS THE TWO READS, mirroring the sibling: the CARD carries the
 * compact halves the board can act on (a reason string, a boolean), and the
 * DRAWER aggregate carries the full blocks with their actor attribution. The
 * reason itself is derived ONCE server-side and served to both, so the Lost
 * column and the drawer quote the same string.
 *
 * R19: `closedReason` / `actionNeeded.note` are staff+clinical FREE TEXT and
 * belong on the deal row and the audit row ONLY — they must never reach a log
 * line or an analytics event, and the portal renders both as PLAIN TEXT NODES
 * (React escapes them). Neither is note HTML, so neither goes near
 * dangerouslySetInnerHTML / sanitizeNoteHtml.
 *
 * R41: the server decides that a deal is closed and that an ask is
 * outstanding; nothing here re-derives either. Appended as interface merges to
 * keep this module append-only (sibling agents edit it in parallel worktrees) —
 * each new field is either a primitive or names a SEPARATELY DECLARED
 * interface, which a merge carries fine (only an inline nested object type
 * cannot).
 */

/**
 * HOW A DEAL ENDED — the drawer aggregate's outcome block (clinic sibling's
 * `dealDetail.outcome`).
 *
 * There is deliberately NO closure-kind discriminator on the wire: the two
 * closures this exists for — a clinician's rejection and an engine-fired hard
 * stop — are told apart by their ACTOR, not by a label. A hard stop carries a
 * SYSTEM role (or no actor at all, e.g. a boot-backfill close) while a
 * rejection carries the clinician's verified principal, which is exactly the
 * distinction the derived "Clinician rejected" chip could not make. The portal
 * therefore never labels the kind — it states the reason and names the author.
 *
 * EVERY FIELD IS INDEPENDENTLY NULL, and that is load-bearing: the server sends
 * whichever half it actually has rather than suppressing the block, so a deal
 * closed with a reason but no recorded actor says so instead of inventing one.
 * All four are null on an OPEN deal.
 *
 * `closedReason` is VERBATIM staff/clinical free text that may name a clinical
 * CATEGORY (R19 — this staff read only; never a log line or an event; the
 * precise hard-stop key is coarsened server-side before it ever gets here).
 * `closedByUserId` is an opaque principal id: names are NEVER resolved
 * server-side, so the portal looks one up through the ADMIN-only batched staff
 * lookup and falls back to the role chip.
 */
export interface DealOutcome {
  closedReason: string | null;
  closedAt: string | null;
  closedByUserId: string | null;
  closedByRole: DealActivityActorRole | null;
}

/**
 * WHAT AN ADMIN WAS ASKED TO DO — the drawer aggregate's ask block (clinic
 * sibling's `dealDetail.actionNeeded`).
 *
 * `required` is the server's honest statement that the deal sits in admin's
 * court; the rest is the ask itself, taken VERBATIM from the handover note.
 *
 * `note` CAN BE NULL WHILE `required` IS TRUE — a deal that arrived in the
 * stage automatically (a callback escalation) writes no ask. The banner still
 * renders in that case and says no note was recorded: borrowing an older
 * round's ask, or silently hiding the fact that admin action is due, would both
 * be worse than an unexplained badge. Same actor posture as DealOutcome —
 * opaque id, coarse role, no server-side name resolution.
 */
export interface DealActionNeeded {
  required: boolean;
  askedByUserId: string | null;
  askedByRole: DealActivityActorRole | null;
  askedAt: string | null;
  /** VERBATIM — the ask exactly as the asker wrote it. Null = none recorded. */
  note: string | null;
}

/**
 * Interface merge: the two stage-clarity facts the BOARD needs, in the compact
 * forms the list read carries them.
 *
 * `closedReason` is the SAME derivation the drawer's `outcome.closedReason`
 * uses (the clinic sibling derives both in one place — the Deal row's column,
 * else the closing audit row's reason), so the card and the drawer quote one
 * string and cannot drift. `actionNeeded` is a pure stage derivation, which is
 * why the card gets a plain boolean and only the drawer pays for the ask.
 *
 * BOTH OPTIONAL on the read side (the established additive-field posture) even
 * though the server always sends them — the two services deploy independently,
 * and an older clinic-service must render nothing rather than crash the board.
 * Absent means "nothing to say": never "closed for no reason", never "no action
 * needed".
 */
export interface DealCard {
  closedReason?: string | null;
  actionNeeded?: boolean;
}

/** Interface merge: the drawer aggregate's two blocks. OPTIONAL for the same
 * independent-deploy reason as the card fields above; absent → the drawer
 * renders neither the outcome block nor the banner and keeps its existing
 * chrome exactly as it was. */
export interface DealDetail {
  outcome?: DealOutcome;
  actionNeeded?: DealActionNeeded;
}

/* ── Clinician photo review (2026-08-27, CTO + Reneal) ───────────────────────
 *
 * [CONTRACTS GAP] local mirrors of the phase-1 server shapes behind
 * "only after the clinician SEES the photo can they finalise": the
 * clinic-service multi-photo gallery read and the orders-service attestation
 * that gates the money capture. Both are deliberately REPO-LOCAL on their own
 * side too — the pinned contracts `assessmentReview` / `staffOrder` are strict
 * and ship untouched — so they mirror here rather than arriving through the
 * contracts package (this repo carries no backend dependency by design).
 */

/**
 * One stored weight photo, as a short-lived signed READ url.
 *
 * `photoUrl` is `null` in stub mode (object storage unset on this
 * environment): a photo IS on file, there is simply no preview here. The url
 * EXPIRES — never cache it beyond the current page view, and re-read the
 * gallery to mint a fresh one rather than retrying a dead link.
 */
export interface AssessmentPhotoEntry {
  photoUrl: string | null;
  /**
   * The `AssessmentPhoto` row id — the ONLY way to address ONE photo
   * (clinic-service 2026-09-06, the retract lane).
   *
   * The entries used to be anonymous, which is why the gallery keys its tiles
   * on their POSITION in the array. That is fine for rendering and useless for
   * a delete: positions shift under every add and every retention sweep, so
   * "remove the third tile" races into removing the wrong image.
   *
   * ⚠️ NULLABLE, AND THE NULL IS LOAD-BEARING. A legacy `Assessment.photoRef`
   * with no ledger row behind it (a first-photo grant predating 2026-08-27)
   * has no id to give, and there is deliberately no retract lane for it. A
   * photo with no id must render WITHOUT a Remove control — no ⋯ row in the
   * viewer either — rather than invent an address. OPTIONAL for the same
   * deploy-window reason as `addedByRole`: absent means "this server does not
   * report photo ids", which reads the same way — no control.
   */
  photoId?: string | null;
  /**
   * WHO produced this image (clinic #166, 2026-09-04): `PATIENT` |
   * `CLINICIAN` | `ADMIN`, or `null` on a legacy pointer and on any grant
   * predating the column.
   *
   * ⚠️ IT IS NOT BOOKKEEPING — IT IS THE EVIDENTIAL CLAIM. Under weight
   * self-certification "the patient supplied this" and "the clinician obtained
   * it themselves" are different kinds of evidence, and a prescriber deciding
   * whether a declaration has been VERIFIED needs to know which one they are
   * looking at. A role enum only, never a name (R19).
   *
   * OPTIONAL here because clinic-service and the portal deploy independently;
   * absent means "not reported", never "the patient".
   */
  addedByRole?: 'PATIENT' | 'CLINICIAN' | 'ADMIN' | null;
  /**
   * Whole days left before the 365-day retention sweep discards this photo
   * (clinic-service, CTO 2026-09-03) — an integer COUNT, never a date,
   * computed server-side from the stored grant timestamp and clamped at 0.
   *
   * MIRRORED, NOT YET RENDERED. It is on the wire and it belongs on this
   * screen — a reviewer weighing a declaration against an image should know
   * the image is about to go — but no surface in this batch shows it.
   * TODO(CTO): put it on the gallery tile.
   */
  remainingDays?: number;
}

/**
 * GET /v1/clinic/assessments/:id/photos (CLINICIAN·ADMIN) — EVERY stored
 * weight photo for the assessment: the legacy first-photo pointer first when
 * it is not duplicated, then the per-photo rows oldest-first. The review's
 * single `photoUrl` stays exactly as it was — this ships BESIDE it.
 */
export interface AssessmentPhotosRead {
  photos: AssessmentPhotoEntry[];
  /**
   * At least one photo for this assessment has been DISCARDED by the 365-day
   * retention sweep (clinic-service, CTO).
   *
   * ⚠️ IT IS WHAT SEPARATES "GONE" FROM "NEVER SENT", and the CTO's rule on it
   * is explicit: render "no photo on file", NEVER a patient failure. Without
   * it an empty gallery reads identically to a patient who ignored us, and the
   * clinician chases somebody who complied a year ago.
   *
   * OPTIONAL for the independent-deploy reason; absent means "not reported",
   * never "nothing was discarded".
   */
  photosDiscarded?: boolean;
}

/**
 * DELETE /v1/clinic/assessments/:id/photos/:photoId (**ADMIN or CLINICIAN** —
 * corrected 2026-09-06, see `retractWeightPhoto` in `api/clinic.ts` for the CTO
 * decision and the objection it overruled; this line said **ADMIN ONLY** for
 * part of that day) — what a RETRACTION answers with.
 * `[CONTRACTS GAP]` — a local mirror of clinic-service's strict
 * `weightEvidenceRetractResult`.
 *
 * `photosRemaining` is the file's size AFTER the removal, and it is the reason
 * the caller does not have to guess from a list it fetched before the delete.
 * It also makes the SLOT rule legible: the five-photo cap counts stored photos,
 * so a retraction hands the slot back and this number proves it.
 */
export interface WeightEvidenceRetractResult {
  photoId: string;
  photosRemaining: number;
}

/**
 * POST /v1/clinic/assessments/:id/evidence-request (CLINICIAN·ADMIN, clinic
 * #166, 2026-09-04) — "ask this patient to evidence the weight they
 * self-certified". `[CONTRACTS GAP]` — a local mirror of clinic-service's
 * strict `weightEvidenceRequestResult`.
 *
 * IDS, ENUMS AND BOOLEANS ONLY: no weight, no address, no identity — the
 * caller already has the record open and this response has no business
 * restating any of it (R19).
 *
 * ⚠️ `notified` IS THE HALF THAT MUST BE SHOWN. The request row is recorded
 * either way, because the clinical decision to ask HAPPENED — but on a stub
 * mail env, a deduped replay, a leadless journey or a mail fault the patient
 * never heard about it. `false` means "phone them", and a screen that hid it
 * would leave a clinician waiting on evidence nobody asked for out loud.
 */
export interface WeightEvidenceRequestResult {
  requestId: string;
  requestedAt: string;
  notified: boolean;
}

/**
 * GET /v1/orders/:id/photo-review-status (CLINICIAN·ADMIN) and the body
 * POST /v1/orders/:id/photos-reviewed returns — ONE shape for "may this order
 * be finalised yet?".
 *
 * `required` is the LANE fact (the order has an assessment behind it;
 * re-subscribe / callback-lane orders are exempt from the gate). The pair is
 * the attestation stamp, both null until a clinician attests.
 * `photosReviewedBy` is the OPAQUE identity ref, never a name (R19) — the
 * portal resolves display names through the existing ADMIN-only batched
 * lookup, and shows the role to everyone else.
 *
 * Money-free by shape, so it is safe to read on the clinician surface.
 */
export interface PhotoReviewStatus {
  required: boolean;
  photosReviewedAt: string | null;
  photosReviewedBy: string | null;
}

/**
 * The month-1 authorisation, as the hold-status read names it:
 *  · `valid`    — a hold stands and can still be captured.
 *  · `expired`  — there was a hold and it can no longer be captured.
 *  · `none`     — no authorisation was ever placed, or its money was refunded.
 *  · `captured` — month 1 has already been taken; there is no hold left to judge.
 */
export type OrderHoldPreAuth = 'valid' | 'expired' | 'none' | 'captured';

/**
 * HOW orders-service knows:
 *  · `stripe`   — Stripe was asked just now and answered.
 *  · `estimate` — Stripe could not be asked, so the 7-day clock decided. The CTO's
 *                 condition for that fallback was that it be LABELLED as an
 *                 estimate, so the label travels with the answer.
 *  · `ledger`   — our own record is conclusive and Stripe was not asked.
 */
export type OrderHoldBasis = 'stripe' | 'estimate' | 'ledger';

/**
 * GET /v1/orders/:orderCaseId/hold-status (ADMIN·CLINICIAN, orders-service #233)
 * — "is there a card, and is the month-1 pre-authorisation still good?",
 * answered LIVE: orders-service asks Stripe when the deal drawer opens.
 *
 * [CONTRACTS GAP] local mirror of orders-service's `hold-status.dto.ts`
 * (`clinicianHoldStatus` / `adminHoldStatus`).
 *
 * NOT THE PAYMENT POSTURE. `dealCard.paymentPosture` is a BOARD read straight
 * from orders' ledger — a page of cards, no Stripe call — so it says
 * `authorised` for any `held` row however old. This read is for ONE order and
 * can say what the posture cannot: whether that hold is still capturable, or
 * died on Stripe's clock while the ledger still said `held`.
 *
 * ⚠️ MONEY IS ROLE-SHAPED. orders-service parses the clinician answer with a
 * `.strict()` schema that has no pence key at all; only an ADMIN answer carries
 * `authorisedPence`. The portal still gates on `role === 'ADMIN'`
 * (`pipeline/HoldStatusPanel.tsx`), so two independent things have to fail
 * before a figure reaches a clinician.
 *
 * R19: ids, enums, booleans, timestamps and (ADMIN only) integer pence. No
 * Stripe identifier and no card detail of any kind.
 */
export interface OrderHoldStatus {
  /** The order case this answer is about (uuid). */
  orderCaseRef: string;
  /** A card is saved for this order's patient — a payment METHOD, not permission to charge. */
  cardOnFile: boolean;
  /** The month-1 authorisation — see {@link OrderHoldPreAuth}. */
  preAuth: OrderHoldPreAuth;
  /** How orders-service knows — see {@link OrderHoldBasis}. */
  basis: OrderHoldBasis;
  /** When a `valid` hold stops being capturable (ISO); null for every other answer. */
  expiresAt: string | null;
  /** True when `expiresAt` is our 7-day estimate rather than Stripe's own deadline. */
  expiresAtEstimated: boolean;
  /** When orders-service produced this answer (ISO) — it is a point-in-time read. */
  checkedAt: string;
  /**
   * ⚠️ ADMIN PROJECTION ONLY — the month-1 amount HELD (`valid`) or TAKEN
   * (`captured`); null on `expired` and `none`. The key is ABSENT from a
   * clinician's answer, not null.
   */
  authorisedPence?: number | null;
}

/* ── Per-owner call attempts (2026-09-05) ───────────────────────────────────
 *
 * [CONTRACTS GAP] local mirror of the clinic sibling's `feat/pipeline-restructure`
 * card fields (`src/deals/deal.schemas.ts`, the "THE PER-OWNER SPLIT" block).
 *
 * WHY THE SPLIT IS READ AND NEVER DERIVED. The portal already receives
 * `attemptsThisRound` and `attemptsLifetime`, so a clinician/admin split looks
 * inferable — and that is exactly the trap. The epoch rules behind those two
 * are subtle: a handover resets the round, the ticket's `escalatedAt` moves in
 * BOTH directions, and the attempts themselves come from two separate ledgers
 * (deal-scoped `DealNote` and the linked ticket's `CallbackNote`). Two screens
 * inferring that separately would eventually disagree about how hard we tried
 * to reach a patient, which is a clinical-contact record, not a cosmetic count.
 * One derivation lives in `DealsService.attemptStats`; this is a read of it.
 *
 * These PARTITION the lifetime total server-side (`clinicianAttempts +
 * adminAttempts === attemptsLifetime`, guard-tested there). Every attempt
 * counts whatever its outcome — a tally of calls MADE, not of the strikes that
 * park a ticket.
 *
 * ⚠️ ALL OPTIONAL, and that is the whole deploy story: clinic-service ships
 * this on a branch, the two services deploy independently, and a portal that
 * insisted on the fields would render a board of zeroes against today's dev
 * server. Absent means "this server does not split attempts" — the split
 * chrome hides and the card is exactly as it was, never "0 attempts made".
 */
export interface DealCard {
  /** Call attempts made while the CLINICIAN owned the deal (lifetime, not per-round). */
  clinicianAttempts?: number;
  /** Call attempts made since the deal was escalated to ADMIN (the `escalatedAt` epoch). */
  adminAttempts?: number;
  /**
   * The three-attempt requirement, ON THE WIRE so no screen hardcodes the
   * denominator of "n/3" (and so it moves on its own if it ever becomes
   * per-tenant).
   */
  attemptsRequired?: number;
  /**
   * Past the requirement — the card renders "5 attempts", never "5/3".
   * The SERVER answers the comparison so each chip does not re-invent it.
   */
  clinicianAttemptsExceeded?: boolean;
  adminAttemptsExceeded?: boolean;
}

/* ── Card triage marks (2026-09-05) ─────────────────────────────────────────
 *
 * [CONTRACTS GAP] — declared here so the board's rendering, and its tests, are
 * written against the fields the server must send rather than against a
 * client-side guess.
 *
 * ⚠️ THE STATUS LINE HERE HAS GONE STALE ONCE ALREADY. It read "UNLIKE the
 * split above, these two are NOT yet specified or implemented anywhere in
 * clinic-service … both marks are invisible in production until clinic-service
 * ships them", and `hasClinicalFlags` landed on 2026-09-06 while that sentence
 * stayed. Where each field actually stands is recorded against the field, at
 * the end of its own WHY below — one place per field, not a summary line that
 * has to be remembered separately. Today: `hasClinicalFlags` SHIPPED,
 * `untouched` still absent everywhere. See README "What the server owed this
 * board".
 *
 * WHY NEITHER IS DERIVED HERE, even though each looks close to something the
 * card already carries:
 *
 *  - `untouched` is not `attemptsLifetime === 0`, and not "stage is the entry
 *    stage". A deal can be worked hard — opened, called, noted — without
 *    reaching an approve or a reject, and a deal can be auto-moved between
 *    lanes by a server hook with no human having looked at it at all. Only the
 *    server's own review/verdict trail knows whether a person has ruled on
 *    this card, so a client-side approximation would mark genuinely-worked
 *    cards "New" and quietly drop the mark from cards nobody has opened. On a
 *    triage surface both errors send someone to the wrong patient first.
 *
 *  - `hasClinicalFlags` MUST be the same vector the review screen acts on
 *    (`AssessmentReview.flags`, the soft-stop/clinician-review flags the
 *    eligibility engine raises for conditions, medications and allergies). It
 *    lives on the per-assessment read, which the board never issues — the card
 *    carries `assessmentId`, not the assessment. Re-deriving "does this look
 *    flagged?" from `eligibility`/`assessmentState` would put a warning on the
 *    card that the review screen then contradicts, and at a glance the card is
 *    the one people trust.
 *
 *    ✅ SHIPPED 2026-09-06 and honoured exactly: clinic-service derives it in
 *    `toCards` as `clinicalFlagsOf(Assessment.flags).length > 0` — the review
 *    screen's own vector, minus q11 `help_me_choose` and legacy q14
 *    `photo_pending`, which are routing markers and not clinical findings. The
 *    engine files a row as `clinician_review` on ANY soft flag, so deriving the
 *    mark from `eligibility` was the shortcut this paragraph warned about, and
 *    a server-side test now pins that case. `untouched` above is still absent.
 */
export interface DealCard {
  /**
   * Nobody has ruled on this deal yet — no approve, no reject. The board reads
   * it as a "New" mark so an untouched patient is findable in a full lane.
   */
  untouched?: boolean;
  /**
   * The assessment behind this deal raised one or more soft-stop /
   * clinician-review flags.
   *
   * A BOOLEAN, DELIBERATELY, AND THE COUNT IS NOT WANTED HERE. Two reasons and
   * they point the same way. Operationally, "there is something to read"
   * is the entire board-level decision — the count changes nothing about
   * whether the card gets opened, and a number invites triage-by-magnitude
   * against flags that are not weighted. And in data-protection terms the
   * flag vector is answer-derived SPECIAL-CATEGORY health data (UK GDPR
   * Art. 9): clinic-service already refuses to put it on the patient-facing
   * Tier-1 read for that reason. A staff board is a different lawful basis, but
   * the minimising choice still holds — the existence of flags is what the lane
   * needs, and the flags themselves stay behind the click, on the review screen
   * where the clinician acts on them.
   */
  hasClinicalFlags?: boolean;
}

/* ── The DUPLICATE surface, both ends of it (2026-09-06, CTO — the Asana model)
 *
 * [CONTRACTS GAP] local mirrors of the clinic sibling's duplicate reads
 * (`feat/plan-link-audit-and-timestamp`). Appended as interface merges to keep
 * this module append-only (sibling agents edit it in parallel worktrees);
 * `DealStage` gained `closed_duplicate` IN PLACE above, because a union member
 * cannot ride a merge.
 *
 * ⚠️ THE WHOLE POINT IS THAT THE PAIR IS READABLE FROM EITHER END. A merged
 * card knows its survivor (`duplicateOfDealId`) and the survivor knows what was
 * folded into it (`mergedDuplicateDealIds` on the card, `mergedDuplicates` on
 * the detail). A one-way pointer is what the estate already had, and it put the
 * marker on the half of the pair nobody opens.
 *
 * ⚠️ AND THE MONEY DOES NOT GET QUIETER BECAUSE A CARD WAS MERGED.
 * `paymentReviewRequired` is the safety flag: a patient who submitted twice and
 * PAID twice has been double-charged. Marking a duplicate deliberately cascades
 * to NOTHING in orders (unlike the test mark, which takes the case off the
 * payments board), so the charge stays exactly where an admin can refund it —
 * and every surface that renders a duplicate has to keep saying so.
 *
 * ALL OPTIONAL on the read side, the established additive posture: the two
 * services deploy independently and a clinic-service that predates the feature
 * omits every key. Absent means "this server does not report duplicates" —
 * never "this card is not a duplicate", which is why each read below is an
 * explicit `!= null` / `=== true` rather than a truthiness check.
 */

/** Interface merge: the duplicate facts the BOARD card carries. */
export interface DealCard {
  /**
   * The canonical deal this card was merged INTO, or null when it is not a
   * duplicate. Non-null wherever a merged card is served at all (its own detail
   * read, the `closed_duplicate` lane, the `?includeDuplicates=true` view); the
   * working lanes never receive one, because the server hides duplicates there.
   *
   * THE SERVER STATES THE FACT AND THE PORTAL WRITES THE SENTENCE (R41): the
   * `closed_duplicate` stage says the card was merged away, this id says into
   * which, and the "Closed and merged into …" banner and its link are both
   * rendered from the pair. There is no server-side label to keep in step.
   */
  duplicateOfDealId?: string | null;
  /**
   * THE OTHER DIRECTION — the ids of the deals merged INTO this card. Empty on
   * the overwhelming majority, which are nobody's canonical.
   *
   * IDS ONLY, and deliberately no nested cards (the server would recurse). The
   * board uses it for the "n merged in" mark; the DRAWER wants the rows, and
   * gets them from `DealDetail.mergedDuplicates` below.
   */
  mergedDuplicateDealIds?: string[];
  /**
   * ⚠️ THIS DUPLICATE CARRIES A REAL CHARGE. Server-derived: the card is a
   * duplicate AND has an `orderCaseRef`. Hiding the duplicate from the pipeline
   * is the point of the feature; hiding the MONEY would be the bug.
   */
  paymentReviewRequired?: boolean;
}

/**
 * POST /v1/clinic/deals/:id/mark-duplicate — what the mark and the un-mark
 * both answer with. `[CONTRACTS GAP]` — a local mirror of clinic-service's
 * strict `dealMarkDuplicateResult`.
 *
 * ⚠️ `payment` IS THE POINT OF THE RESPONSE, not decoration and not a
 * convenience copy of the card's flag. The operator has just hidden a card that
 * may have a REAL CHARGE behind it, and the charge is still live because
 * marking a duplicate deliberately cascades to nothing in orders.
 * `reviewRequired` says go and look; `orderCaseRef` says where (it is the
 * reference the Payments board is searched by). Both are false/null for the
 * overwhelmingly common duplicate that never reached checkout, and the block is
 * present in BOTH directions so an un-mark can say the review is no longer
 * needed.
 */
export interface DealMarkDuplicateResult {
  deal: DealCard;
  payment: {
    reviewRequired: boolean;
    orderCaseRef: string | null;
  };
}

/** Interface merge: a sibling row can now say it was merged, and into what. */
export interface DealSibling {
  /**
   * The canonical this sibling was merged into, or null when it is not a
   * duplicate. `otherDeals` is the ONE list clinic-service deliberately does
   * not filter duplicates out of, so without this the History tab renders a
   * `closed_duplicate` row that says a card was merged and cannot say where to.
   */
  duplicateOfDealId?: string | null;
}

/**
 * One deal that was MERGED INTO the deal being read — the canonical side of the
 * pair, as a renderable row rather than a bare id.
 *
 * A SEPARATELY DECLARED interface, which is what lets the `DealDetail` merge
 * below carry it (an inline nested object type could not ride a merge).
 *
 * NO CONTACT BLOCK, and that is the server's decision, not an omission: a
 * merged duplicate is by definition the same person as the canonical, whose
 * contact details are already on the card above it (R41 data minimisation). The
 * drawer therefore identifies these rows by their journey — when it started,
 * how it entered, where it ended — never by re-printing the patient.
 */
export interface DealMergedDuplicate {
  dealId: string;
  /**
   * READ FROM THE ROW, never assumed to be `closed_duplicate`: an earlier
   * release left marked cards sitting on their working lane, and asserting the
   * terminal stage here would tell an operator those had been closed when they
   * had not.
   */
  stage: DealStage;
  entryPath: DealEntryPath;
  assessmentId: string | null;
  /** When the DUPLICATE journey started — not when it was merged. */
  createdAt: string;
  /**
   * When staff pointed it at this card. NULLABLE for real: a pointer set before
   * the column existed has the association and no stamp, and null is the honest
   * answer rather than borrowing `createdAt`.
   */
  mergedAt: string | null;
  /**
   * Opaque principal id — the portal resolves the display name through the
   * ADMIN-only batched staff lookup, exactly as the audit feed does, and shows
   * nothing rather than a raw UUID when it cannot.
   *
   * NO ROLE COMES WITH IT, and none may be invented here: the columns record
   * only a user id, and deriving a role from that principal's role TODAY would
   * mislabel a clinician who has since become an admin. The role at the moment
   * of marking lives on the audit row.
   */
  mergedByUserId: string | null;
  /** ⚠️ This duplicate carries an order: very likely a double charge. */
  paymentReviewRequired: boolean;
}

/**
 * Interface merge: the canonical's own list of what was folded into it.
 *
 * OLDEST MERGE FIRST — the same order as `dealCard.mergedDuplicateDealIds`, so
 * the board mark and the drawer list enumerate one sequence. An EMPTY ARRAY
 * (never null) on the overwhelming majority; ABSENT on a clinic-service that
 * predates the read, which is why every consumer uses `?? []` rather than
 * treating the key as guaranteed.
 */
export interface DealDetail {
  mergedDuplicates?: DealMergedDuplicate[];
}

/* ── The mark-test CASCADE (2026-09-06) ─────────────────────────────────────
 *
 * ONE PRESS, THREE RECORDS: the deal, the plan behind it and the patient behind
 * it. clinic-service has returned `{deal, cascade}` from
 * `POST /v1/clinic/deals/:id/mark-test` since 2026-09-04 and its controller
 * docblock is headed "WHAT THE PORTAL MUST RENDER" — but `markDealTest` was
 * typed `Promise<DealCard>` and the drawer threw the body away, so every
 * outcome below rendered as an unqualified success. The API's "never silent"
 * guarantee was true of the wire and false of the product: an operator could
 * walk away from a HALF-MARKED record believing it was clean, which is the
 * exact failure the six outcomes were invented to make impossible.
 */

/**
 * What ONE downstream leg did. SIX outcomes, and the distinctions are the
 * whole point — five of them used to be three, and the collapsing is what hid
 * two real defects.
 *
 *  - `done`     — the downstream service confirmed the state. Nothing to say.
 *  - `skipped`  — there was genuinely NOTHING on that side: no order case, no
 *                 journey, no account. Not a failure; nothing is left to do.
 *  - `withheld` — there WAS something and we deliberately did not mark it.
 *                 ⚠️ NOT AN ERROR AND NOT A NOTHING. Today the one case is the
 *                 patient leg: `User.isTest` is a claim about a HUMAN and this
 *                 press is a decision about ONE JOURNEY, so the human is only
 *                 flagged when every OTHER deal of theirs agrees. Render the
 *                 sentence; never render it red.
 *  - `stub`     — the seam is unconfigured, so no call was made. On a deployed
 *                 environment that is a mis-set service URL, not a success.
 *  - `anomaly`  — the leg's target could not be resolved and that is abnormal
 *                 (a dangling or cross-tenant pointer). Outstanding work.
 *  - `failed`   — the call was made and did not succeed. Downstream state is
 *                 UNKNOWN and the record stays half-marked until a re-run.
 */
export type DealTestCascadeOutcome = 'done' | 'skipped' | 'withheld' | 'stub' | 'anomaly' | 'failed';

/** One leg of the cascade — what happened, and what to tell the operator. */
export interface DealTestCascadeLeg {
  outcome: DealTestCascadeOutcome;
  /**
   * THE OPERATOR SENTENCE, ALREADY WRITTEN AND ALREADY CORRECTLY PLURALISED —
   * present for `failed`, `withheld`, `anomaly` and `stub`, null otherwise.
   *
   * ⚠️ RENDER IT VERBATIM. The server's contract is explicit that it is a whole
   * sentence "ready to render as-is: the portal must never have to parse a
   * count back out of it or repair its grammar". Ids and counts only — it is
   * PHI-free by construction (R19), which is what makes rendering it safe.
   */
  detail: string | null;
  /**
   * THE CARDS THE `withheld` SENTENCE IS TALKING ABOUT. Empty on every other
   * outcome, and empty is the honest default on an older clinic-service.
   *
   * WHY THEY MATTER MORE THAN THEY LOOK: the sentence ends in an instruction —
   * "mark those deals too, then re-run this" — and the operator has no other
   * route to them. This leg joins on `patientUserId` (the authenticated human)
   * while the drawer's `otherDeals` joins on `contactId`, and a patient who
   * submitted twice under two email addresses is the ordinary case here. Turn
   * these into links or the instruction is a dead end and the record stays
   * half-marked.
   *
   * Opaque uuids, the same posture as `mergedDuplicateDealIds`: no name, no
   * email, no stage, no clinical content.
   */
  relatedDealIds?: string[];
}

/**
 * `complete` means NOTHING IS OUTSTANDING — which is NOT "no leg failed".
 * `skipped` and `withheld` are complete (one had nothing to do, the other
 * deliberately did nothing); `failed`, `anomaly`, and `stub` on a deployed
 * runtime are not. The server owns that judgement, so the portal reads the
 * flag and never re-derives it from the outcomes.
 */
export interface DealTestCascade {
  complete: boolean;
  /** OrderCase.isTest — "this MONEY was not real money". */
  plan: DealTestCascadeLeg;
  /** User.isTest — "this HUMAN does not exist". */
  patient: DealTestCascadeLeg;
}

/**
 * The mark-test response. `deal` is authoritative and ALWAYS committed by the
 * time this arrives — the deal write is the transaction and the two legs are
 * post-commit, which is why a `complete: false` is still a 200 and why the
 * report must lead with "the deal is marked" rather than with the failure.
 *
 * ⚠️ `cascade` IS OPTIONAL HERE AND MUST STAY OPTIONAL. A clinic-service that
 * predates 2026-09-04 answers with a bare card, and a `.cascade.plan` on that
 * response is a TypeError inside the success handler of an action that has
 * already succeeded — the operator would see a crash for a mark that worked.
 * Absent means "this server cannot tell us", which is exactly the pre-cascade
 * behaviour and is reported as nothing, never as a clean cascade.
 */
export interface DealMarkTestResult {
  deal: DealCard;
  cascade?: DealTestCascade;
}

/* ── The warm-lead calling lane (2026-09-08, CTO) ────────────────────────────
 *
 * [CONTRACTS GAP] Local mirrors of the clinic-service additions
 * (`leadCallRequest` / `leadCallResult` in deal.schemas.ts). The pinned
 * contracts tag has no lead-call shapes, so these are local per the established
 * gap pattern. Appended as their own interfaces rather than merged, to keep this
 * module append-only for the sibling agents editing it in parallel worktrees.
 *
 * TODO(CTO): promote leadCallRequest/leadCallResult into gpsclinics-contracts.
 */

/**
 * What the PERSON said on a lead call — a different axis from
 * `CallAttemptOutcome`, which says what the PHONE did. Neither derives from the
 * other: `answered` + interested and `answered` + not_interested are the same
 * phone call and opposite business facts.
 */
export type LeadCallDisposition = 'interested' | 'not_interested' | 'no_answer';

/**
 * POST /v1/clinic/deals/:id/lead-call body.
 *
 * ⚠️ THE SERVER OWNS THE CROSS-FIELD RULE (R41 — the UI is never the control).
 * A non-contact outcome may carry ONLY `no_answer`; a contact outcome may not
 * carry it; `reason` is required for and accepted only on `not_interested`.
 * The dialog mirrors all three for AFFORDANCE — so a desk is not offered a
 * combination that will bounce — and the 400 is still the truth.
 */
export interface LeadCallRequest {
  outcome: CallAttemptOutcome;
  disposition: LeadCallDisposition;
  /** Optional, unlike the activity composer's body: outcome + disposition already say it. */
  note?: string;
  /** REQUIRED for `not_interested` — the close reason on the record. */
  reason?: string;
}

/**
 * The lead-call response — what was recorded, and what the operator does next.
 *
 * ⚠️ `nextStep` IS AN INSTRUCTION TO THIS SURFACE, NOT A DESCRIPTION OF A STAGE
 * MOVE. `'send_plan_link'` means the DEAL WAS NOT TOUCHED and the patient is
 * waiting for their link — render PlanLinkActions and let the operator send it.
 * `'closed'` means the deal IS `closed_lost`. `'none'` means nothing moved.
 *
 * `assessmentId` is non-null only on `'send_plan_link'`, and only when the deal
 * has a questionnaire behind it — a staff-created SQL card has no `#resume=`
 * link to offer, and the dialog must say so rather than render a dead control.
 */
export interface LeadCallResult {
  activityId: string;
  /** The stage AFTER the call — unchanged except on `'closed'`. */
  stage: DealStage;
  disposition: LeadCallDisposition;
  outcome: CallAttemptOutcome;
  /** §9 — this call's 1-based position in the active round (SERVER-counted). */
  attempt: number;
  /** REQUIRED_ROUND_ATTEMPTS, echoed so this surface never hard-codes 3. */
  attemptsRequired: number;
  /** attempt >= attemptsRequired. ADVISORY — nothing parks automatically. */
  roundExhausted: boolean;
  nextStep: 'send_plan_link' | 'closed' | 'none';
  assessmentId: string | null;
}

/* ── THE CARD-ON-FILE FACT (2026-09-07) ──────────────────────────────────────
 *
 * [CONTRACTS GAP] local mirror of the clinic sibling's `deal-payment-posture.ts`
 * (feat/card-on-file-seam), which is itself a repo-local mirror of
 * orders-service's `payment-posture` projection. Nothing here rides the pinned
 * contracts package: the shape is deliberately owned by the two services that
 * speak it, and this is the third reader.
 *
 * WHAT IT ANSWERS: can this patient be CHARGED, or do they need a link? Until
 * this seam existed no payment fact crossed from orders-service into the
 * pipeline at all — a deal card carried `orderCaseRef`, `planSent` and chase
 * counts, so a clinician approving a plan could not tell whether the money was
 * already committed. The Patient journey panel is the surface that renders it.
 *
 * ⚠️ THREE DIFFERENT ABSENCES, AND CONFUSING THEM IS THE BUG. This is copied
 * from the sibling's own docblock because a portal reader must not have to open
 * another repo to get it right:
 *   ABSENT (`undefined`)  a clinic-service that predates the seam. The two
 *                         services deploy independently, so the panel has to
 *                         render without the posture rather than crash.
 *   `null`                THIS RESPONSE DID NOT ASK. Every mutation echo — a
 *                         stage patch, a handover, a mark — returns the card
 *                         with a null posture on purpose (those run inside the
 *                         write transaction and an HTTP hop inside `withTenant`
 *                         is a P2028). A screen holding a card from a mutation
 *                         must KEEP the posture it already had, or render
 *                         nothing — never "no card".
 *   `{state:'unknown'}`   WE ASKED AND COULD NOT LEARN — orders-service was
 *                         unreachable, slow or unconfigured. A transport fact,
 *                         not a payment fact.
 * ⚠️ NEVER render or route `unknown` as "no card". A patient we could already
 * have charged would be sent a payment link, and one we could not would be
 * promised a charge that 409s.
 *
 * ⚠️ MONEY IS ROLE-SHAPED (Reshape §3.1 — "clinicians never see money"). The
 * pence fields exist ONLY on the ADMIN projection: orders-service decides the
 * shape from the forwarded staff principal and clinic-service re-checks it with
 * a `.strict()` clinician schema that REJECTS a pence key. This mirror keeps
 * them OPTIONAL rather than modelling the union, and every consumer must gate on
 * `role === 'ADMIN'` as well as on presence — `clinicians/clinicians.spec.tsx`
 * sweeps the whole clinician drawer for `£ / pence / price`.
 *
 * R19: ids, enums, integers and booleans. No card data, no PAN, NO LAST4 and no
 * Stripe identifier of any kind — the saved-card fact travels as the single
 * word `card_on_file`.
 */

/**
 * THE LADDER, lowest rung to highest. The first six are orders-service's own
 * `paymentPostureState`; `unknown` is clinic-service's and is a TRANSPORT fact.
 *
 *  · `no_order`         — no order case for this assessment at all. The
 *                         ORDINARY state before checkout: the assessment is
 *                         submitted for clinical review BEFORE the card form, so
 *                         a patient reaches a clinician whether or not they ever
 *                         open it.
 *  · `plan_only`        — a case exists; the card form was never opened.
 *  · `checkout_started` — a SetupIntent exists; no saved card, nothing held.
 *  · `card_on_file`     — a card is saved; NOTHING AUTHORISED.
 *  · `authorised`       — a `month1_preauth` is HELD; an approval can capture.
 *  · `captured`         — month 1 is already taken.
 *  · `unknown`          — we asked and could not learn.
 *
 * ⚠️ `card_on_file` IS NOT AN AUTHORISATION and the two rungs are adjacent.
 * `OrdersService.approve()` requires a `month1_preauth` in `held`/`captured` and
 * throws otherwise, so collapsing them puts a 409 in front of a clinician.
 */
export type DealPaymentPostureState =
  | 'no_order'
  | 'plan_only'
  | 'checkout_started'
  | 'card_on_file'
  | 'authorised'
  | 'captured'
  | 'unknown';

/**
 * One deal's payment posture, as the pipeline sees it.
 *
 * DERIVED, NEVER STORED — clinic-service resolves it live from orders-service on
 * the two READ boundaries only. There is no column, because the posture changes
 * on a SetupIntent mint, a saved-card webhook, a hold-release cron and a refund,
 * and orders-service emits a domain event for none of them.
 */
export interface DealPaymentPosture {
  state: DealPaymentPostureState;
  /**
   * The order case this posture describes.
   *
   * ⚠️ NOT the same fact as `DealCard.orderCaseRef`, and the difference is
   * informative rather than a fault: this one is read live from the service that
   * owns it, that one is stamped only by the order-sync. A value HERE beside a
   * null THERE is the ordinary pre-checkout state — 0 of 36 `plan_sent` deals on
   * production carry an `orderCaseRef`.
   */
  orderCaseRef: string | null;
  /** The human `ORD-…` reference staff quote. Null on older rows — fall back to `orderCaseRef`. */
  orderReference: string | null;
  /**
   * The order's own lifecycle state — CONTEXT, NOT THE ROUTING FACT. A plain
   * string, not a pinned enum: it is orders-service's vocabulary and a state its
   * newer deploy knows must not fail a render.
   *
   * ⚠️ A `rejected` / `cancelled` case must not be routed as chargeable even
   * when the ladder says `card_on_file` — see `isTerminalOrderState`.
   */
  orderState: string | null;
  /** The medication KEY, never brand prose. */
  productKey: string | null;
  /** The committed term in months. */
  months: number | null;
  /**
   * ⚠️ THE SAFETY CATCH. Money was held or taken for month 1 and is NO LONGER
   * STANDING. The ladder FALLS BACK on a refund — a refunded case reads
   * `card_on_file` — so without this a patient we charged and repaid is
   * indistinguishable from one who never paid, and the difference is whether
   * approving them again is a second charge. On production today EVERY
   * `month1_preauth` is `refunded` and none is `held`.
   */
  priorMonth1: 'released' | 'refunded' | null;
  /**
   * The ORDER's test-record flag — orders-service's, not this deal's. Kept in
   * step by the test-mark cascade but a separate row, so a disagreement is worth
   * being able to see. Null on `unknown`.
   */
  isTest: boolean | null;
  /**
   * ⚠️ ADMIN PROJECTION ONLY — what is actually HELD or TAKEN (the
   * "£188.65 authorised" figure). Present only on `authorised`/`captured`, and
   * the key does not exist at all on a clinician's row.
   */
  authorisedPence?: number | null;
  /** ⚠️ ADMIN PROJECTION ONLY — what a charge WOULD be. Two claims, two fields. */
  monthlyPence?: number | null;
  /**
   * [CONTRACTS GAP] Whether a PLAN is live behind this order (orders S4.4,
   * money-free; clinic #224 accepts it). OPTIONAL, and ABSENCE IS "NOT KNOWN",
   * never "no": orders OMITS it when a capped read could not prove a "no"
   * (plan Addendum 8), and an orders build without S4.4 never sends it. Nothing
   * here renders it directly — the board's "Already on an active plan" /
   * "Plan status unknown" chips are clinic's `returning.chips`, derived from
   * exactly this — so the field is mirrored only so the shape stays honest.
   */
  planLive?: boolean;
  /**
   * [CONTRACTS GAP] ⚠️ ADMIN PROJECTION ONLY (orders S4.4): the running plan's
   * subscription — for a re-subscribed patient, the SUCCESSOR's (the one the
   * renewal engine bills). Absent from a clinician's row by the server's strict
   * shape. Reserved for S6.2's "Cancel current plan — Change treatment", which
   * cancels by THIS id and never by `orderCaseRef` (Addendum 8). Not rendered
   * by S4.5.
   */
  liveSubscriptionId?: string | null;
}

/*
 * ⚠️ THE LADDER'S TWO HELPERS ARE NOT HERE. `CHARGEABLE_POSTURES` and
 * `isChargeablePosture()` live in `pipeline/paymentPosture.ts`, because THIS
 * MODULE IS TYPES ONLY — every one of its ~4,300 lines is an `interface`, a
 * `type` or a docblock, and dozens of call sites import from it with
 * `import type`, which erases at compile time and would take a runtime const
 * with it. A helper hidden in a type module is a helper that stops existing the
 * first time somebody tightens an import.
 */

/**
 * Interface merge: the posture on the board card and therefore on the drawer's
 * `dealDetail.deal`.
 *
 * OPTIONAL AND NULLABLE, and the two mean different things (see the block
 * above). The sibling always SENDS the key on the two read boundaries, but the
 * portal deploys independently of it, so `undefined` is a real state this
 * surface has to render through.
 */
export interface DealCard {
  paymentPosture?: DealPaymentPosture | null;
}

/* ── Marketing consent (2026-09-11) — [CONTRACTS GAP] local mirrors of the
 * clinic-service marketing-consent routes (contract: "Marketing consent +
 * unsubscribe — the cross-repo contract", 2026-09-11). Consent to contact the
 * patient by EMAIL and by WHATSAPP, one boolean per channel, stored with the
 * wording version and the source. WhatsApp SENDING is out of scope — this is
 * the consent only. ───────────────────────────────────────────────────────── */

/** The two channels a patient can consent to. */
export type MarketingChannel = 'email' | 'whatsapp';

/**
 * Where a ledger row came from. Stamped SERVER-side — the portal never sends it.
 * Mirrors clinic-service `marketing.schemas.ts` `CONSENT_SOURCES`, in its order.
 *
 * `journey_consent_step` = the questionnaire's consent step (q15's "Keeping in
 * touch" block) — the tick is held in the browser and recorded by the one
 * post-submit grant write (`marketing/journeyConsent.ts`), so the row's time is
 * that write, seconds after the tick. Everything the guest route has stamped
 * since the 2026-09-23 one-flow move.
 *
 * `journey_result` = HISTORICAL: the eligible Result screen's card, where the
 * boxes lived until 2026-09-23. Nothing new is stamped with it, but the ledger is
 * append-only and those rows keep it forever, so it stays in this union — and
 * its label stays "Result screen", which is still the truth about every row that
 * carries it. Never re-spell or remove a value; stored rows cannot follow.
 *
 * `preferences_page` = /preferences; `unsubscribe_link` = the button on the
 * inert unsubscribe page; `unsubscribe_one_click` = a mail provider's RFC 8058
 * one-click POST; `staff` = a withdrawal recorded from the deal drawer.
 *
 * The source is half of the UK GDPR Art 7(1) record — WHERE the person was
 * asked — which is why the move got a new value rather than re-using the old
 * one: a grant stamped `journey_result` would claim a screen the patient never
 * saw the box on.
 */
export type ConsentSource =
  | 'journey_consent_step'
  | 'journey_result'
  /**
   * The Step-0 mini form's own box (clinic #218, 2026-09-23) — grant-only by
   * shape. In clinic-service's CONSENT_SOURCES since then, and missing from this
   * mirror until S4.5 (2026-09-26): a row carrying it read "Source not
   * recognised" on the drawer. The portal's own Step-0 box is still behind
   * `VITE_STEP0_CONSENT_ENABLED` (off everywhere), but the server lane is live,
   * so the label has to exist whatever the flag says.
   */
  | 'lead_capture'
  | 'preferences_page'
  | 'unsubscribe_link'
  | 'unsubscribe_one_click'
  | 'staff';

/**
 * The patient-facing state (the guest assessment lane's read + grant echo, and
 * the /preferences page) — the person's OWN current choice. `whatsappAvailable` =
 * the phone on the lead is a UK mobile; asking for WhatsApp without one is
 * recorded as NOT granted (not an error), and the response says so.
 *
 * On the guest lane's READ (`getMarketingConsent` — answered for a draft since
 * clinic-service `fix/consent-step-source`) the questionnaire uses
 * `whatsappAvailable` ALONE, to decide whether to offer the WhatsApp box; the
 * stored `email` / `whatsapp` never pre-tick anything there.
 */
export interface MarketingConsentState {
  email: boolean;
  whatsapp: boolean;
  whatsappAvailable: boolean;
}

/**
 * The channels a consent write NAMES — at least one, and only the ones the
 * surface SHOWED and the person CHANGED (`marketing/consentChannels.ts` decides
 * which). A channel that is ABSENT is left exactly as it is server-side, which is
 * what stops a hidden box recording a withdrawal nobody made.
 *
 * ⚠️ NOT "the full state, every time", which is what this was until 2026-09-11.
 * The union makes "neither channel" unrepresentable here, exactly as
 * clinic-service's schema makes it a 400 (`namesAChannel`).
 */
export type MarketingConsentChannels = { email: boolean; whatsapp?: boolean } | { email?: boolean; whatsapp: boolean };

/**
 * POST /v1/clinic/assessments/:id/marketing-consent (guest) — since 2026-09-23
 * the one post-submit grant for the tick made on the questionnaire's consent
 * step; clinic-service stamps it `journey_consent_step`.
 * `wordingVersion` names the words the patient was shown
 * (`marketing/consentWording.ts`); an unknown id is a 400.
 */
export type GuestMarketingConsentWrite = MarketingConsentChannels & { clinicSlug: string; wordingVersion: string };

/** POST /v1/clinic/marketing/preferences (public, the emailed link's token in the BODY). */
export type MarketingPreferencesWrite = MarketingConsentChannels & { token: string; wordingVersion: string };

/**
 * One channel as STAFF see it. `changedAt`/`source`/`wordingVersion` are null for
 * a channel that has never had a ledger row — "never asked or never answered",
 * which the drawer renders as "Not given".
 */
export interface MarketingChannelState {
  granted: boolean;
  changedAt: string | null;
  wordingVersion: string | null;
  source: ConsentSource | null;
}

/**
 * What one ledger row records (clinic S4.3, 2026-09-25 — `CONSENT_EVENT_KINDS`):
 * `change` = the state moved (a grant of a channel that was not granted, or a
 * withdrawal); `reaffirmed` = the patient RE-TICKED a channel that was already
 * granted — a dated "Re-confirmed" entry (CTO Q10). Additive only server-side.
 */
export type ConsentEventKind = 'change' | 'reaffirmed';

/** One ledger row. The server sends them NEWEST FIRST, at most 50. */
export interface MarketingConsentHistoryRow {
  channel: MarketingChannel;
  granted: boolean;
  source: ConsentSource;
  wordingVersion: string | null;
  occurredAt: string;
  /**
   * [CONTRACTS GAP] S4.3 (clinic #226). OPTIONAL: a clinic-service that predates
   * S4.3 omits it, and every row it sends is a state change — so ABSENT reads as
   * `change`, never as a re-confirmation nobody made.
   */
  kind?: ConsentEventKind;
  /**
   * [CONTRACTS GAP] S4.3 — ADMIN SHAPE ONLY (CTO Q11: "admins see which staff
   * member did it"). The opaque principal of a STAFF withdrawal; null on every
   * patient, link and unsubscribe row. The key does not exist on a CLINICIAN's
   * row (clinic's strict clinician shape), and a `svc:` machine id is never
   * looked up or shown as a person (`lookupStaffUsers` strips it at the
   * boundary; the section shows no name for it either).
   */
  actorUserId?: string | null;
}

/** GET /v1/clinic/deals/:id/marketing-consent (ADMIN, CLINICIAN) — and the withdraw echo. */
export interface StaffMarketingConsent {
  email: MarketingChannelState;
  whatsapp: MarketingChannelState;
  whatsappAvailable: boolean;
  history: MarketingConsentHistoryRow[];
}

/**
 * POST /v1/clinic/deals/:id/marketing-consent/withdraw. Staff can WITHDRAW and
 * never GRANT — consent has to come from the person — so there is no `granted`
 * field to send. `reason` is 3..500 characters and lands on the AuditEntry.
 */
export interface MarketingConsentWithdrawRequest {
  channel: MarketingChannel;
  reason: string;
}

/* ── Engagement (2026-09-23, deal-drawer Engagement tab) ──────────────────────
 *
 * [CONTRACTS GAP] Hand-written mirrors of the PINNED engagement wire contract
 * (2026-09-23 — clinic-service routes; staff lanes ride the gateway authed
 * catch-all as `/v1/clinic/…`). No contracts package pins these yet, so the
 * mirror is the contract on this side — `testing/engagementWire.ts` re-states
 * it as strict zod and every fixture is parsed through that first, per the
 * 2026-09-13 rule that a fixture must not describe a payload no server can
 * send.
 */

/** The re-engagement email sequence's lifecycle on one deal. */
export type EngagementSequenceStatus = 'active' | 'stopped' | 'completed' | 'exhausted';

/**
 * One automated step of the sequence. `pending` is a step still on the clock
 * (`settledAt` null); everything else is settled and belongs on the timeline.
 * The union is what the pinned contract names today — an unknown future status
 * degrades to its raw token in the UI (the KIND_LABEL Record idiom), never to a
 * crash and never to a claim.
 */
export type EngagementStepStatus = 'pending' | 'sending' | 'sent' | 'failed' | 'skipped' | 'abandoned';

export interface EngagementStep {
  /** The template this step sends — `day_<n>` today; the day label derives from it. */
  templateKey: string;
  /** Days after enrolment this step is due — the honest fallback for the label. */
  dayOffset: number;
  dueAt: string;
  status: EngagementStepStatus;
  settledAt: string | null;
  outcome: string | null;
}

export interface EngagementEnrolment {
  status: EngagementSequenceStatus;
  /** Why a `stopped` sequence stopped — a PHI-free token, humanised for display. */
  stopReason: string | null;
  enrolledAt: string;
  /** 8 on today's wire; render it, never hard-code an 8. */
  stepsTotal: number;
  steps: EngagementStep[];
}

/** A manual record: staff sent content, or logged a WhatsApp send. */
export type EngagementTouchKind = 'content_sent' | 'whatsapp_logged';

export interface EngagementTouch {
  id: string;
  kind: EngagementTouchKind;
  contentId: string | null;
  /** The content's title at the time — display-ready, never re-fetched. */
  title: string | null;
  note: string | null;
  actorUserId: string;
  actorRole: string;
  at: string;
}

/**
 * The deal's unlock-link row, WITHOUT the linkId — the id is a bearer secret
 * hashed at rest server-side and this read deliberately never carries it.
 */
export interface EngagementLinkState {
  expiresAt: string;
  revokedAt: string | null;
  lastRefreshedAt: string;
}

/** GET /v1/clinic/deals/:id/engagement (CLINICIAN + ADMIN read). */
export interface DealEngagement {
  enrolment: EngagementEnrolment | null;
  /** Manual records, newest first (the automated steps live on `enrolment`). */
  touches: EngagementTouch[];
  link: EngagementLinkState | null;
  /** Drives the send affordance only — the server re-checks on every send. */
  consent: { email: boolean };
  /**
   * The guide-email switch (clinic ENGAGEMENT_CONTENT_SEND_ENABLED, default
   * OFF) as the serving revision reads it. Display only: while it is not
   * `true` the "Send content…" button is off and says why, and the server
   * refuses the send anyway (409). Read with `=== true`, so a clinic that
   * predates the field shows the button as off rather than inviting a send.
   */
  contentEmailEnabled: boolean;
}

/** POST …/engagement/send → the send outcome; `stub` = no mail transport here. */
export interface EngagementSendResult {
  outcome: 'sent' | 'stub';
}

/**
 * POST …/engagement/whatsapp-message → the copy payload: the content's short
 * message plus the deal's unlock link (minted-or-refreshed, 90 days). The
 * browser copies `${message}\n${url}` and NOTHING here is a send.
 */
export interface EngagementWhatsAppMessage {
  message: string;
  url: string;
}

/**
 * POST …/engagement/whatsapp-log → 201, and the body is the LITERAL `{}` —
 * verified against clinic-service `feat/engagement-unlock` 2026-09-23. The
 * recorded touch rides the NEXT engagement read, never this response, so
 * nothing may reach for a `.touch` here (the panel refreshes by re-reading).
 */
export type EngagementWhatsAppLogged = Record<string, never>;

/**
 * POST …/engagement/revoke-links (ADMIN) → 201 — the wrong-chat kill switch:
 * every live unlock link on the deal is stamped revoked in one sweep and the
 * next send mints fresh. `revoked` is the count stamped by THIS call, so a
 * repeat answers 0 — which is the idempotent steady state, not a failure.
 */
export interface EngagementRevokeResult {
  revoked: number;
}

export type EngagementContentKind = 'tips' | 'meal_plan' | 'other';
export type EngagementContentStatus = 'published' | 'draft' | 'retired';

/** One piece of the ADMIN content library (GET /v1/clinic/engagement/content). */
export interface EngagementContentItem {
  id: string;
  title: string;
  /** The WhatsApp-ready one-liner the copy payload leads with. */
  shortMessage: string;
  kind: EngagementContentKind;
  status: EngagementContentStatus;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface EngagementContentList {
  items: EngagementContentItem[];
}

/**
 * POST /v1/clinic/engagement/content — the ADMIN library upload's create body.
 * `contentType` is pinned to the one type the storage seam signs for this lane
 * (the PDF guides), and `sizeBytes` is the picked file's size — the server
 * binds content-type + a 1B–10MB range into the V4 signature it answers with.
 */
export interface EngagementContentCreate {
  /** 3..120 — the server's zod bounds; the dialog's field stops at them. */
  title: string;
  /** 3..300 — the WhatsApp one-liner patients may read. */
  shortMessage: string;
  kind: EngagementContentKind;
  contentType: 'application/pdf';
  sizeBytes: number;
}

/**
 * The create's 201: the new draft row's id plus the signed GCS PUT URL
 * (15 minutes). `uploadUrl: null` is SERVER STUB MODE — no storage bucket on
 * this environment. The row still exists as a draft; publish will refuse until
 * an object lands, and the dialog says the upload needs a configured bucket.
 */
export interface EngagementContentCreateResult {
  contentId: string;
  uploadUrl: string | null;
  /**
   * The signed grant's own byte ceiling — the PUT's
   * `x-goog-content-length-range` echoes THIS value. `null` in server stub
   * mode (no PUT was signed); optional only for a clinic build predating the
   * field, where the mirrored `ENGAGEMENT_PDF_MAX_BYTES` stands in.
   */
  maxBytes?: number | null;
}

/**
 * PATCH /v1/clinic/engagement/content/:id — edit the words, or move the
 * status one way. An EMPTY `{}` body is a 400 (the server's nothing-to-change
 * refine). `publish: true` 409s until the PDF object exists (the OBJECT gate
 * runs FIRST), and 409s on a retired row whose object landed (retired never
 * re-publishes); a title/shortMessage naming a medicine or treatment 400s
 * (the server's R28 sweep — the refused term is never echoed back) — every
 * refusal is a sentence the surface shows VERBATIM.
 */
export interface EngagementContentPatch {
  title?: string;
  shortMessage?: string;
  publish?: true;
  retire?: true;
}

/** The PATCH's 200 — the BARE row as the server now holds it (never `{item}`). */
export type EngagementContentUpdated = EngagementContentItem;

/* ── Unlock (2026-09-23, the PATIENT side of engagement) ──────────────────────
 *
 * [CONTRACTS GAP] Same pinned wire contract as the staff shapes above — the
 * two PUBLIC token lanes (resolve/download), the resume-grant exchange the
 * addendum adds, and the addendum-2 self-scoped `mine` pair. Restated as
 * strict zod in `testing/engagementWire.ts`; every fixture parses through
 * that first.
 *
 * NOTE the public POST lanes answer 201 (Nest's POST default) — clients gate
 * on `res.ok`, never a literal status (the contract's own note). The shared
 * client already does.
 */

/**
 * What one unlock link (or the signed-in patient's own read) is in a position
 * to offer. Server-authoritative, derived in ONE place server-side — the
 * portal renders it and re-derives nothing.
 */
export type UnlockState = 'needs_questionnaire' | 'waiting' | 'needs_plan' | 'unlocked' | 'lapsed' | 'none';

/** One piece of content behind an unlock link — R28-constrained title, display-ready. */
export interface UnlockItem {
  contentId: string;
  title: string;
  kind: EngagementContentKind;
}

/**
 * POST /v1/clinic/engagement/resolve {token}. `nextUrl` takes a different
 * shape per state, and only two states carry one: `needs_plan` is the ONE
 * grant-shaped state — a fresh short-TTL grant minted per click, riding the
 * `/weightloss#resume=<grant>` FRAGMENT, never a raw assessment id and never
 * a query string; `needs_questionnaire` is a PLAIN restart URL
 * (`/weightloss?src=unlock` — a query string of provenance, no fragment, no
 * grant); `lapsed` and every other state carry null (an ended plan has no
 * lane to hand into). EVERY failure is one generic 404
 * `{message:'Not found'}`, byte-identical and non-enumerating.
 */
export interface UnlockResolveResult {
  state: UnlockState;
  nextUrl: string | null;
  items: UnlockItem[];
}

/**
 * The download ladder, shared by the public lane and the signed-in `mine`
 * lane: `url` is a short-TTL signed read URL (a bearer capability — open it,
 * never park it in the DOM or the address bar); `locked` means the state was
 * RE-DERIVED server-side on this very call and is not unlocked (re-resolve,
 * never argue); `unconfirmed` means orders-service was unreachable — honest
 * and retryable, never a verdict on the patient's plan.
 */
export type UnlockDownloadResult =
  | { outcome: 'url'; url: string }
  | { outcome: 'locked' }
  | { outcome: 'unconfirmed' };

/**
 * GET /v1/clinic/engagement/mine (@Roles('PATIENT'), self-scoped server-side
 * via the bind-once Assessment.patientUserId — R41, no deal id on the wire).
 * No binding / no deal → `{state:'none', items:[]}`, which renders NOTHING.
 */
export interface MyEngagement {
  state: UnlockState;
  items: UnlockItem[];
}

/**
 * POST /v1/clinic/assessments/resume-exchange {token} — what a `#resume=`
 * GRANT (a non-UUID fragment value) exchanges into. Generic 404 for a token
 * the server will not honour.
 */
export interface ResumeGrantExchange {
  assessmentId: string;
  clinicSlug: string;
}

/* ── Returning patients on the board (2026-09-26, S4.5) ────────────────────────
 *
 * [CONTRACTS GAP] Hand-written mirrors of clinic-service S4.1 (#224), S4.2
 * (#225) and S4.3 (#226), read off clinic `origin/dev` `src/deals/deal.schemas.ts`,
 * `src/deals/returning.ts` and `src/deals/deal-history.schemas.ts`. None of it is
 * in the pinned contracts tag (the plan's later pin sweep).
 *
 * ⚠️ EVERY NEW CARD/DETAIL FIELD IS OPTIONAL, and ABSENT is its own answer: a
 * clinic-service that predates S4.1 sends none of these keys. Absent `returning`
 * renders no chip (never "first attempt"), absent `lastActivityAt` keeps the
 * "Created …" footer, absent `supersededByDealId` means "this server does not
 * report supersedes" (never "this card was not superseded").
 */

/**
 * How two questionnaires are known to belong together — strongest first.
 * `same_account` = both bound to one identity account AND both binds proven
 * verified; `same_journey` = the Back-button chain, a shared journey id or the
 * step-0 Lead (NOT proof — Addendum 4: those ids ride the browser and can be
 * forged); `email_unverified` = nothing but the typed address.
 */
export type DealMatchBasis = 'same_account' | 'same_journey' | 'email_unverified';

/** The chip vocabulary, in the order a card renders them (clinic `RETURNING_CHIP_CODES`). */
export type DealReturningChipCode =
  | 'returning'
  | 'new_order'
  | 'active_plan'
  | 'active_plan_unknown'
  | 'two_live_cards'
  | 'started_again';

/**
 * One returning-patient chip. `label` is the SERVER's sentence and already ends
 * "(email match — not verified)" on an email-only basis — rendered VERBATIM so
 * no screen can show the claim without the warning (Addendum 2).
 */
export interface DealReturningChip {
  code: DealReturningChipCode;
  basis: DealMatchBasis;
  label: string;
}

/** "Is this person already on a plan with us" — fail-soft to `unknown`, never `none`. */
export type DealActivePlanState = 'active' | 'none' | 'unknown';

/** One of the contact's OTHER open cards. `dealId` is NULL exactly when `matchBasis` is `email_unverified`. */
export interface DealOtherOpenDeal {
  dealId: string | null;
  /** Its questionnaire was submitted or hard-stopped. */
  concluded: boolean;
  matchBasis: DealMatchBasis;
}

/** The returning-patient block on every card (clinic `dealReturning`). */
export interface DealReturning {
  attemptNumber: number;
  newOrder: boolean;
  otherOpenDeals: DealOtherOpenDeal[];
  /** Null when this response did not ask (a mutation echo) — render nothing for it. */
  activePlan: DealActivePlanState | null;
  /**
   * The name typed on THIS questionnaire when it differs from the card's name
   * (the maiden/married-name case). Rendered with the visible words "(from this
   * questionnaire, not verified)" — never a hover-only tooltip.
   */
  submittedAs: { firstName: string | null; lastName: string | null } | null;
  chips: DealReturningChip[];
}

/** Interface merge: the S4.1 card fields. */
export interface DealCard {
  /**
   * When something last happened on the card — the board's sort key under
   * `order=activity` and the "Updated …" half of the footer. Null on a row the
   * clinic boot sweep has not reached (it sorts to the lane's foot); ABSENT on
   * a clinic-service that predates S4.1.
   */
  lastActivityAt?: string | null;
  /**
   * The newer card that SUPERSEDED this one — the SYSTEM pointer, set when the
   * patient submitted a newer questionnaire and this unpaid pre-plan card was
   * closed into `closed_duplicate`. NOT the staff duplicate pointer
   * (`duplicateOfDealId`): different fact, different undo (an ADMIN reopen vs
   * the mark-duplicate release).
   */
  supersededByDealId?: string | null;
  returning?: DealReturning;
}

/** Interface merge: a sibling row can say it was superseded (S4.1). */
export interface DealSibling {
  supersededByDealId?: string | null;
}

/** Interface merge: prior attempts carry the match basis and the server's warning words (S4.1, Addendum 2). */
export interface DealPriorAttempt {
  matchBasis?: DealMatchBasis;
  /** "email match — not verified" on an email-only row, else null. Rendered verbatim. */
  matchLabel?: string | null;
}

/* ── The History tab's route (clinic S4.2, #225) ─────────────────────────────
 *
 * GET /v1/clinic/deals/:id/history and POST …/history/entries/:entryId/reveal,
 * both CLINICIAN·ADMIN, both 404 `{code:'disabled'}` while clinic's
 * CONTACT_HISTORY_ENABLED is off (the default until the DPIA and identity's
 * dispute producer). ⚠️ THE SHAPE IS THE CONTROL: an `email_unverified` row
 * carries NO assessment id, NO card id and NO stop reason — only an opaque entry
 * id redeemable through the LOGGED reveal.
 */

/** The coarse stop band (clinic `STOP_CATEGORIES`) — the same vocabulary as `MhRiskCategory`. */
export type DealHistoryStopCategory = MhRiskCategory;

/** Why an earlier questionnaire stopped — inline only on a same-account / same-journey row. */
export interface DealHistoryStop {
  category: DealHistoryStopCategory;
  /** The question that committed the stop; null on the submit-time lane. */
  questionKey: string | null;
  /** Whether that answer differs on THIS card's questionnaire; null = cannot compare. */
  answerChanged: boolean | null;
  /** R31 — that questionnaire's own mental-health flag. */
  mhRisk: boolean;
}

export type DealHistoryEntryKind = 'questionnaire' | 'callback';

/** One earlier questionnaire or callback ticket. */
export interface DealHistoryEntry {
  /** 32 lowercase hex — OPAQUE (not secret); the only handle on an email-only row. */
  entryId: string;
  kind: DealHistoryEntryKind;
  startedAt: string;
  endedAt: string | null;
  outcome: DealAttemptOutcome;
  matchBasis: DealMatchBasis;
  /** "email match — not verified" on an email-only row, else null. */
  matchLabel: string | null;
  /** A questionnaire's answers can be revealed (logged); a callback has none. */
  revealable: boolean;
  /** Null on every email-only row. */
  assessmentId: string | null;
  /** Null on every email-only row (and when no card carries it). */
  dealId: string | null;
  dealStage: DealStage | null;
  /** Null on every email-only row. */
  stop: DealHistoryStop | null;
}

export type DealHistoryTouchKind =
  | 'guide_emailed'
  | 'whatsapp_logged'
  | 'recovery_email_sent'
  | 'consent_granted'
  | 'consent_withdrawn'
  | 'consent_reaffirmed';

/** One marketing / engagement contact with this address — ADMIN SHAPE ONLY (Q8). */
export interface DealHistoryTouch {
  kind: DealHistoryTouchKind;
  at: string;
  channel: MarketingChannel | null;
  source: ConsentSource | null;
  onThisCard: boolean;
  /** An opaque staff principal when a person did it; the ADMIN lookup resolves the name. */
  actorUserId: string | null;
}

/** GET /v1/clinic/deals/:id/history — the CLINICIAN shape (no `touches`). */
export interface DealHistory {
  dealId: string;
  entries: DealHistoryEntry[];
  /** More may exist than this page shows (a source filled its read window). */
  truncated: boolean;
  /** The address's owner said "This wasn't me" — every email-only row was left out. */
  emailDisputed: boolean;
  consent: {
    email: { granted: boolean; changedAt: string | null };
    whatsapp: { granted: boolean; changedAt: string | null };
  };
  /** ⚠️ ADMIN SHAPE ONLY — absent from a clinician's answer by clinic's strict parse. */
  touches?: DealHistoryTouch[];
  /** ⚠️ ADMIN SHAPE ONLY — unlock links issued across this contact's cards (a count). */
  unlockLinksIssued?: number;
}

/** Why a member of staff opened earlier answers (clinic `HISTORY_REVEAL_REASONS`). */
export type DealHistoryRevealReason = 'clinical_review' | 'safety_check' | 'patient_request' | 'other';

/** POST …/history/entries/:entryId/reveal — `note` ≤200 trimmed, REQUIRED for `other`. */
export interface DealHistoryRevealRequest {
  reason: DealHistoryRevealReason;
  note?: string;
}

/** One rendered answer — the clinician review's own projection, never raw JSON. */
export interface DealHistoryRevealItem {
  questionKey: string;
  prompt: string;
  answer: string;
  detail?: string;
}

/** The reveal answer. `dealId` is NULL on an email-only row; no assessment id on either. */
export interface DealHistoryRevealResult {
  entryId: string;
  matchBasis: DealMatchBasis;
  matchLabel: string | null;
  dealId: string | null;
  stop: DealHistoryStop | null;
  sections: Array<{ key: string; title: string; items: DealHistoryRevealItem[] }>;
  /** What the projection deliberately left out (no identity, no photos). */
  withheld: Array<'date_of_birth' | 'photo'>;
}
